import { visiblePendingReplies, type PendingReply } from '../lib/pendingReplies';
import { PendingReplyCard } from '../components/inbox/PendingReplyCard';
import { collapseMessageConversations } from '../lib/messageConversationList';
import { createReplyComposer, replyMatchesSelection } from '../lib/replyComposer';
import { ReviewAttachments, type ReplyAttachment } from '../components/messages/ReviewAttachments';
import { createMailboxRequestScope } from '../lib/mailbox-request-scope';
import { MessageTemplateChoices, filterMessageTemplates, type MessageTemplateItem } from '../components/messages/MessageTemplateChoices';
import { ComplaintDetailModal } from '../components/complaints/ComplaintDetailModal';
import { complaintTypes, complaintStatuses } from '../components/complaints/ComplaintCasePanel';
import { OrderNotesPanel } from '../components/complaints/OrderNotesPanel';
import { Modal } from '../components/ui/Modal';
import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import type { MutableRefObject, ReactNode, TouchEvent as ReactTouchEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  Mail,
  MessageSquare,
  Inbox,
  FolderTree,
  Archive,
  Trash2,
  Search,
  Eye,
  EyeOff,
  Sparkles,
  Send,
  ArrowLeft,
  Paperclip,
  Check,
  Facebook,
  X,
  Loader2,
  AlertCircle,
  Copy,
  FileText,
  Bot,
  Forward,
  SlidersHorizontal,
  Tag,
  StickyNote,
  User,
  Info,
  Zap,
  Settings,
  ExternalLink,
  CheckCircle2,
  Clock,
  RotateCcw,
  Save,
  Mic,
  MicOff,
  AlertTriangle,
  CreditCard,
  Truck,
  Edit2,
  FolderOpen,
  BookOpen,
  Star,
} from 'lucide-react';
import { SkullIcon } from '../components/icons/SkullIcon';
import ReactQuill from 'react-quill';
import { Virtuoso } from 'react-virtuoso';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { messagesApi, aiApi, inboxEmailApi, customerNotesApi, userApi, orderApi, type Order, type MissingTemplateVariableDto } from '../lib/api';
import api from '../lib/api';
import type { InboxSwipeAction } from '../lib/api';
import { decideSwipeMomentum } from '../lib/swipeMomentum';
import { OrderDetailModal, type StatusGroup } from './OrdersPage';
import { CreateOrderModal } from '@/components/orders/CreateOrderModal';
import { statusColor, statusTextColor } from '../lib/statusColor';
import { useTheme, type Theme } from '../contexts/ThemeContext';
import { useFullscreen } from '../contexts/FullscreenContext';
import { getSocket, onSocketReady, subscribeToMessages, unsubscribeFromMessages } from '../lib/socket';
import { withPlainTextPaste } from '../lib/quillPlainTextPaste';
import { EmptyState } from '../components/EmptyState';
import { InboxSidebar } from '../components/inbox/InboxSidebar';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { splitQuotedContent, splitQuotedPlainText } from '../lib/email-quote-utils';
import { QuotedContentToggle } from '../components/QuotedContentToggle';
import { useInboxMutations } from '../hooks/useInboxMutations';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Button } from '../components/ui/Button';
import { MESSAGE_TEMPLATE_VARIABLES } from '../components/messages/MessageTemplateVariablePicker';
import { extractMailboxEmail, resolveMessageCustomerEmail } from '../lib/messageParticipants';

interface StoreItem {
  id: string;
  name: string;
  messageInboxPosition: number;
  unreadCount: number;
  mailboxCount: number;
}

interface InboxMessage {
  id: string;
  source: string;
  from: string;
  fromName: string | null;
  to: string | null;
  cc: string | null;
  bcc: string | null;
  replyTo?: string | null;
  subject: string | null;
  // Zero-persistence: treść (bodyText/bodyHtml) fetchowana lazy przez messagesApi.getMessageBody(id).
  // Na liście mamy tylko `snippet` (~500 znaków z peek IMAP body).
  snippet: string | null;
  isRead: boolean;
  isStarred: boolean;
  isDraft: boolean;
  isDeleted?: boolean;
  isArchived?: boolean;
  // IMAP anchors — folder pochodzi z natywnego drzewa IMAP, nie jest już stringiem enum.
  imapConfigId: string | null;
  imapFolderId: string | null;
  imapUid: number | null;
  imapFlags: string[];
  imapFolder: { id: string; path: string; name: string; specialUse: string | null } | null;
  hasAttachments: boolean;
  // Manifest załączników (bez content) — [{partId, filename, mimeType, contentId?, size}]
  attachments: Array<{ partId: string; filename: string; mimeType: string; contentId?: string; size?: number }>;
  metadata: Record<string, unknown>;
  receivedAt: string;
  createdAt: string;
  store?: { id: string; name: string } | null;
  labels?: Array<{ label: { id: string; name: string; color: string } }>;
  _count?: { replies: number };
  replies?: InboxMessage[];
  parentId?: string | null;
  conversationRootId?: string | null;
  parent?: { id: string; subject: string; from: string; fromName: string | null } | null;
  fbPostId?: string | null;
  fbCommentId?: string | null;
  // Conversation lifecycle
  conversationStatus?: string; // open, pending, resolved, snoozed
  snoozedUntil?: string | null;
  resolvedAt?: string | null;
  firstResponseAt?: string | null;
  // AI categorization
  aiCategory?: string | null;
  aiPriority?: number | null;
  aiConfidence?: number | null;
  aiSentiment?: 'positive' | 'neutral' | 'negative' | 'angry' | null;
  aiRiskLevel?: 'low' | 'medium' | 'high' | 'critical' | null;
  aiResponseDeadline?: string | null;
  threadStats?: {
    totalMessages?: number;
    totalReplies: number;
    unreadReplies: number;
    hasDraftReply?: boolean;
    latestReplyAt: string | null;
    latestInboundAt?: string | null;
    latestReplySnippet: string | null;
  };
  // FTS match (Krok 5) — wycinek body z <mark> wokół trafień. HTML-escaped na backendzie,
  // jedyne zachowane tagi to <mark>/</mark>.
  matchSnippet?: string | null;
}

interface InboxLabelItem {
  id: string;
  name: string;
  color: string;
  position: number;
  _count?: { messages: number };
}

interface Stats {
  totalUnread: number;
  emailUnread: number;
  fbUnread: number;
  totalMessages: number;
  starredCount: number;
  folderCounts?: {
    inbox: number;
    starred: number;
    sent: number;
    drafts: number;
    archive: number;
    trash: number;
    spam: number;
  };
  statusCounts?: {
    pending: number;
    important: number;
    resolved: number;
  };
  mappedStatuses?: string[];
  reviewQueueCount?: number;
  reviewQueueMessageIds?: string[];
}



interface CustomerData {
  complaints?: Array<{ id: string; type: string; status: string; reason: string; createdAt: string; orderNumber: string }>;
  customer: { name: string | null; email: string; phone: string | null };
  orders: Array<{
    complaintRequests?: Array<{ id: string; type: string; status: string; reason: string; createdAt: string }>;
    id: string;
    orderNumber: string;
    totalAmount: number;
    currency: string;
    status: string;
    statusId: string | null;
    externalStatus: string | null;
    source: 'WOOCOMMERCE' | 'MANUAL' | 'RESEND';
    parentOrderId: string | null;
    orderStatus?: {
      id: string;
      name: string;
      color: string;
      settings?: Record<string, unknown> | null;
    } | null;
    parentOrder?: {
      id: string;
      orderNumber: string;
    } | null;
    customerName: string | null;
    items: unknown;
    totals: Record<string, string> | null;
    shippingMethod: string | null;
    paymentMethod: string | null;
    createdAt: string;
    externalCreatedAt: string | null;
    customerNotesCount?: number;
    refunds?: Array<{
      id: string;
      amount: number;
      currency: string;
      status: string;
      description: string;
      createdAt: string;
    }>;
    invoices?: Array<{
      id: string;
      invoiceNumber: string | null;
      status: string;
      issueDate: string | null;
    }>;
    shipments?: Array<{
      id: string;
      trackingNumber: string | null;
      carrier: string;
      status: string;
      inpostStatus: string | null;
      deliveredAt?: string | null;
      statusChangedAt?: string | null;
      createdAt: string;
      updatedAt?: string;
    }>;
    isPaid?: boolean;
    isBlacklisted?: boolean;
  }>;
  totalLTV: number;
  currency: string;
  orderCount: number;
  // M1: Extended customer data
  shippingAddress?: Record<string, string> | null;
  billingAddress?: Record<string, string> | null;
}

const formatCustomerOrderDate = (order: Pick<CustomerData['orders'][number], 'externalCreatedAt' | 'createdAt'>): string => {
  const raw = order.externalCreatedAt || order.createdAt;
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const formatCustomerMoney = (value: number, currency: string) =>
  `${new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0)} ${currency}`;

function findCustomerOrderStatus(
  list: StatusGroup[] | null,
  statusId: string | null,
  name: string,
): StatusGroup | null {
  if (!list) return null;
  for (const status of list) {
    if ((statusId && status.id === statusId) || (!statusId && status.name === name)) return status;
    const child = findCustomerOrderStatus(status.children, statusId, name);
    if (child) return child;
  }
  return null;
}

function CustomerOrderHistory({
  data,
  statuses,
  theme,
  onOpenOrder,
  onOpenComplaint,
  maxOrders,
  maxHeightClass,
}: {
  data: CustomerData;
  statuses: StatusGroup[] | null;
  theme: Theme;
  onOpenOrder: (orderId: string) => void;
  onOpenComplaint: (complaintId: string) => void;
  maxOrders: number;
  maxHeightClass: string;
}) {
  const complaints = data.complaints ?? data.orders.flatMap(order => (order.complaintRequests || []).map(complaint => ({ ...complaint, orderNumber: order.orderNumber })));
  return (
    <>
      {complaints.length > 0 && <section className="rounded-xl border border-info-border bg-info-bg p-3 space-y-2">
        <p className="text-xs font-semibold text-text-primary">Zwroty i reklamacje ({complaints.length})</p>
        {complaints.length === 50 && <p className="text-xs text-text-muted">Ostatnie 50 zgłoszeń</p>}
        <div className="max-h-56 overflow-y-auto space-y-2">
        {complaints.map(complaint => <button key={complaint.id} type="button" onClick={() => onOpenComplaint(complaint.id)} className="block w-full text-left rounded-lg p-2 bg-surface-raised hover:bg-surface-hover">
          <span className="flex flex-wrap justify-between gap-1 text-xs font-medium"><span>{complaintTypes[complaint.type] || complaint.type} · #{complaint.orderNumber}</span><span className="text-info">{complaintStatuses[complaint.status] || complaint.status}</span></span>
          <span className="block text-xs text-text-muted mt-1 line-clamp-2">{complaint.reason}</span>
        </button>)}</div>
      </section>}

      <div className="rounded-lg border border-border-subtle bg-surface-hover/30 px-2.5 py-2">
        <div className="flex items-center justify-between gap-2 text-[10px] text-text-muted">
          <span className="font-medium uppercase tracking-wide">Historia zakupów</span>
          <span className="whitespace-nowrap">
            {data.orderCount} {data.orderCount === 1 ? 'zamówienie' : 'zamówień'}
          </span>
        </div>
        <div className="mt-1 flex items-baseline justify-between gap-2">
          <span className="text-[10px] text-text-muted">Łączna wartość</span>
          <strong className="whitespace-nowrap text-sm font-semibold tabular-nums text-accent-primary">
            {formatCustomerMoney(data.totalLTV, data.currency)}
          </strong>
        </div>
      </div>

      <div className={`space-y-2 overflow-y-auto pr-0.5 ${maxHeightClass}`}>
        {data.orders.slice(0, maxOrders).map(order => {
          const statusName = order.orderStatus?.name || order.status || translateStatus(order.externalStatus || '');
          const statusDefinition = order.orderStatus
            || findCustomerOrderStatus(statuses, order.statusId, statusName);
          const isTrash = statusName === 'Kosz' || order.externalStatus === 'trash';
          const shipments = order.shipments || [];
          const activeShipments = shipments.filter(shipment => !['FAILED', 'RETURNED'].includes(shipment.status));
          const activeInvoices = (order.invoices || []).filter(invoice => invoice.status !== 'DELETED');
          const deletedInvoices = (order.invoices || []).filter(invoice => invoice.status === 'DELETED');

          const renderOrderStatus = () => {
            if (isTrash) {
              return <span className="rounded-full border border-danger/40 bg-danger/20 px-1.5 py-0.5 text-[9px] font-medium text-danger whitespace-nowrap">Kosz</span>;
            }
            if (statusDefinition) {
              const background = statusColor(statusDefinition, theme);
              const text = statusTextColor(statusDefinition, theme);
              return (
                <span
                  className="rounded-full border border-border-strong px-1.5 py-0.5 text-[9px] font-medium whitespace-nowrap"
                  style={{ backgroundColor: `${background}20`, color: text }}
                >
                  {statusName}
                </span>
              );
            }
            const externalStatus = (order.externalStatus || '').toLowerCase();
            const fallbackClass = externalStatus === 'completed'
              ? 'bg-success-bg text-success'
              : externalStatus === 'processing'
                ? 'bg-info-bg text-info'
                : externalStatus === 'on-hold'
                  ? 'bg-warning-bg text-warning'
                  : ['cancelled', 'refunded', 'failed'].includes(externalStatus)
                    ? 'bg-danger-bg text-danger'
                    : 'bg-surface-muted text-text-secondary';
            return <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium whitespace-nowrap ${fallbackClass}`}>{statusName}</span>;
          };

          return (
            <div key={order.id} className="rounded-xl border border-border-subtle bg-surface-hover/20 p-2.5">
              <button type="button" onClick={() => onOpenOrder(order.id)} className="w-full text-left hover:text-text-primary transition-colors">
                <div className="flex min-w-0 items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span className="shrink-0 font-mono text-[11px] text-text-primary">#{order.orderNumber}</span>
                    <span className="shrink-0 text-[9px] tabular-nums text-text-disabled">{formatCustomerOrderDate(order)}</span>
                  </div>
                  {renderOrderStatus()}
                </div>
                {order.source === 'RESEND' && (
                  <div className="mt-1 flex items-center gap-1 text-[9px] text-accent-primary">
                    <RotateCcw className="h-2.5 w-2.5" />
                    Ponowna wysyłka{order.parentOrder?.orderNumber ? ` do #${order.parentOrder.orderNumber}` : ''}
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between gap-2 border-t border-border-subtle pt-1.5">
                  <span className="text-[9px] text-text-muted">Wartość zamówienia</span>
                  <strong className="whitespace-nowrap text-[11px] font-semibold tabular-nums text-text-primary">
                    {formatCustomerMoney(Number(order.totalAmount), order.currency)}
                  </strong>
                </div>
              </button>

              <div className="mt-1.5 flex items-center gap-1 border-t border-border-subtle pt-1.5">
                <div className={`rounded p-0.5 ${order.isPaid ? 'text-success' : 'text-text-disabled'}`} title={order.isPaid ? 'Opłacone' : 'Nieopłacone / Za pobraniem'}>
                  <CreditCard className="h-3 w-3" />
                </div>
                <div className={`rounded p-0.5 ${activeShipments.length > 0 ? 'text-accent-primary' : 'text-text-disabled'}`} title={shipments.length > 0 ? `${shipments.length} przesyłek w historii` : 'Brak przesyłki'}>
                  <Truck className="h-3 w-3" />
                </div>
                <div className={`rounded p-0.5 ${activeInvoices.length > 0 ? 'text-info' : deletedInvoices.length > 0 ? 'text-text-muted line-through' : 'text-text-disabled'}`} title={activeInvoices.length > 0 ? 'Faktura wystawiona' : deletedInvoices.length > 0 ? 'Faktura usunięta' : 'Brak faktury'}>
                  <FileText className={`h-3 w-3 ${deletedInvoices.length > 0 && activeInvoices.length === 0 ? 'opacity-50' : ''}`} />
                </div>
                {order.isBlacklisted && (
                  <div className="rounded p-0.5 text-danger" title="Klient na czarnej liście">
                    <SkullIcon className="h-3 w-3" />
                  </div>
                )}
              </div>

              {shipments.length > 0 && (
                <div className="mt-2 border-t border-border-subtle pt-2">
                  <div className="mb-1 flex items-center gap-1 text-[9px] font-medium uppercase tracking-wide text-text-muted">
                    <Truck className="h-2.5 w-2.5" /> Przesyłki ({shipments.length})
                  </div>
                  <div className="space-y-1.5">
                    {shipments.map((shipment, index) => {
                      const shipmentStatus = shipment.inpostStatus || shipment.status;
                      const shipmentDate = shipment.statusChangedAt || shipment.updatedAt || shipment.createdAt;
                      return (
                        <div key={shipment.id} className="rounded-md bg-surface-base/50 px-2 py-1.5 text-[9px]">
                          <div className="flex items-center justify-between gap-1.5">
                            <span className="min-w-0 truncate text-text-secondary" title={shipment.trackingNumber || undefined}>
                              {shipment.trackingNumber || `Przesyłka ${shipments.length - index}`}
                            </span>
                            <span className={`shrink-0 rounded-full px-1.5 py-0.5 font-medium ${shipmentStatusClass(shipmentStatus)}`}>
                              {translateShipmentStatus(shipmentStatus)}
                            </span>
                          </div>
                          <div className="mt-0.5 text-text-disabled">
                            {shipment.carrier} • {new Date(shipmentDate).toLocaleDateString('pl-PL')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-2 border-t border-border-subtle pt-2">
                <p className="text-xs font-medium text-text-secondary">Notatki zamówienia</p>
                <OrderNotesPanel orderId={order.id} compact />
              </div>
              <div className="mt-2 space-y-1 border-t border-border-subtle pt-2">
                <p className="text-xs font-medium text-text-secondary">Zwroty płatności</p>
                {!order.refunds?.length && <p className="text-xs text-text-muted">Brak zwrotów</p>}
                {order.refunds?.map(refund => (
                  <div key={refund.id} className="space-y-1 text-[10px]">
                    <div className="flex flex-wrap items-center gap-1">
                      <RotateCcw className="h-3 w-3 shrink-0" />
                      <span>{formatCustomerMoney(Number(refund.amount), refund.currency)}</span>
                      <span className={`rounded px-1 py-0.5 ${refund.status === 'COMPLETED' ? 'bg-success-bg text-success' : refund.status === 'PENDING' ? 'bg-warning-bg text-warning' : 'bg-danger-bg text-danger'}`}>
                        {{ COMPLETED: 'Zrealizowany', PENDING: 'Oczekujący', PROCESSING: 'W trakcie', FAILED: 'Nieudany', CANCELLED: 'Anulowany' }[refund.status] || refund.status}
                      </span>
                    </div>
                    <p className="text-text-muted">{new Date(refund.createdAt).toLocaleDateString('pl-PL')}{refund.description ? ` · ${refund.description}` : ''}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// Unified CustomerNote (scala notatki z wątków Inbox i z kart zamówień).
// userName jest zdenormalizowany (zachowany po usunięciu Usera), orderId/
// conversationId wskazują skąd została dodana (badge UI opcjonalny).
interface ConversationNote {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  userId: string | null;
  userName: string;
  orderId: string | null;
  conversationId: string | null;
  order?: { id: string; orderNumber: string } | null;
}

interface ReviewQueueItem {
  id: string;
  messageId: string;
  category: string | null;
  confidence: number | null;
  draftHtml: string;
  draftText: string | null;
  status: string;
  tokensUsed: number | null;
  provider: string | null;
  createdAt: string;
  message: {
    id: string;
    from: string;
    fromName: string | null;
    replyTo?: string | null;
    subject: string | null;
    // Zero-persistence: body nie jest w DB — mamy tylko snippet w payloadzie review queue.
    // Jeśli chcemy pełną treść, fetchujemy lazy przez messagesApi.getMessageBody(id).
    snippet: string | null;
    receivedAt: string | null;
    aiCategory: string | null;
    imapConfigId: string | null;
  };
}

interface InboxAiDailyReport {
  pendingReview: number;
  overdueResponses: number;
  highRiskOpen: number;
  aiErrors: number;
  sendingErrors: number;
  knowledgeGaps: number;
  knowledgeSuggestions: string[];
  totalExceptions: number;
}

// Helper: determine if a message was sent by us (outgoing)
// Po migracji na natywne drzewo IMAP: folder poznajemy po imapFolder.specialUse (`\\Sent`),
// a nie po stringu. Fallback: dopasowanie nadawcy do odbiorcy rodzica.
const isOutgoingMessage = (msg: InboxMessage, parentMsg: InboxMessage) => {
  return msg.imapFolder?.specialUse === '\\Sent' || msg.from === parentMsg.to;
};

// Helper: podświetla dopasowania search terms w tekście (case-insensitive, unicode).
// Escape'uje HTML, zwraca string z <mark> wokół trafień — do dangerouslySetInnerHTML.
const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const missingTemplateFieldPattern = /\[BRAK DANYCH:[^\]]+\]/i;
const templateVariableLabels = new Map(
  MESSAGE_TEMPLATE_VARIABLES.map(variable => [
    variable.code.slice(2, -2),
    variable.description,
  ]),
);

const markMissingTemplateValues = (
  value: string,
  output: 'html' | 'text',
): { content: string; missingVariables: MissingTemplateVariableDto[] } => {
  const missingByKey = new Map<string, MissingTemplateVariableDto>();
  const content = value.replace(/\{\{([a-z0-9_]+)\}\}/gi, (_match, key: string) => {
    const variable = {
      key,
      label: templateVariableLabels.get(key) || key.replace(/_/g, ' '),
    };
    missingByKey.set(key, variable);
    const marker = `[BRAK DANYCH: ${variable.label}]`;
    return output === 'html'
      ? `<strong><span style="color: rgb(185, 28, 28); background-color: rgb(254, 226, 226);">${escapeHtml(marker)}</span></strong>`
      : marker;
  });

  return { content, missingVariables: [...missingByKey.values()] };
};

const mergeMissingTemplateVariables = (
  ...groups: MissingTemplateVariableDto[][]
): MissingTemplateVariableDto[] => [
  ...new Map(groups.flat().map(variable => [variable.key, variable])).values(),
];

const highlightText = (text: string | null | undefined, terms: string[]): string => {
  const safe = escapeHtml(text || '');
  if (!terms.length || !safe) return safe;
  const pattern = new RegExp(`(${terms.map(escapeRegex).join('|')})`, 'giu');
  return safe.replace(pattern, '<mark>$1</mark>');
};

// Helper: generate initials for avatar
const getInitials = (name: string | null, email: string) => {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].substring(0, 2).toUpperCase();
  }
  return email.substring(0, 2).toUpperCase();
};

// Helper: generate random coupon code
const generateCouponCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'EF-';
  for (let i = 0; i < 6; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
  return code;
};

// Helper: translate order status to Polish
const translateStatus = (status: string) => {
  const map: Record<string, string> = {
    'processing': 'W realizacji',
    'completed': 'Zrealizowane',
    'pending': 'Oczekujące',
    'on-hold': 'Wstrzymane',
    'cancelled': 'Anulowane',
    'refunded': 'Zwrócone',
    'failed': 'Nieudane',
    'trash': 'Usunięte',
  };
  return map[status] || status;
};

const translateShipmentStatus = (status?: string | null) => {
  if (!status) return '';
  const map: Record<string, string> = {
    CREATED: 'Utworzona',
    CONFIRMED: 'Potwierdzona',
    DISPATCHED: 'Wysłane',
    IN_TRANSIT: 'W drodze',
    OUT_FOR_DELIVERY: 'W doręczeniu',
    DELIVERED: 'Dostarczono',
    RETURNED: 'Zwrot',
    FAILED: 'Błąd',
    created: 'Utworzona',
    confirmed: 'Potwierdzona',
    dispatched_by_sender: 'Nadana przez nadawcę',
    collected_from_sender: 'Odebrana od nadawcy',
    adopted_at_source_branch: 'Przyjęta w oddziale',
    sent_from_source_branch: 'W drodze',
    ready_to_pickup: 'Gotowa do odbioru',
    out_for_delivery: 'W doręczeniu',
    delivered: 'Dostarczona',
    pickup_reminder_sent: 'Przypomnienie o odbiorze',
    pickup_time_expired: 'Nieodebrana',
    returned_to_sender: 'Zwrócona do nadawcy',
  };
  return map[status] || status;
};

const shipmentStatusClass = (status?: string | null) => {
  switch (status?.toLowerCase()) {
    case 'delivered':
      return 'bg-success-bg text-success';
    case 'ready_to_pickup':
    case 'out_for_delivery':
    case 'in_transit':
      return 'bg-info-bg text-info';
    case 'dispatched':
    case 'dispatched_by_sender':
    case 'confirmed':
    case 'created':
      return 'bg-accent-primary/20 text-accent-primary';
    case 'failed':
    case 'returned':
    case 'returned_to_sender':
    case 'pickup_time_expired':
      return 'bg-danger-bg text-danger';
    default:
      return 'bg-surface-muted text-text-secondary';
  }
};

// Helper: format file size
const formatFileSize = (bytes: number) => {
  if (!bytes || bytes <= 0) return '';
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

const plainTextToEmailHtml = (value: string): string => `<p>${value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/\n/g, '<br/>')}</p>`;

// Zwraca klucz YYYY-MM-DD dla daty (do grupowania po dniach)
const getDayKey = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Etykieta dnia po polsku: "Dzisiaj (13.04.2026)", "Wczoraj (12.04.2026)", "Piątek (10.04.2026)"
const WEEKDAYS_PL = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
const formatDayLabel = (d: Date): string => {
  const today = new Date();
  const todayKey = getDayKey(today);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = getDayKey(yesterday);
  const dKey = getDayKey(d);
  const dateStr = d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  if (dKey === todayKey) return `Dzisiaj (${dateStr})`;
  if (dKey === yesterdayKey) return `Wczoraj (${dateStr})`;
  return `${WEEKDAYS_PL[d.getDay()]} (${dateStr})`;
};

type SourceFilter = 'all' | 'email' | 'facebook';
type FolderFilter = 'review_queue' | 'inbox' | 'resolved' | 'starred' | 'sent' | 'drafts' | 'archive' | 'spam' | 'trash' | (string & {});

const MAILBOX_VIRTUAL_VIEWS = new Set([
  'review_queue', 'inbox', 'resolved', 'starred', 'sent', 'drafts', 'archive', 'spam', 'trash',
]);

function normalizeMailboxView(value: string | null): FolderFilter {
  return value && MAILBOX_VIRTUAL_VIEWS.has(value) ? value as FolderFilter : 'review_queue';
}

// Voice dictation: hard limit 90s żeby nie zostawić mikrofonu włączonego na godzinę
// (Whisper ~$0.02/min + cleanup LLM tokeny bez capa). Typowe dyktowanie odpowiedzi
// na mail mieści się w 30-60s. Auto-stop odpala transkrypcję tego co już nagrane.
const VOICE_RECORDING_LIMIT_SECONDS = 90;
// Ostrzeżenie UI (czerwony kolor) gdy pozostało mniej niż tyle sekund do limitu.
const VOICE_RECORDING_WARN_SECONDS_LEFT = 15;

// Sanitize email HTML — strip dangerous elements but keep inline styles for layout.
// Przeniesione z MessagesInboxPage na top-level żeby można było używać w MessageBodyRenderer.
function sanitizeEmailHtmlTopLevel(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    // Zostawiamy <img src="cid:…"> — backend przepisuje je na /api/messages/:id/attachments/:partId
    // w czasie renderu /body. Usuwamy tylko te których backend nie zdążył zmapować.
    .replace(/<img[^>]*src=["']cid:[^"']*["'][^>]*\/?>/gi, '')
    .replace(/width\s*:\s*\d{4,}px/gi, 'width: 100%')
    .replace(/width="\d{4,}"/gi, 'width="100%"');
}

// Body z PostgreSQL (InboxMessageBody). Otwarcie maila = jeden SELECT (<100ms).
// Brak body (mail nie zbackfillowany lub IMAP go już nie ma) → 404 i błąd w UI.
function MessageBodyRenderer({ messageId, source }: { messageId: string; source: string }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['message-body', messageId],
    queryFn: async () => {
      const res = await messagesApi.getMessageBody(messageId);
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="px-5 pb-4 pt-2.5 flex items-center gap-2 text-xs text-text-muted">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Ładowanie treści…
      </div>
    );
  }

  if (isError) {
    const axiosErr = error as { response?: { status?: number; data?: { error?: string } } };
    const status = axiosErr?.response?.status;
    const backendMsg = axiosErr?.response?.data?.error;
    const defaultMsg =
      status === 404
        ? 'Wiadomość została usunięta.'
        : 'Nie udało się pobrać treści wiadomości.';
    return (
      <div className="px-5 pb-4 pt-2.5">
        <div className="text-xs flex items-center gap-2 text-danger">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{backendMsg || defaultMsg}</span>
        </div>
      </div>
    );
  }

  const html = data?.bodyHtml ?? null;
  const text = data?.bodyText ?? '';
  const bodySource = (data as { source?: string } | undefined)?.source;

  // Brak body + brak snippet. Dwa realne przypadki:
  // (a) race: mail dopiero zsyncowany, body w trakcie pobierania (99% sytuacji)
  // (b) celowy purge: retencja/manual (rzadkie, tylko operator)
  // Komunikat optymalizowany pod (a) — dla (b) operator wie co zrobił.
  if (bodySource === 'purged') {
    return (
      <div className="px-5 pb-4 pt-2.5">
        <div className="text-xs text-text-muted italic">
          Treść pobiera się w tle — odśwież listę za chwilę.
        </div>
      </div>
    );
  }

  // Helper: komunikat gdy body w ogóle nie istnieje (bez HTML i bez text).
  // Spotykany przy mailach typu "odpowiedź tylko w temacie" — klient daje
  // odpowiedź w Subject ("Tak", "OK, zgoda") i zostawia puste body. Bez
  // placeholderu user widzi pusty blok — wygląda jak bug.
  const renderEmptyBodyNotice = () => (
    <div className="px-5 pb-4 pt-2.5">
      <div className="text-xs text-text-muted italic">
        Nadawca nic nie napisał. Wiadomość jest pusta.
      </div>
    </div>
  );

  const trimmedText = (text || '').trim();

  // Wiadomości Facebook: brak HTML, mamy tylko plain text (snippet).
  if (source !== 'email' || !html) {
    if (!trimmedText) return renderEmptyBodyNotice();
    const { original, quoted } = splitQuotedPlainText(text);
    return (
      <div className="px-5 pb-4 pt-2.5">
        <div className="text-sm text-text-secondary whitespace-pre-wrap">{original}</div>
        {quoted && <QuotedContentToggle quotedText={quoted} />}
      </div>
    );
  }

  const sanitized = sanitizeEmailHtmlTopLevel(html);
  const { original, quoted } = splitQuotedContent(sanitized);
  // Gdy HTML po sanityzacji i odcięciu cytatów jest pusty (bądź same tagi
  // bez tekstu — np. <p></p>, <br> itp.) traktujemy jak pusty body.
  const originalStripped = original.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
  if (!originalStripped && !trimmedText) {
    return renderEmptyBodyNotice();
  }
  const isRichHtml =
    /<table[\s>]/i.test(sanitized) ||
    /bgcolor/i.test(sanitized) ||
    /background-color\s*:\s*#(?!0{3,6}|transparent)/i.test(sanitized);

  return (
    <div className="px-5 pb-4 pt-2.5">
      <div className={`email-body-sandbox text-sm ${isRichHtml ? 'email-body-sandbox--rich' : ''}`}>
        <div dangerouslySetInnerHTML={{ __html: original }} />
        {quoted && <QuotedContentToggle quotedHtml={quoted} />}
      </div>
    </div>
  );
}

function bulkActionToastMessage(action: string, count: number): string {
  const one = count === 1;
  switch (action) {
    case 'trash':
      return one ? 'Przeniesiono wiadomość do kosza' : `Przeniesiono ${count} wiadomości do kosza`;
    case 'archive':
      return one ? 'Zarchiwizowano wiadomość' : `Zarchiwizowano ${count} wiadomości`;
    case 'restore':
      return one ? 'Przywrócono wiadomość' : `Przywrócono ${count} wiadomości`;
    case 'read':
      return one ? 'Oznaczono wiadomość jako przeczytaną' : `Oznaczono ${count} wiadomości jako przeczytane`;
    case 'unread':
      return one ? 'Oznaczono wiadomość jako nieprzeczytaną' : `Oznaczono ${count} wiadomości jako nieprzeczytane`;
    case 'resolve':
      return one ? 'Oznaczono wiadomość jako rozwiązaną' : `Oznaczono ${count} wiadomości jako rozwiązane`;
    case 'pending':
      return one ? 'Oznaczono wiadomość jako oczekującą' : `Oznaczono ${count} wiadomości jako oczekujące`;
    case 'important':
      return one ? 'Oznaczono wiadomość jako ważną' : `Oznaczono ${count} wiadomości jako ważne`;
    case 'spam':
      return one ? 'Oznaczono wiadomość jako spam' : `Oznaczono ${count} wiadomości jako spam`;
    case 'star':
      return one ? 'Dodano gwiazdkę do wiadomości' : `Dodano gwiazdkę do ${count} wiadomości`;
    case 'unstar':
      return one ? 'Usunięto gwiazdkę z wiadomości' : `Usunięto gwiazdkę z ${count} wiadomości`;
    default:
      return one ? 'Wykonano akcję na wiadomości' : `Wykonano akcję na ${count} wiadomościach`;
  }
}

// Metadane wyświetlania dla akcji swipe (etykieta, kolor tła, ikona tekstowa).
// Używane w SwipeableRow — musi być poza komponentem (stable ref, Vite fast refresh).
const SWIPE_ACTION_META: Record<InboxSwipeAction, { label: string; bg: string; text: string } | null> = {
  none: null,
  resolved: { label: 'Rozwiązane', bg: 'bg-success/90', text: 'text-white' },
  pending: { label: 'Oczekujące', bg: 'bg-warning/90', text: 'text-white' },
  important: { label: 'Ważne', bg: 'bg-danger/90', text: 'text-white' },
  archive: { label: 'Archiwum', bg: 'bg-info/90', text: 'text-white' },
  trash: { label: 'Kosz', bg: 'bg-danger/90', text: 'text-white' },
  drafts: { label: 'Szkice', bg: 'bg-accent-deep/90', text: 'text-white' },
};

interface SwipeWheelGuard {
  suppressMomentum: boolean;
  lastHorizontalEventAt: number;
  lastAbsDeltaX: number;
  newGestureEvidence: number;
}

/**
 * Opakowuje wiersz wiadomości w swipe gesty (lewo/prawo) dla trzech typów
 * wejścia: touch (mobile), pointer (mysz drag), wheel (touchpad / Magic Mouse
 * swipe bez kliknięcia). Próg 80 px; po przekroczeniu odpalamy akcję,
 * poniżej — wracamy animacją do 0.
 */
function SwipeableRow({
  enabled,
  leftAction,
  rightAction,
  onLeftSwipe,
  onRightSwipe,
  sharedWheelGuardRef,
  children,
}: {
  enabled: boolean;
  leftAction: InboxSwipeAction;
  rightAction: InboxSwipeAction;
  onLeftSwipe: () => void;
  onRightSwipe: () => void;
  sharedWheelGuardRef: MutableRefObject<SwipeWheelGuard>;
  children: ReactNode;
}) {
  const [dx, setDx] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number; active: boolean; committed: boolean } | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number; id: number; committed: boolean } | null>(null);
  const wheelAccumRef = useRef(0);
  const wheelEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Kalibracja kroków (ręczna, po feedbackach):
  //  - ACTIVATION: minimalny ruch w poziomie ZANIM zaczniemy przesuwać wiersz.
  //    Poniżej tej wartości klik/tap nie wywołuje gestu (user może normalnie
  //    otworzyć mail albo scrollować pionowo bez "cienia" swipe).
  //  - ACTION: minimalny offset PRZED zatwierdzeniem akcji. Poniżej — wiersz
  //    animuje powrót do 0. User musi świadomie przesunąć, nie drgnięcie.
  //  Dla wheel (touchpad/Magic Mouse) używamy osobnych, większych progów —
  //  dwu-palcowy swipe naturalnie generuje dużo delty szybko, więc akcje
  //  odpalały się zbyt łatwo. Wyższe progi = "jeszcze raz, ale zdecydowanie".
  const ACTIVATION_THRESHOLD = 40;
  const ACTION_THRESHOLD = 220;
  const WHEEL_ACTIVATION_THRESHOLD = 60;
  const WHEEL_ACTION_THRESHOLD = 280;
  const WHEEL_MOMENTUM_QUIET_MS = 120;
  const leftMeta = SWIPE_ACTION_META[leftAction];
  const rightMeta = SWIPE_ACTION_META[rightAction];

  // Non-passive wheel listener — React onWheel jest domyślnie passive (nie
  // można preventDefault). Touchpad/Magic Mouse swipe w bok generuje wheel
  // z deltaX > 0 (swipe w lewo) lub < 0 (swipe w prawo). Accumulujemy w
  // ref, a po 150 ms ciszy decydujemy: akcja czy reset.
  useEffect(() => {
    if (!enabled) return;
    const el = containerRef.current;
    if (!el) return;
    const finalize = () => {
      const finalDx = -wheelAccumRef.current;
      wheelAccumRef.current = 0;
      setTransitioning(true);
      if (finalDx <= -WHEEL_ACTION_THRESHOLD && leftMeta) {
        setDx(-400);
        setTimeout(() => { onLeftSwipe(); setDx(0); setTransitioning(false); }, 150);
      } else if (finalDx >= WHEEL_ACTION_THRESHOLD && rightMeta) {
        setDx(400);
        setTimeout(() => { onRightSwipe(); setDx(0); setTransitioning(false); }, 150);
      } else {
        setDx(0);
        setTimeout(() => setTransitioning(false), 200);
      }
    };
    const handler = (e: WheelEvent) => {
      // Dominanta pozioma - inaczej pozwól Virtuoso scrollować pionowo.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      const eventTime = performance.now();
      const wheelGuard = sharedWheelGuardRef.current;
      const quietFor = eventTime - wheelGuard.lastHorizontalEventAt;
      const absDeltaX = Math.abs(e.deltaX);
      // Po usunięciu/archiwizacji Virtuoso natychmiast podstawia kolejny wiersz
      // pod kursor. Malejącą końcówkę starego gestu konsumujemy globalnie.
      // Nowy, świadomy gest może jednak zacząć się zanim bezwładność touchpada
      // całkiem ucichnie. Rozpoznajemy go po dwóch kolejnych, wyraźnie rosnących
      // deltach, dzięki czemu nie ma wielosekundowego cooldownu.
      if (wheelGuard.suppressMomentum) {
        const decision = decideSwipeMomentum({
          quietForMs: quietFor,
          absDeltaX,
          previousAbsDeltaX: wheelGuard.lastAbsDeltaX,
          previousEvidence: wheelGuard.newGestureEvidence,
          quietThresholdMs: WHEEL_MOMENTUM_QUIET_MS,
        });
        wheelGuard.newGestureEvidence = decision.evidence;
        wheelGuard.lastHorizontalEventAt = eventTime;
        wheelGuard.lastAbsDeltaX = absDeltaX;

        if (!decision.startsNewGesture) {
          e.preventDefault();
          return;
        }
        wheelGuard.suppressMomentum = false;
        wheelGuard.newGestureEvidence = 0;
        wheelAccumRef.current = 0;
      } else {
        wheelGuard.lastHorizontalEventAt = eventTime;
        wheelGuard.lastAbsDeltaX = absDeltaX;
      }
      const proposed = wheelAccumRef.current + e.deltaX;
      if (proposed < 0 && (!rightMeta || rightAction === 'none')) return;
      if (proposed > 0 && (!leftMeta || leftAction === 'none')) return;
      e.preventDefault();
      wheelAccumRef.current = proposed;
      setTransitioning(false);
      // Wiersz nie reaguje wizualnie dopóki accum nie przekroczy progu
      // aktywacji — delikatne drgnięcia touchpadem nie migoczą wierszem.
      if (Math.abs(proposed) < WHEEL_ACTIVATION_THRESHOLD) {
        setDx(0);
      } else {
        const signed = proposed > 0 ? proposed - WHEEL_ACTIVATION_THRESHOLD : proposed + WHEEL_ACTIVATION_THRESHOLD;
        setDx(-signed);
      }
      // Po przekroczeniu ACTION_THRESHOLD odpal NATYCHMIAST.
      // User świadomie osiągnął "Puść" - momentum tail nie ma znaczenia.
      // Reszta eventów zostanie zignorowana także po podmianie wiersza,
      // ale pierwszy event po krótkiej ciszy rozpocznie od razu nowy swipe.
      if (Math.abs(proposed) >= WHEEL_ACTION_THRESHOLD) {
        if (wheelEndTimerRef.current) {
          clearTimeout(wheelEndTimerRef.current);
          wheelEndTimerRef.current = null;
        }
        wheelGuard.suppressMomentum = true;
        wheelGuard.newGestureEvidence = 0;
        finalize();
        return;
      }
      if (wheelEndTimerRef.current) clearTimeout(wheelEndTimerRef.current);
      // Pod-thresholdowy timer - finalize gdy user przestaje a nie osiągnął
      // jeszcze "Puść". Skraca się do 0 (return wcześniej) gdy threshold
      // pęka - dlatego 150ms to bezpieczna wartość dla "nie zdecydował".
      wheelEndTimerRef.current = setTimeout(finalize, 150);
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => {
      el.removeEventListener('wheel', handler);
      if (wheelEndTimerRef.current) clearTimeout(wheelEndTimerRef.current);
    };
  }, [enabled, leftAction, rightAction, leftMeta, rightMeta, onLeftSwipe, onRightSwipe, sharedWheelGuardRef]);

  if (!enabled) {
    return <>{children}</>;
  }

  // --- TOUCH (mobile) ---
  const onTouchStart = (e: ReactTouchEvent) => {
    if (e.touches.length !== 1) return;
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, active: true, committed: false };
    setTransitioning(false);
  };
  const onTouchMove = (e: ReactTouchEvent) => {
    const s = touchStartRef.current;
    if (!s || !s.active) return;
    const t = e.touches[0];
    const deltaX = t.clientX - s.x;
    const deltaY = t.clientY - s.y;
    // Przed commit: bierzemy ruch pod uwagę dopiero po przekroczeniu
    // ACTIVATION_THRESHOLD — inaczej tap do otwarcia maila migotał.
    if (!s.committed) {
      if (Math.abs(deltaY) > Math.abs(deltaX) + 6) {
        s.active = false;
        setDx(0);
        return;
      }
      if (Math.abs(deltaX) < ACTIVATION_THRESHOLD) return;
      s.committed = true;
    }
    if (deltaX < 0 && (!leftMeta || leftAction === 'none')) return;
    if (deltaX > 0 && (!rightMeta || rightAction === 'none')) return;
    if (Math.abs(deltaX) > Math.abs(deltaY) && e.cancelable) {
      e.preventDefault();
    }
    // Od ACTIVATION_THRESHOLD — wizualnie "przyklej" wiersz do palca od zera,
    // więc user widzi spójną kontrolę: im więcej ciągnie, tym więcej odsłania.
    const visual = deltaX > 0 ? deltaX - ACTIVATION_THRESHOLD : deltaX + ACTIVATION_THRESHOLD;
    setDx(visual);
  };
  const onTouchEnd = () => {
    const s = touchStartRef.current;
    touchStartRef.current = null;
    if (!s || !s.active || !s.committed) { setDx(0); return; }
    setTransitioning(true);
    if (dx <= -ACTION_THRESHOLD && leftMeta) {
      setDx(-400);
      setTimeout(() => { onLeftSwipe(); setDx(0); setTransitioning(false); }, 150);
    } else if (dx >= ACTION_THRESHOLD && rightMeta) {
      setDx(400);
      setTimeout(() => { onRightSwipe(); setDx(0); setTransitioning(false); }, 150);
    } else {
      setDx(0);
      setTimeout(() => setTransitioning(false), 200);
    }
  };

  // --- POINTER (mysz drag) ---
  const onPointerDown = (e: ReactPointerEvent) => {
    if (e.pointerType === 'touch') return;
    if (e.button !== 0) return;
    pointerStartRef.current = { x: e.clientX, y: e.clientY, id: e.pointerId, committed: false };
    setTransitioning(false);
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    const s = pointerStartRef.current;
    if (!s || s.id !== e.pointerId) return;
    if (e.buttons === 0) { pointerStartRef.current = null; setDx(0); return; }
    const deltaX = e.clientX - s.x;
    const deltaY = e.clientY - s.y;
    if (!s.committed) {
      if (Math.abs(deltaY) > Math.abs(deltaX) + 6) {
        pointerStartRef.current = null;
        setDx(0);
        return;
      }
      if (Math.abs(deltaX) < ACTIVATION_THRESHOLD) return;
      s.committed = true;
    }
    if (deltaX < 0 && (!leftMeta || leftAction === 'none')) return;
    if (deltaX > 0 && (!rightMeta || rightAction === 'none')) return;
    const visual = deltaX > 0 ? deltaX - ACTIVATION_THRESHOLD : deltaX + ACTIVATION_THRESHOLD;
    setDx(visual);
  };
  const onPointerUp = (e: ReactPointerEvent) => {
    const s = pointerStartRef.current;
    if (!s || s.id !== e.pointerId) return;
    pointerStartRef.current = null;
    if (!s.committed) { setDx(0); return; }
    setTransitioning(true);
    if (dx <= -ACTION_THRESHOLD && leftMeta) {
      setDx(-400);
      setTimeout(() => { onLeftSwipe(); setDx(0); setTransitioning(false); }, 150);
    } else if (dx >= ACTION_THRESHOLD && rightMeta) {
      setDx(400);
      setTimeout(() => { onRightSwipe(); setDx(0); setTransitioning(false); }, 150);
    } else {
      setDx(0);
      setTimeout(() => setTransitioning(false), 200);
    }
  };

  const showRightBg = dx > 0 && rightMeta;
  const showLeftBg = dx < 0 && leftMeta;
  // #R8-4 - progressive feedback: opacity rośnie od 0.35 do 1.0 między 0 a
  // ACTION_THRESHOLD. User WIDZI że "jeszcze trochę i akcja się wykona".
  // Po przekroczeniu progu pokazujemy etykietę "Puść" - clear sygnał że
  // teraz puszczenie potwierdza akcję, a cofnięcie ją anuluje. Akcja
  // zawsze wykonuje się tylko na pointerup/touchend, więc user może
  // wycofać się dopóki trzyma palec.
  const intensity = Math.min(Math.abs(dx) / ACTION_THRESHOLD, 1);
  const willTriggerRight = dx >= ACTION_THRESHOLD;
  const willTriggerLeft = dx <= -ACTION_THRESHOLD;
  return (
    // #R10-3 - overscroll-behavior-x: contain blokuje macOS browser back/forward
    // gesture na touchpadzie. Bez tego dwu-palcowy swipe od lewej do prawej
    // odpalał systemowy "wstecz" zamiast naszej akcji swipe na wierszu.
    <div ref={containerRef} className="relative overflow-hidden" style={{ overscrollBehaviorX: 'contain' }}>
      {showRightBg && (
        <div
          className={`absolute inset-y-0 left-0 right-0 flex items-center justify-start px-4 pointer-events-none ${rightMeta!.bg} ${rightMeta!.text} transition-opacity duration-fast`}
          style={{ opacity: 0.35 + intensity * 0.65 }}
        >
          <span className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
            {willTriggerRight && <span className="text-[10px] opacity-80">Puść →</span>}
            {rightMeta!.label}
          </span>
        </div>
      )}
      {showLeftBg && (
        <div
          className={`absolute inset-y-0 left-0 right-0 flex items-center justify-end px-4 pointer-events-none ${leftMeta!.bg} ${leftMeta!.text} transition-opacity duration-fast`}
          style={{ opacity: 0.35 + intensity * 0.65 }}
        >
          <span className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
            {leftMeta!.label}
            {willTriggerLeft && <span className="text-[10px] opacity-80">← Puść</span>}
          </span>
        </div>
      )}
      <div
        style={{
          transform: `translate3d(${dx}px, 0, 0)`,
          transition: transitioning ? 'transform 200ms ease-out' : 'none',
          touchAction: 'pan-y',
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {children}
      </div>
    </div>
  );
}

export function MessagesInboxPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerEmailParam = searchParams.get('customer')?.trim() || undefined;
  const mailboxViewParam = searchParams.get('view');
  const mailboxFolderIdParam = searchParams.get('folderId')?.trim() || null;
  const swipeWheelGuardRef = useRef<SwipeWheelGuard>({
    suppressMomentum: false,
    lastHorizontalEventAt: 0,
    lastAbsDeltaX: 0,
    newGestureEvidence: 0,
  });
  const [stats, setStats] = useState<Stats | null>(null);
  // Swipe preferences (per-user). Default OFF żeby nie zmienić zachowania
  // operatorom którzy przywykli do samego klikania. Load z /users/me/preferences.
  const [swipePrefs, setSwipePrefs] = useState<{
    enabled: boolean;
    left: InboxSwipeAction;
    right: InboxSwipeAction;
  }>({ enabled: false, left: 'trash', right: 'resolved' });
  const reviewQueueMsgIds = useMemo(() => new Set(stats?.reviewQueueMessageIds || []), [stats?.reviewQueueMessageIds]);
  const [selectedMessage, setSelectedMessage] = useState<InboxMessage | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('email');
  const [folderFilter, setFolderFilter] = useState<FolderFilter>(() => (
    customerEmailParam || mailboxFolderIdParam ? 'inbox' : normalizeMailboxView(mailboxViewParam)
  ));
  // Konkretny folder IMAP z drzewa (ma pierwszeństwo nad folderFilter jako filtr listy).
  const [selectedImapFolderId, setSelectedImapFolderId] = useState<string | null>(() => (
    customerEmailParam ? null : mailboxFolderIdParam
  ));
  const [searchQuery, setSearchQuery] = useState('');
  // Debounce search input — FTS jest cięższy niż contains, 300 ms odciąża DB przy pisaniu.
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);
  // Wyłuskane terms do highlightu subject/fromName/from w liście — te same reguły
  // co backend (operatory odcięte, terms >=2 znaki, sanitize unicode+digit).
  const searchHighlightTerms = useMemo(() => {
    if (!debouncedSearchQuery) return [] as string[];
    const plain = debouncedSearchQuery
      .replace(/(?:^|\s)(from|to|subject|has|is|before|after|label):(\S+|"[^"]*")/gi, ' ')
      .trim();
    return plain
      .split(/\s+/)
      .map(t => t.replace(/[^\p{L}\d]/gu, ''))
      .filter(t => t.length >= 2);
  }, [debouncedSearchQuery]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  // Flag: "zaznaczono WSZYSTKIE w folderze" (po kliknięciu bannera Gmail-style).
  // Różne od samego selectedIds.size, bo gdy user odznaczy jeden mail manualnie,
  // flag spada do false i banner powraca do stanu "Zaznaczono X widocznych".
  const [selectAllInFolder, setSelectAllInFolder] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  // Bulk destruktywny confirm (trash/expunge gdy N>100): trzymamy pending action
  // żeby ConfirmDialog mógł go wykonać po OK.
  const [pendingBulkAction, setPendingBulkAction] = useState<{ action: string; count: number; isExpunge: boolean } | null>(null);

  // Conversation status filter
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // #R13-3 - migracja na globalny FullscreenContext (Browser Fullscreen API
  // + body.app-fullscreen). Inbox zachowuje custom 4-kolumnowy layout side-
  // by-side gdy isFullscreen=true. Klasa `inbox-fullscreen` na body zostaje
  // dla CSS-owych override'ów Inboxowych (poza globalną app-fullscreen).
  // localStorage persistence wycięty - Browser Fullscreen API wymaga user
  // activation, więc auto-restore po refresh i tak by nie zadziałał.
  const { isFullscreen, toggle: toggleFullscreen, exit: exitFullscreen } = useFullscreen();
  useEffect(() => {
    if (isFullscreen) document.body.classList.add('inbox-fullscreen');
    else document.body.classList.remove('inbox-fullscreen');
    return () => document.body.classList.remove('inbox-fullscreen');
  }, [isFullscreen]);
  // Reset the conversation-owned composer before paint, including auto-advance.
  // Keeping its wrapper mounted in fullscreen must not keep the previous recipient.
  useLayoutEffect(() => {
    const nextId = selectedMessage?.id || null;
    const changed = composerMessageRef.current !== nextId;
    if (changed) {
      if (!nextId) mailboxRequests.begin('message-detail', selectedStoreId);
      composerMessageRef.current = nextId;
      composerRevisionRef.current += 1;
      setComposerAttachments([]);
      setComposerAttaching(false);
      setShowCcBcc(false);
      setShowTemplates(false);
      setShowAiDraft(false);
      setAiDraft('');
      setAiError('');
      setAiLoading(false);
      setComposeTranscribing(false);
      setTemplateApplying(false);
      setSlashQuery(null);
      setSlashDropdownPos(null);
      slashStartRef.current = null;
      if (isComposeRecording) cancelComposeRecording();
    }
    if (selectedMessage && !selectedMessage.isDraft && isFullscreen && (changed || composeModeRef.current === 'none')) {
      startReply();
    } else if (changed && (nextId !== null || composeMode !== 'new')) {
      composeModeRef.current = 'none';
      setComposeMode('none');
      setComposerOwnerId(null);
      setComposeData({ to: '', cc: '', bcc: '', subject: '', bodyHtml: '', parentId: '' });
    }
  }, [isFullscreen, selectedMessage?.id]);
  // #R15-1 - lokalny skrót `F` USUNIĘTY. Spójność: jeden globalny skrót
  // Cmd/Ctrl+Shift+F we wszystkich modułach (FullscreenContext) zamiast
  // per-strona różnych skrótów. User decyzja.

  const [inboxSidebarCollapsed, setInboxSidebarCollapsed] = useState(() => {
    return localStorage.getItem('ecomflow_inbox_sidebar_collapsed') === 'true';
  });

  // Expunge confirm dialog (trwałe usunięcie z Kosza)
  const [expungeConfirm, setExpungeConfirm] = useState<{ ids: string[]; mode: 'single' | 'bulk' } | null>(null);


  // Store switcher — wymuszony konkretny sklep (bez unified "all"). Kolejność
  // fallbacku przy mount: localStorage (ostatnia sesja) → resetowany po hydratacji
  // tenant settings (defaultStoreId z ustawień) → pierwszy sklep z listy. Pusty
  // string = jeszcze nie ustalony (render czeka na hydratację).
  const [stores, setStores] = useState<StoreItem[]>([]);
  const [storesLoadFailed, setStoresLoadFailed] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<string>(() => {
    const linkedStore = searchParams.get('storeId')?.trim();
    if (linkedStore && linkedStore !== 'all') return linkedStore;
    const stored = localStorage.getItem('ecomflow_messages_storeId');
    // Migracja: stare wartości "all" wyrzucamy — unified view został wycofany.
    return stored && stored !== 'all' ? stored : '';
  });
  const mailboxRequests = useRef(createMailboxRequestScope()).current;
  mailboxRequests.select(selectedStoreId);
  useEffect(() => {
    mailboxRequests.select(selectedStoreId);
    return () => mailboxRequests.select('');
  }, [mailboxRequests, selectedStoreId]);
  const [badgeDisplay, setBadgeDisplay] = useState<{ inbox: boolean; imapFolders: boolean; favorites: boolean }>({
    inbox: true,
    imapFolders: true,
    favorites: true,
  });

  // Signaturki IMAP configów — renderowane jako nieedytowalny podgląd pod
  // edytorem Quill (user widzi co wyśle, ale nie może ingerować). Backend
  // dokleja stopkę do body przy wysyłce przez appendInboxSignature.
  type InboxCfgMini = { id: string; storeId: string | null; isActive: boolean; signatureHtml: string | null; signatureMarginTop: number };
  const [inboxConfigs, setInboxConfigs] = useState<InboxCfgMini[]>([]);
  // #R2-1 - mapping conversation status (resolved/pending/important) → folder IMAP
  // per skrzynka (configId). Używane w Drafty AI quick actions: pokazujemy ikonki
  // tylko tych statusów, które MAJĄ zmapowany folder dla configa danego draftu.
  type StatusMapping = { pending: Record<string, string>; important: Record<string, string>; resolved: Record<string, string>; starred: Record<string, string> };
  const [statusMapping, setStatusMapping] = useState<StatusMapping>({ pending: {}, important: {}, resolved: {}, starred: {} });
  // Modal szczegółów zamówienia — ten sam który działa w Logistyce, żeby
  // user nie tracił kontekstu rozmowy klikając zamówienie z panelu klienta.
  const [complaintModalId, setComplaintModalId] = useState<string | null>(null);
  const [orderModalId, setOrderModalId] = useState<string | null>(null);
  const [orderModalData, setOrderModalData] = useState<Order | null>(null);
  const [similarOrderSource, setSimilarOrderSource] = useState<Order | null>(null);
  const [orderModalLoading, setOrderModalLoading] = useState(false);
  // Statuses tree — uzywane PRZEZ panel klienta (kolor plakietki statusu, ten
  // sam co w Logistyce/Statusy) ORAZ przez OrderDetailModal po kliku w
  // zamowienie. Preload przy mount zeby plakietka statusu mogla pokazac
  // kolor z brandingu od pierwszego renderu (zamiast hardcoded mappingu).
  const [orderStatusesCache, setOrderStatusesCache] = useState<StatusGroup[] | null>(null);
  const { theme } = useTheme();
  const activeSignature = useMemo(() => {
    const storeId = selectedStoreId || selectedMessage?.store?.id || null;
    const forStore = inboxConfigs.find(c => c.isActive && c.storeId === storeId);
    const html = (forStore?.signatureHtml || '').trim();
    if (!html) return null;
    return { html, marginTop: forStore?.signatureMarginTop ?? 10 };
  }, [inboxConfigs, selectedStoreId, selectedMessage?.store?.id]);

  // AI Reply state
  const [aiDraft, setAiDraft] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [showAiDraft, setShowAiDraft] = useState(false);
  const [aiProvider, setAiProvider] = useState<string | null>(null);

  // Inline compose state
  const [composeMode, setComposeMode] = useState<'none' | 'new' | 'reply'>('none');
  const [composeData, setComposeData] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    bodyHtml: '',
    parentId: '',
  });
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [showDraftConfirm, setShowDraftConfirm] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  // Guard against double-submit — user kliknął 3x "Wyślij" zanim pierwszy
  // request zdążył wrócić i dostał 3 identyczne wiadomości w wątku.
  const [sendingMessage, setSendingMessage] = useState(false);
  const sendSubmissionLockRef = useRef(false);
  const composeModeRef = useRef(composeMode);
  const [pendingReplies, setPendingReplies] = useState<PendingReply[]>([]);
  const pendingInThread = visiblePendingReplies(pendingReplies, selectedStoreId, selectedMessage);
  const pendingReplyRef = useRef<HTMLDivElement>(null);
  const [composerOwnerId, setComposerOwnerId] = useState<string | null>(null);
  const composerMessageRef = useRef<string | null>(null);
  const composerRevisionRef = useRef(0);
  // Lista folderów IMAP (flattened z getFoldersTree) — do dropdownu "Przenieś".
  // totalCount bierzemy z endpointu (oparte na DB groupBy, nie zamrożonym
  // InboxImapFolder.totalCount) — pokazujemy obok nazwy folderu w menu.
  const [imapFoldersFlat, setImapFoldersFlat] = useState<Array<{
    id: string; path: string; name: string; configId: string; specialUse: string | null; totalCount: number;
  }>>([]);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [showBulkMoveMenu, setShowBulkMoveMenu] = useState(false);

  // C3: załączniki composera (upload przez POST /messages/attachments → state →
  // wysyłka w createMessage.attachments). Drag-drop + paste obsługiwane na całym
  // composer wrapper.
  type ComposerAttachment = { storedAs: string; filename: string; size: number; mimeType: string };
  const [composerAttachments, setComposerAttachments] = useState<ComposerAttachment[]>([]);
  const [reviewAttachments, setReviewAttachments] = useState<Record<string, ReplyAttachment[]>>({});
  const [reviewUploading, setReviewUploading] = useState<Record<string, boolean>>({});
  const [composerAttaching, setComposerAttaching] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const composerFileInputRef = useRef<HTMLInputElement>(null);
  const quillRef = useRef<ReactQuill | null>(null);
  // #18 - selection-end-outside protection: gdy user zaznacza tekst wewnątrz modala
  // i puszcza myszkę poza nim, default `onClick` na backdropie zamknie modal.
  // Refy poniżej trackują czy mousedown szedł na content - blokujemy wtedy zamknięcie.
  const quickEditDownInsideRef = useRef(false);
  const suggestionDownInsideRef = useRef(false);

  // Templates state
  const [templates, setTemplates] = useState<MessageTemplateItem[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [reviewTemplateId, setReviewTemplateId] = useState<string | null>(null);
  // Labels (custom foldery zostały zastąpione natywnym drzewem IMAP w Sidebarze).
  const [labels, setLabels] = useState<InboxLabelItem[]>([]);
  const [activeLabelFilter, setActiveLabelFilter] = useState<string | null>(null);
  const [showAddLabel, setShowAddLabel] = useState(false);

  // Advanced search
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advSearch, setAdvSearch] = useState({
    from: '', to: '', subject: '',
    dateFrom: '', dateTo: '',
    hasAttachment: false, isUnread: false, isStarred: false,
    labelName: '',
  });

  // Sender card popup
  const [showSenderCard, setShowSenderCard] = useState(false);
  // Helper: wyciągnij email z formatu "Name <email@x.com>" lub zwróć jak jest.
  const extractEmail = extractMailboxEmail;
  const getReplyRecipient = (message: Pick<InboxMessage, 'from' | 'replyTo'> | null | undefined): string => {
    if (!message) return '';
    return extractEmail(message.replyTo) || extractEmail(message.from);
  };
  const copyToClipboard = (text: string, label = 'Skopiowano') => {
    navigator.clipboard.writeText(text).then(() => showToast(label));
  };

  // Customer panel state (Task 2)
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  // Notes state (Task 3)
  const [notes, setNotes] = useState<ConversationNote[]>([]);
  const [noteInput, setNoteInput] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);

  // Coupon modal state (Task 4)
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponForm, setCouponForm] = useState({ code: '', discountType: 'percent' as 'percent' | 'fixed_cart', amount: '', singleUse: true });
  const [couponLoading, setCouponLoading] = useState(false);

  // AI template personalization (Task 5)
  const [aiTemplatePersonalizationEnabled, setAiTemplatePersonalizationEnabled] = useState(false);
  const [templateApplying, setTemplateApplying] = useState(false);

  // Slash command state
  const [slashQuery, setSlashQuery] = useState<string | null>(null);
  const [slashIndex, setSlashIndex] = useState(0);
  // Pozycja dropdownu liczona w viewport coordinates (position:fixed).
  // Renderujemy przez Portal w document.body żeby ominąć overflow:hidden /
  // clip context na rodzicu editora. Bez tego dropdown był renderowany
  // w DOM ale nigdy widoczny (clipped przez scroll wrapper).
  const [slashDropdownPos, setSlashDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  // Cache pozycji startu `/sza` w plain text editora w momencie ostatniego
  // matchu. Klik na button w Portalu zabiera focus z Quill — `getSelection()`
  // zwraca null. Bez tego cache `applySlashTemplate` nie wie skąd usunąć
  // "/sza" przed wstawieniem szablonu (i wstawiał szablon na końcu, zostawiając
  // "/sza" w treści).
  const slashStartRef = useRef<{ start: number; length: number } | null>(null);
  // Ref do diva dropdownu — używany do click-outside detection.
  const slashDropdownRef = useRef<HTMLDivElement | null>(null);
  // autoSendCountdown usunięty 1.49.13 — auto-send szablonu po wyborze był
  // anty-patternem (incident: testowy szablon poszedł do klienta przed
  // anulowaniem). Szablon zawsze ląduje w composerze do edycji.

  // Auto-scroll to bottom of chat
  const lastMessageRef = useRef<HTMLDivElement>(null);

  // Lightbox for attachment preview
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxType, setLightboxType] = useState<'image' | 'pdf'>('image');
  const [lightboxFilename, setLightboxFilename] = useState<string>('');
  const [previewingAttachmentKey, setPreviewingAttachmentKey] = useState<string | null>(null);

  useEffect(() => {
    composeModeRef.current = composeMode;
  }, [composeMode]);

  useEffect(() => {
    if (!lightboxUrl?.startsWith('blob:')) return;
    return () => URL.revokeObjectURL(lightboxUrl);
  }, [lightboxUrl]);

  // Review queue (AI drafts)
  const [reviewQueue, setReviewQueue] = useState<ReviewQueueItem[]>([]);
  const [aiDailyReport, setAiDailyReport] = useState<InboxAiDailyReport | null>(null);
  const [reviewQueueLoading, setReviewQueueLoading] = useState(false);
  const [reviewActionLoading, setReviewActionLoading] = useState<string | null>(null);
  const [expandedDraftId, setExpandedDraftId] = useState<string | null>(null);
  // Draft zaakceptowany do edytora nie powinien ponownie zasłaniać wpisanej
  // treści. Sam rekord kolejki pozostaje do czasu wysłania albo odrzucenia.
  const [handledThreadDraftIds, setHandledThreadDraftIds] = useState<Set<string>>(new Set());
  const [selectedDraftIds, setSelectedDraftIds] = useState<Set<string>>(new Set());
  const [draftSelectionMode, setDraftSelectionMode] = useState(false);
  const [draftCustomerData, setDraftCustomerData] = useState<CustomerData | null>(null);
  const [draftCustomerLoading, setDraftCustomerLoading] = useState(false);

  // Suggestion popup (AI reply with user hint)
  const [showSuggestionPopup, setShowSuggestionPopup] = useState(false);
  const [suggestionText, setSuggestionText] = useState('');

  // Szybka edycja draftu AI — modal z textarea, override treści bez regeneracji.
  const [quickEditDraftId, setQuickEditDraftId] = useState<string | null>(null);
  const [quickEditValue, setQuickEditValue] = useState('');
  const [quickEditSaving, setQuickEditSaving] = useState(false);
  useEffect(() => {
    if (!quickEditDraftId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !quickEditSaving) setQuickEditDraftId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [quickEditDraftId, quickEditSaving]);
  // M2: Track last suggestion per review item/compose to restore on regenerate
  const lastSuggestionsRef = useRef<Map<string, string>>(new Map());
  const [suggestionContext, setSuggestionContext] = useState<{
    type: 'review_queue' | 'compose';
    reviewItemId?: string;
    messageId?: string;
  } | null>(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Hard-stop timeout dla suggestion popup (review queue AI drafts + compose suggestions).
  // User zostawia mikrofon = tokeny Whisper bez capa → auto-stop po limicie.
  const recordingLimitRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Compose voice recording (separate from suggestion popup)
  const [isComposeRecording, setIsComposeRecording] = useState(false);
  const [composeRecordingTime, setComposeRecordingTime] = useState(0);
  const [composeTranscribing, setComposeTranscribing] = useState(false);
  const composeMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const composeChunksRef = useRef<Blob[]>([]);
  const composeRecordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Hard-stop timeout — forsuje auto-stop nagrywania po VOICE_RECORDING_LIMIT_SECONDS.
  const composeRecordingLimitRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Flag "anuluj bez transkrypcji" — gdy user zamknie modal composera w trakcie
  // nagrywania, chcemy przerwać recorder BEZ wysyłania audio do Whisper (oszczędność
  // tokenów + intencja user'a). Onstop sprawdza tę flagę i skipuje transcribe call.
  const composeCancelledRef = useRef(false);

  // Set id-ów wiadomości które dotarły przez socket w ostatnich 2s. Wiersz z
  // takim id-em dostaje data-new-item="true" → delikatna animacja fade-in.
  // Po 2s id jest usuwany żeby animacja nie pozostała aktywna przy re-renderze.
  const [newMessageIds, setNewMessageIds] = useState<Set<string>>(new Set());

  // Set id-ów wiadomości w trakcie usuwania/archiwizacji. Wiersz dostaje
  // class .pending-delete → CSS transition (opacity + translateX). Po ~220ms
  // (match z CSS) uruchamiamy faktyczną mutację, a item znika z cache.
  // Wrapper markPending opakowuje callback, obsługuje cleanup setu.
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set());
  const markPendingAndRun = (id: string, fn: () => void) => {
    setPendingDeleteIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setTimeout(() => {
      fn();
      // Po uruchomieniu mutacji, onMutate w useInboxMutations filtruje
      // item z cache. Czyścimy pendingDeleteIds po kolejnych 500ms żeby
      // pozbyć się stale referencji — w większości przypadków item już
      // nie istnieje, ale strażnik nie zaszkodzi.
      setTimeout(() => {
        setPendingDeleteIds(prev => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, 500);
    }, 220);
  };

  // AI compose menu
  const [showAiMenu, setShowAiMenu] = useState(false);

  // Right info panel (customer, notes, labels, coupon)
  const [showInfoPanel, setShowInfoPanel] = useState(false);

  // Email config status
  const [hasEmailConfig, setHasEmailConfig] = useState<boolean | null>(null);

  // Toast — obsługuje optional action button (np. "Cofnij" po delete/archive)
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
    action?: { label: string; onClick: () => void };
  } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (
    message: string,
    type: 'success' | 'error' | 'warning' = 'success',
    options?: { action?: { label: string; onClick: () => void }; durationMs?: number },
  ) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type, action: options?.action });
    toastTimerRef.current = setTimeout(() => setToast(null), options?.durationMs ?? 3000);
  };

  const showMissingTemplateWarning = (missingVariables: MissingTemplateVariableDto[]) => {
    if (missingVariables.length === 0) return;
    const visibleLabels = missingVariables.slice(0, 3).map(variable => variable.label).join(', ');
    const remaining = missingVariables.length > 3 ? ` i ${missingVariables.length - 3} więcej` : '';
    showToast(
      `Szablon został wczytany. Uzupełnij czerwone pola: ${visibleLabels}${remaining}.`,
      'warning',
      { durationMs: 8000 },
    );
  };

  // Krok 3: optimistic mutations dla skrzynki — natychmiastowy update cache,
  // rollback przy błędzie, IMAP sync async w workerze.
  // loadStats jest zdefiniowane niżej — do useInboxMutations przekazujemy
  // stabilną referencję przez lazy wrapper (bez stawiania hooków "zanim" się da).
  const loadStatsRef = useRef<() => void>(() => {});
  // Lazy ref dla loadReviewQueue — wywoływany z polling interval i event listener
  // zanim funkcja sama jest zadeklarowana niżej w pliku (useCallback order).
  const loadReviewQueueRef = useRef<() => void>(() => {});
  const inboxInteractionActiveRef = useRef(false);
  const pendingInboxRefreshRef = useRef(false);
  const mutations = useInboxMutations(
    (m, k) => showToast(m, k === 'error' ? 'error' : 'success'),
    () => loadStatsRef.current?.(),
    selectedStoreId || undefined,
  );

  const persistMailboxSelection = useCallback((view: FolderFilter, folderId: string | null = null, storeId?: string) => {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      if (storeId) next.set('storeId', storeId);
      if (folderId) {
        next.set('folderId', folderId);
        next.delete('view');
      } else {
        next.set('view', view);
        next.delete('folderId');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // URL jest źródłem prawdy dla aktywnej zakładki. Dzięki temu F5 oraz
  // nawigacja Wstecz/Dalej wracają do tego samego widoku, a brak parametru
  // nadal oznacza domyślne "Odpowiedzi AI".
  useEffect(() => {
    if (customerEmailParam) {
      setSelectedImapFolderId(null);
      setFolderFilter('inbox');
      return;
    }
    if (mailboxFolderIdParam) {
      setSelectedImapFolderId(mailboxFolderIdParam);
      setFolderFilter('inbox');
      return;
    }
    setSelectedImapFolderId(null);
    setFolderFilter(normalizeMailboxView(mailboxViewParam));
  }, [customerEmailParam, mailboxFolderIdParam, mailboxViewParam]);

  const handleStoreChange = useCallback((storeId: string) => {
    if (!storeId || storeId === selectedStoreId) return;

    mailboxRequests.select(storeId);
    setStats(null);
    setTemplates([]);
    setAiDailyReport(null);
    setImapFoldersFlat([]);
    setSelectedStoreId(storeId);
    setSelectedImapFolderId(null);
    const nextView: FolderFilter = customerEmailParam ? 'inbox' : 'review_queue';
    setFolderFilter(nextView);
    persistMailboxSelection(nextView, null, storeId);
    setSelectedMessage(null);
    setSelectedIds(new Set());
    setSelectionMode(false);
    setSelectAllInFolder(false);
    setActiveLabelFilter(null);
    setStatusFilter('all');
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setAdvSearch({
      from: '', to: '', subject: '', dateFrom: '', dateTo: '',
      hasAttachment: false, isUnread: false, isStarred: false, labelName: '',
    });
    setShowAdvancedSearch(false);
    setComposeMode('none');
    setComposeData({ to: '', cc: '', bcc: '', subject: '', bodyHtml: '', parentId: '' });
    setComposerAttachments([]);
    setShowCcBcc(false);
    setShowDraftConfirm(false);
    setAiDraft('');
    setAiError('');
    setShowAiDraft(false);
    setReviewQueue([]);
    setReviewAttachments({});
    setReviewUploading({});
    setSelectedDraftIds(new Set());
    setExpandedDraftId(null);
    setCustomerData(null);
    setDraftCustomerData(null);
    setNotes([]);
    setNoteInput('');
    setShowInfoPanel(false);
    setShowMoveMenu(false);
    setShowBulkMoveMenu(false);
  }, [customerEmailParam, persistMailboxSelection, selectedStoreId, mailboxRequests]);

  const handleStoresReorder = useCallback(async (storeIds: string[]) => {
    const previous = stores;
    const byId = new Map(stores.map(store => [store.id, store]));
    const reordered = storeIds
      .map((id, position) => {
        const store = byId.get(id);
        return store ? { ...store, messageInboxPosition: position } : null;
      })
      .filter((store): store is StoreItem => store !== null);
    if (reordered.length !== stores.length) return;

    setStores(reordered);
    try {
      const response = await messagesApi.reorderMailboxStores(storeIds);
      setStores(response.data.data);
    } catch {
      setStores(previous);
      showToast('Nie udało się zapisać kolejności sklepów', 'error');
    }
  }, [stores]);

  // Close suggestion popup on Escape
  useEffect(() => {
    if (!showSuggestionPopup) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !suggestionLoading) {
        setShowSuggestionPopup(false);
        setSuggestionContext(null);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [showSuggestionPopup, suggestionLoading]);

  // Close AI menu on outside click / Escape
  useEffect(() => {
    if (!showAiMenu) return;
    const handleClick = () => setShowAiMenu(false);
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowAiMenu(false); };
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('click', handleClick); document.removeEventListener('keydown', handleKey); };
  }, [showAiMenu]);



  // Persist sidebar collapsed state
  useEffect(() => {
    localStorage.setItem('ecomflow_inbox_sidebar_collapsed', String(inboxSidebarCollapsed));
  }, [inboxSidebarCollapsed]);

  // Quill config
  const quillModules = useMemo(() => withPlainTextPaste({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean'],
    ],
  }), []);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link',
  ];

  // Quill domyślnie przy edycji istniejącego linka zmienia tylko href, anchor
  // text zostaje stary — przez co klient maila widzi w treści starą wartość
  // (tekst), a klika dopiero do nowego URLa. User chce żeby przy edycji
  // linka tekst też się aktualizował na nowy URL. Patch po stronie
  // tooltip.save: w trybie edit zastąp całość (tekst+link) nowym URLem.
  useEffect(() => {
    if (composeMode !== 'reply' && composeMode !== 'new') return;
    const tryPatch = (attempt: number) => {
      const editor = quillRef.current?.getEditor();
      const tooltip: any = (editor as any)?.theme?.tooltip;
      if (!editor || !tooltip || !tooltip.save) {
        if (attempt < 8) setTimeout(() => tryPatch(attempt + 1), 80);
        return;
      }
      if (tooltip._anchorSyncPatched) return;
      tooltip._anchorSyncPatched = true;
      const originalSave = tooltip.save.bind(tooltip);
      tooltip.save = function () {
        const value = (this.textbox?.value ?? '').trim();
        const isEditing = this.root?.classList?.contains('ql-editing');
        if (isEditing && value && this.linkRange) {
          const r = this.linkRange;
          editor.deleteText(r.index, r.length, 'user');
          editor.insertText(r.index, value, 'link', value, 'user');
          this.hide();
          return;
        }
        originalSave();
      };
    };
    tryPatch(0);
  }, [composeMode]);

  // Load stores + tenant inbox settings (defaultStoreId) — rozstrzyga z czym
  // startuje store switcher. Priorytety: localStorage (ostatnia sesja) → defaultStoreId
  // z settings → pierwszy aktywny sklep. Żadnego "all" — unified view wycofany.
  useEffect(() => {
    // #R2-1 - status mapping load przy mount, raz na sesję (mapping rzadko się
    // zmienia, refetch po zmianie ustawień nie jest na liście priorytetów).
    messagesApi.getStatusMapping()
      .then((res) => {
        const data = res.data as Partial<StatusMapping> | undefined;
        if (data) {
          setStatusMapping({
            pending: data.pending || {},
            important: data.important || {},
            resolved: data.resolved || {},
            starred: data.starred || {},
          });
        }
      })
      .catch(() => { /* mapping opcjonalny - brak nie psuje UI */ });

    inboxEmailApi.getConfigs().then((configsRes) => {
      const configs = configsRes.data?.data || [];
      // Konfiguracje skrzynek są ładowane niezależnie od listy sklepów. Awaria
      // tego endpointu nie może wygasić przełącznika sklepów w sidebarze.
      setInboxConfigs(configs.map((c: { id: string; store?: { id: string } | null; isActive: boolean; signatureHtml?: string | null; signatureMarginTop?: number }) => ({
        id: c.id,
        storeId: c.store?.id || null,
        isActive: c.isActive,
        signatureHtml: c.signatureHtml ?? null,
        signatureMarginTop: c.signatureMarginTop ?? 10,
      })));
    }).catch(() => { /* brak podpisów nie blokuje listy sklepów */ });

    Promise.all([
      messagesApi.getMailboxStores(),
      messagesApi.getTenantSettings().catch(() => null),
    ]).then(([storesRes, settingsRes]) => {
      const allStores: StoreItem[] = storesRes.data.data || [];
      setStores(allStores);
      setStoresLoadFailed(false);

      const inboxSettings = (settingsRes?.data as { inbox?: { defaultStoreId?: string | null; badgeDisplay?: { inbox?: boolean; imapFolders?: boolean; favorites?: boolean } } } | undefined)?.inbox;
      const tenantDefault = inboxSettings?.defaultStoreId ?? null;
      if (inboxSettings?.badgeDisplay) {
        setBadgeDisplay({
          inbox: inboxSettings.badgeDisplay.inbox ?? true,
          imapFolders: inboxSettings.badgeDisplay.imapFolders ?? true,
          favorites: inboxSettings.badgeDisplay.favorites ?? true,
        });
      }
      setSelectedStoreId(prev => {
        // Uszanuj bieżącą selekcję jeśli wciąż dostępna po hydratacji listy.
        if (prev && allStores.some(s => s.id === prev)) return prev;
        // Spróbuj defaultStoreId z settings (o ile ma aktywną skrzynkę IMAP).
        if (tenantDefault && allStores.some(s => s.id === tenantDefault)) return tenantDefault;
        // Fallback: pierwszy z listy (user ma aktywne skrzynki — wybierzemy którąkolwiek).
        return allStores.find(store => store.mailboxCount > 0)?.id ?? allStores[0]?.id ?? '';
      });
    }).catch(() => setStoresLoadFailed(true));
  }, []);

  // Save store selection (pomijamy pusty string z initial state przed hydratacją).
  useEffect(() => {
    if (selectedStoreId) localStorage.setItem('ecomflow_messages_storeId', selectedStoreId);
  }, [selectedStoreId]);

  // Zapytania do API czekają aż storeId zostanie ustalony — bez tego wysłalibyśmy
  // unified query (brak storeId) co wraca do starego zachowania.
  const storeIdParam = selectedStoreId || undefined;
  // storeId jest obowiązkowym elementem kontekstu także dla konkretnego folderu IMAP.
  const messageListStoreIdParam = storeIdParam;

  // Deeplink z karty zamówienia: /messages?customer=klient@sklep.pl filtruje listę
  // po adresie (from LIKE || to LIKE). Pozwala operatorowi wejść w cały wątek
  // z konkretnym klientem bez ręcznego wyszukiwania. Gdy brak parametru — wartość
  // pusta i filtr się nie aktywuje.
  // Infinite scroll: useInfiniteQuery replaces manual loadMessages + pagination state
  const messagesQueryKey = ['messages', sourceFilter, folderFilter, selectedImapFolderId, debouncedSearchQuery, messageListStoreIdParam, activeLabelFilter, statusFilter, customerEmailParam];
  const {
    data: messagesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: loading,
  } = useInfiniteQuery({
    queryKey: messagesQueryKey,
    queryFn: async ({ pageParam = 1 }) => {
      const params: Record<string, unknown> = {
        page: pageParam,
        limit: 50,
        source: sourceFilter,
        ...(selectedImapFolderId ? { imapFolderId: selectedImapFolderId } : { folder: folderFilter }),
        search: debouncedSearchQuery || undefined,
        storeId: messageListStoreIdParam,
        customerEmail: customerEmailParam,
      };
      if (activeLabelFilter) {
        params.labelId = activeLabelFilter;
      }
      if (statusFilter !== 'all') {
        params.conversationStatus = statusFilter;
      }
      const response = await messagesApi.getMessages(params as any);
      return response.data;
    },
    getNextPageParam: (lastPage: any) => {
      const { page, pages } = lastPage.pagination;
      return page < pages ? page + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: Boolean(messageListStoreIdParam),
  });
  const rawMessages: InboxMessage[] = messagesData?.pages.flatMap((p: any) => p.data) ?? [];
  // Total wiadomości w folderze (wg aktualnych filtrów) — z pagination.total
  // backendu. Używane przez banner "Zaznacz wszystkie w folderze" żeby pokazać
  // ile jest do zaznaczenia poza widocznymi.
  const totalInFolder: number = messagesData?.pages[0]?.pagination?.total ?? 0;

  // Czy user jest w widoku Kosza? Pokrywa oba przypadki:
  //   - Wirtualny folder "Kosz" w sidebarze (folderFilter === 'trash')
  //   - Konkretny folder IMAP z drzewa z specialUse='\\Trash' (selectedImapFolderId)
  // Używane żeby toolbar pokazał "Usuń trwale" i bulkAction wywołał expunge confirm,
  // zamiast soft-delete do Kosza (bezsensowne skoro user już jest w Koszu).
  const isInTrashView = (): boolean => {
    if (debouncedSearchQuery.trim()) return false;
    if (folderFilter === 'trash') return true;
    if (selectedImapFolderId) {
      const folder = imapFoldersFlat.find((f) => f.id === selectedImapFolderId);
      return folder?.specialUse === '\\Trash';
    }
    return false;
  };

  // Dedupe przejściowych duplikatów (restore-any flow: przez 2-5s po Cofnij
  // mogą istnieć dwa rekordy A+B/B+C dla tego samego maila z różnymi UID,
  // zanim worker cleanup zdąży usunąć stary). Reguły bezpieczeństwa:
  //   - Dedupe TYLKO w ramach tego samego folderu IMAP (imapFolderId match).
  //     Nie łączymy Sent + Inbox (legit case gdy user wysłał sam do siebie).
  //   - Rekord bez Message-ID header w metadata nigdy nie jest ukrywany
  //     (może być legit mail bez header'a, nie ryzykujemy utraty widoczności).
  //   - Priorytet: wyższy imapUid wygrywa (nowsza kopia na serwerze IMAP,
  //     zwykle z body). Stary rekord z nieaktualnym UID jest ukrywany.
  //   - Backend nic nie traci — dedupe to wyłącznie UI filter.
  const messages: InboxMessage[] = useMemo(() => {
    const bestPerKey = new Map<string, InboxMessage>();
    const result: InboxMessage[] = [];
    const indexMap = new Map<string, number>();

    for (const m of rawMessages) {
      const msgId = (m.metadata as { messageId?: unknown } | null)?.messageId;
      const hasHeader = typeof msgId === 'string' && msgId.length > 0;
      if (!hasHeader || !m.imapFolderId) {
        result.push(m);
        continue;
      }
      const key = `${m.imapFolderId}::${msgId}`;
      const existing = bestPerKey.get(key);
      if (!existing) {
        bestPerKey.set(key, m);
        indexMap.set(key, result.length);
        result.push(m);
        continue;
      }
      // Duplikat — preferuj rekord z wyższym imapUid
      const existingUid = existing.imapUid ?? 0;
      const candidateUid = m.imapUid ?? 0;
      if (candidateUid > existingUid) {
        bestPerKey.set(key, m);
        const idx = indexMap.get(key);
        if (idx !== undefined) result[idx] = m;
      }
      // else: ukryty (nie dodajemy do result)
    }

    // The server identity follows parents through other folders and pages.
    const finalResult = collapseMessageConversations(result);
    // Offset pagination + optimistic removals mogą na chwilę zostawić stare
    // dalsze strony w cache infinite-query. Jeśli page 1 mówi, że folder ma
    // już mniej rekordów (w tym 0), nie pokazujemy stale overflow z cache.
    if (totalInFolder <= 0) return [];
    return finalResult.length > totalInFolder ? finalResult.slice(0, totalInFolder) : finalResult;
  }, [rawMessages, totalInFolder]);

  // Helper: invalidate messages query (replaces loadMessages() callsites)
  const invalidateMessages = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['messages'] });
  }, [queryClient]);

  // C2: infinite scroll obsługiwany przez <Virtuoso endReached={...}> — bez
  // IntersectionObserver (sentinel znikał przy virtualizacji). Trigger na
  // fetchNextPage jest bezpośrednio w itemContent wrapper.

  const loadStats = useCallback(async () => {
    if (!storeIdParam) return;
    const isCurrent = mailboxRequests.begin('stats', storeIdParam);
    try {
      const params: { storeId?: string; conversationStatus?: string } = { storeId: storeIdParam };
      // Gdy user ma aktywny status-filter w widoku Inbox, badge "Odebrane" ma
      // odzwierciedlać to co widoczne na liście (nie globalnie wszystkie unread).
      if (folderFilter === 'inbox' && statusFilter !== 'all') {
        params.conversationStatus = statusFilter;
      }
      const response = await messagesApi.getStats(params);
      if (!isCurrent()) return;
      setStats(response.data);
      void queryClient.invalidateQueries({ queryKey: ['navigation-messages'] });
      messagesApi.getMailboxStores()
        .then((storesResponse) => {
          setStores(storesResponse.data.data || []);
          setStoresLoadFailed(false);
        })
        .catch(() => setStoresLoadFailed(true));
    } catch {
      // ignore
    }
  }, [storeIdParam, folderFilter, statusFilter, mailboxRequests, queryClient]);

  // Daj useInboxMutations dostęp do aktualnej funkcji loadStats — dzięki temu
  // po każdej mutacji (read/unread/trash/archive/move) sidebar automatycznie
  // odświeża licznik "Odebrane" bez potrzeby klikania Odśwież.
  useEffect(() => {
    loadStatsRef.current = loadStats;
  }, [loadStats]);

  const refreshInboxInBackground = useCallback((includeReviewQueue = true) => {
    if (inboxInteractionActiveRef.current) {
      pendingInboxRefreshRef.current = true;
      return;
    }
    queryClient.invalidateQueries({ queryKey: ['messages'] });
    queryClient.invalidateQueries({ queryKey: ['imap-folders-tree'] });
    loadStatsRef.current?.();
    if (includeReviewQueue) loadReviewQueueRef.current?.();
  }, [queryClient]);

  // Refreshe listy nie powinny przerywać pisania, wyboru szablonu ani pracy
  // w modalach. Eventy odkładamy i wykonujemy jeden zbiorczy refresh zaraz po
  // zakończeniu aktywnej interakcji.
  useEffect(() => {
    const isActive = composeMode !== 'none'
      || showTemplates
      || slashQuery !== null
      || showSuggestionPopup
      || Boolean(complaintModalId)
      || Boolean(reviewTemplateId)
      || Boolean(quickEditDraftId)
      || Boolean(noteInput.trim())
      || noteSubmitting
      || showCouponModal;
    inboxInteractionActiveRef.current = isActive;
    if (!isActive && pendingInboxRefreshRef.current) {
      pendingInboxRefreshRef.current = false;
      refreshInboxInBackground();
    }
  }, [
    composeMode,
    showTemplates,
    slashQuery,
    showSuggestionPopup,
    quickEditDraftId,
    reviewTemplateId,
    complaintModalId,
    noteInput,
    noteSubmitting,
    showCouponModal,
    refreshInboxInBackground,
  ]);

  // Polling fallback: co 45s dociągaj świeży stan listy + licznika, żeby
  // w razie padniętego socketa (IDLE z home.pl bywa nietrwałe) nowe maile
  // nie czekały na manualne kliknięcie Odśwież. 45s to kompromis między
  // responsywnością a obciążeniem backendu/IMAP.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return;
      // Przy działającym websocketcie eventy są źródłem świeżego stanu. Polling
      // pozostaje wyłącznie fallbackiem po utracie połączenia i nie powoduje już
      // cyklicznego renderowania całej skrzynki podczas normalnej pracy.
      if (getSocket()?.connected) return;
      refreshInboxInBackground();
    }, 45_000);
    return () => clearInterval(interval);
  }, [refreshInboxInBackground]);

  // Manualne odświeżenie: endpoint sync-all zwraca 202 natychmiast (sync leci
  // w tle), więc my tylko zapinamy polling krótkoterminowy — co 3 s, 10 s, 20 s
  // invaliduje messages/stats. Nowe maile pojawiają się przez IDLE socket events
  // albo przez te trzy invalidate punkty (fallback gdyby IDLE padł).
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const storesResponse = await messagesApi.getMailboxStores();
      const refreshedStores: StoreItem[] = storesResponse.data.data || [];
      setStores(refreshedStores);
      setStoresLoadFailed(false);
      setSelectedStoreId((currentStoreId) => {
        if (currentStoreId && refreshedStores.some(store => store.id === currentStoreId)) {
          return currentStoreId;
        }
        return refreshedStores.find(store => store.mailboxCount > 0)?.id ?? refreshedStores[0]?.id ?? '';
      });
    } catch {
      setStoresLoadFailed(true);
    }
    try {
      inboxEmailApi.syncAll().catch(() => undefined);
    } catch {
      // ignore — tak czy siak invalidujemy niżej
    }
    // Fallback polling: 3 odświeżenia w ciągu 20 s — łapią maile gdy IDLE nie
    // puszcza eventu "message:new" lub gdy sync trwa dłużej.
    const schedule = [3_000, 10_000, 20_000];
    schedule.forEach((ms) => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['messages'] });
        queryClient.invalidateQueries({ queryKey: ['imap-folders-tree'] });
        loadStatsRef.current?.();
        loadReviewQueueRef.current?.();
      }, ms);
    });
    // Wizualny wskaźnik "odświeżam" trzymamy tylko 2 s — user nie czeka aż sync
    // faktycznie skończy (bo to może być 30+ s); UI samo się zaktualizuje.
    setTimeout(() => setIsRefreshing(false), 2_000);
  }, [queryClient]);

  const loadTemplates = useCallback(async () => {
    if (!storeIdParam) return;
    const isCurrent = mailboxRequests.begin('templates', storeIdParam);
    try {
      const response = await messagesApi.getTemplates({ storeId: storeIdParam });
      if (isCurrent()) setTemplates(response.data.data || []);
    } catch {
      // ignore
    }
  }, [storeIdParam, mailboxRequests]);

  const loadLabels = useCallback(async () => {
    try {
      const response = await messagesApi.getLabels();
      setLabels(response.data.data || []);
    } catch {
      // ignore
    }
  }, []);

  const loadReviewQueue = useCallback(async () => {
    if (!storeIdParam) return;
    const isCurrent = mailboxRequests.begin('review-queue', storeIdParam);
    if (!isCurrent()) return;
    try {
      setReviewQueueLoading(true);
      const [queueResponse, reportResponse] = await Promise.all([
        messagesApi.getAutoReplyQueue({ storeId: storeIdParam, page: 1, limit: 50 }),
        messagesApi.getInboxAiDailyReport({ storeId: storeIdParam }),
      ]);
      if (!isCurrent()) return;
      setReviewQueue(queueResponse.data.data || []);
      setAiDailyReport(reportResponse.data.data || null);
    } catch {
      // ignore
    } finally {
      if (isCurrent()) setReviewQueueLoading(false);
    }
  }, [storeIdParam, mailboxRequests]);
  // Sync ref po definicji — polling interval i event listeners używają ref zamiast
  // bezpośrednio funkcji żeby uniknąć TDZ (deklarowane wyżej w pliku).
  useEffect(() => {
    loadReviewQueueRef.current = loadReviewQueue;
  }, [loadReviewQueue]);

  // Load review queue when switching to that folder
  useEffect(() => {
    if (folderFilter === 'review_queue') {
      loadReviewQueue();
    }
  }, [folderFilter, loadReviewQueue]);

  // Auto-load customer data przy rozwijaniu drafta — panel info jest zawsze widoczny,
  // więc dane klienta muszą się załadować od razu po expand.
  useEffect(() => {
    if (!expandedDraftId) {
      setDraftCustomerData(null);
      return;
    }
    const draft = reviewQueue.find(q => q.id === expandedDraftId);
    if (!draft) return;
    setDraftCustomerData(null);
    setDraftCustomerLoading(true);
    let cancelled = false;
    const isCurrent = mailboxRequests.begin('draft-customer', storeIdParam || '');
    messagesApi.getCustomerHistory({ storeId: storeIdParam, email: extractEmail(draft.message.replyTo) || extractEmail(draft.message.from) || draft.message.from })
      .then(res => { if (!cancelled && isCurrent()) setDraftCustomerData(res.data); })
      .catch(() => { if (!cancelled && isCurrent()) setDraftCustomerData(null); })
      .finally(() => { if (!cancelled && isCurrent()) setDraftCustomerLoading(false); });
    return () => { cancelled = true; };
  }, [expandedDraftId, reviewQueue, storeIdParam, mailboxRequests]);

  // Cache ciała wiadomości dla draftów — fetch pełnej treści (nie snippet)
  // kiedy draft się rozwija. Cache per messageId.
  const [draftBodies, setDraftBodies] = useState<Record<string, { loading: boolean; text: string | null; error: string | null }>>({});
  useEffect(() => {
    if (!expandedDraftId) return;
    const draft = reviewQueue.find(q => q.id === expandedDraftId);
    if (!draft) return;
    const msgId = draft.message.id;
    if (draftBodies[msgId]) return; // już mamy w cache
    setDraftBodies(prev => ({ ...prev, [msgId]: { loading: true, text: null, error: null } }));
    messagesApi.getMessageBody(msgId)
      .then(res => {
        const text = (res.data?.bodyText || '').replace(/\s+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
        setDraftBodies(prev => ({ ...prev, [msgId]: { loading: false, text, error: null } }));
      })
      .catch(() => {
        setDraftBodies(prev => ({ ...prev, [msgId]: { loading: false, text: null, error: 'fetch_failed' } }));
      });
  }, [expandedDraftId, reviewQueue, draftBodies]);

  const approveReviewItem = async (id: string) => {
    if (reviewUploading[id] || reviewActionLoading) return;
    try {
      setReviewActionLoading(id);
      const resp = await messagesApi.approveAutoReply(id, reviewAttachments[id]);
      setReviewAttachments(previous => { const next = { ...previous }; delete next[id]; return next; });
      setReviewQueue(q => q.filter(item => item.id !== id));
      loadStats();
      // Invalidate listy i drzewo folderów — backend zmienia conversationStatus
      // + fizycznie przenosi mail (applyStatusFolderMapping). Bez invalidate user
      // widziałby mail ciągle w Odebrane do F5.
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['imap-folders-tree'] });
      // Backend zwraca mappingApplied=false z reason gdy mail zostaje w Inboxie
      // mimo approve (brak mappingu per imapConfigId, nieistniejący folder,
      // błąd komunikacji z IMAP). Pokaż user'owi konkretny powód żeby mógł
      // poprawić Ustawienia → Wiadomości → Statusy konwersacji.
      const data = resp.data as { mappingApplied?: boolean; mappingReason?: string; mappingDetail?: string; mappingError?: string };
      if (data?.mappingApplied === false) {
        const reasonLabels: Record<string, string> = {
          'no-mapping': 'brak mappingu statusu na folder',
          'no-imap-config': 'wiadomość bez konta IMAP',
          'target-folder-missing': 'folder docelowy nie znaleziony na serwerze',
          'same-folder': 'mail już w docelowym folderze',
          'message-not-found': 'wiadomości nie znaleziono',
        };
        const label = reasonLabels[data.mappingReason || ''] || data.mappingReason || 'nieznany powód';
        showToast(`Wysłano, ale bez przeniesienia do folderu (${label})${data.mappingDetail ? ': ' + data.mappingDetail : ''}`, 'error', { durationMs: 8000 });
      } else if (data?.mappingError) {
        showToast(`Wysłano, ale mapping folderu padł: ${data.mappingError}`, 'error', { durationMs: 8000 });
      } else {
        showToast('Odpowiedź zatwierdzona i wysłana');
      }
    } catch (error) {
      const message = (error as { response?: { data?: { error?: string } } }).response?.data?.error;
      showToast(message || 'Błąd zatwierdzania odpowiedzi', 'error');
    } finally {
      setReviewActionLoading(null);
    }
  };

  // Szybka edycja: otwiera modal z textarea ładowanym z aktualnej treści draftu (plain text).
  const openQuickEdit = (item: ReviewQueueItem) => {
    // Preferuj draftText (już odtagowany); fallback na stripped draftHtml.
    const initial = item.draftText ?? item.draftHtml.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    setQuickEditDraftId(item.id);
    setQuickEditValue(initial);
  };

  const saveQuickEdit = async () => {
    if (!quickEditDraftId) return;
    setQuickEditSaving(true);
    try {
      // Konwersja plain text → HTML: paragrafy po \n\n, <br> po pojedynczym \n.
      const html = quickEditValue
        .split(/\n{2,}/)
        .map(para => `<p>${para.replace(/\n/g, '<br>').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/&lt;br&gt;/g, '<br>')}</p>`)
        .join('');
      await messagesApi.updateAutoReplyDraft(quickEditDraftId, {
        draftHtml: html,
        draftText: quickEditValue,
      });
      // Update lokalnego reviewQueue — user widzi zmianę natychmiast bez refetch.
      setReviewQueue(q => q.map(item =>
        item.id === quickEditDraftId
          ? { ...item, draftHtml: html, draftText: quickEditValue }
          : item
      ));
      setQuickEditDraftId(null);
      showToast('Draft zaktualizowany');
    } catch {
      showToast('Błąd zapisu draftu', 'error');
    } finally {
      setQuickEditSaving(false);
    }
  };

  const rejectReviewItem = async (id: string) => {
    try {
      setReviewActionLoading(id);
      await messagesApi.rejectAutoReply(id);
      setReviewQueue(q => q.filter(item => item.id !== id));
      loadStats();
      showToast('Odpowiedź odrzucona');
    } catch {
      showToast('Błąd odrzucania odpowiedzi', 'error');
    } finally {
      setReviewActionLoading(null);
    }
  };

  const rejectAutoReplyIfStillExists = async (id: string) => {
    try {
      await messagesApi.rejectAutoReply(id);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 404) throw err;
    }
  };

  // #17 + #R2-1 - quick actions w nagłówku draftu AI:
  //   a) ikony POWIĄZANYCH FOLDERÓW (resolved/pending/important) - tylko gdy
  //      user ma mapping skonfigurowany w ustawieniach Wiadomości DLA TEJ
  //      konkretnej skrzynki (configId draftu). Klik = zmiana statusu konwersacji
  //      + automatyczny move do zmapowanego folderu (backend: applyStatusFolderMapping).
  //   b) ikona Trash - przeniesienie do kosza, zawsze widoczna.
  // Bez Archive (poprzednia wersja) - to nie była opcja "powiązanego folderu",
  // tylko hardcoded archive. User wolał jawne mapowania ze statusów.
  const quickChangeStatusDraft = async (item: ReviewQueueItem, status: 'resolved' | 'pending' | 'important', label: string) => {
    try {
      setReviewActionLoading(item.id);
      await messagesApi.changeStatus(item.message.id, { status });
      await rejectAutoReplyIfStillExists(item.id);
      setReviewQueue(q => q.filter(x => x.id !== item.id));
      loadStats();
      invalidateMessages();
      showToast(`${label}, draft odrzucony`, 'success');
    } catch {
      showToast(`Nie udało się zmienić statusu na "${label}"`, 'error');
    } finally {
      setReviewActionLoading(null);
    }
  };

  const quickTrashDraft = async (item: ReviewQueueItem) => {
    try {
      setReviewActionLoading(item.id);
      await messagesApi.bulkAction({ storeId: selectedStoreId, ids: [item.message.id], action: 'trash' });
      await rejectAutoReplyIfStillExists(item.id);
      setReviewQueue(q => q.filter(x => x.id !== item.id));
      loadStats();
      invalidateMessages();
      showToast('Wiadomość przeniesiona do kosza, draft odrzucony', 'success');
    } catch {
      showToast('Nie udało się przenieść do kosza', 'error');
    } finally {
      setReviewActionLoading(null);
    }
  };

  // #R2-1 - sprawdza czy dla danej skrzynki (imapConfigId) status ma zmapowany
  // folder - tylko wtedy pokazujemy ikonkę quick action. Brak mappingu = ikonka
  // ukryta (zgodnie z user expectation: "jeśli tych folderów nie mamy
  // zmapowanych i powiązanych to nie wyświetlamy tam żadnej ikonki").
  const hasStatusMappingFor = (configId: string | null, status: 'resolved' | 'pending' | 'important'): boolean => {
    if (!configId) return false;
    return Boolean(statusMapping[status]?.[configId]);
  };

  // Bulk reject drafts
  const bulkApproveDrafts = async () => {
    if (selectedDraftIds.size === 0 || reviewActionLoading) return;
    const ids = Array.from(selectedDraftIds);
    if (ids.some(id => reviewUploading[id])) {
      showToast('Poczekaj na zakończenie wgrywania załączników.', 'error');
      return;
    }
    const approved: string[] = [];
    try {
      setReviewActionLoading('bulk');
      for (const id of ids) {
        await messagesApi.approveAutoReply(id, reviewAttachments[id]);
        approved.push(id);
        setReviewAttachments(previous => { const next = { ...previous }; delete next[id]; return next; });
      }
      setReviewQueue(queue => queue.filter(item => !approved.includes(item.id)));
      setSelectedDraftIds(new Set());
      setDraftSelectionMode(false);
      loadStats();
      invalidateMessages();
      queryClient.invalidateQueries({ queryKey: ['imap-folders-tree'] });
      showToast(`Zatwierdzono i wysłano ${approved.length} ${approved.length === 1 ? 'odpowiedź' : 'odpowiedzi'}`);
    } catch {
      setReviewQueue(queue => queue.filter(item => !approved.includes(item.id)));
      setSelectedDraftIds(new Set(ids.filter(id => !approved.includes(id))));
      loadStats();
      invalidateMessages();
      showToast(`Wysłano ${approved.length} z ${ids.length}. Dalsze wysyłanie zatrzymano po błędzie.`, 'error');
    } finally {
      setReviewActionLoading(null);
    }
  };

  const bulkRejectDrafts = async () => {
    if (selectedDraftIds.size === 0) return;
    const ids = Array.from(selectedDraftIds);
    try {
      setReviewActionLoading('bulk');
      for (const id of ids) {
        await messagesApi.rejectAutoReply(id);
      }
      setReviewQueue(q => q.filter(item => !selectedDraftIds.has(item.id)));
      setSelectedDraftIds(new Set());
      setDraftSelectionMode(false);
      loadStats();
      showToast(`Odrzucono ${ids.length} draftów`);
    } catch {
      showToast('Błąd masowego odrzucania', 'error');
    } finally {
      setReviewActionLoading(null);
    }
  };

  const toggleDraftSelect = (id: string) => {
    setSelectedDraftIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllDrafts = () => {
    if (selectedDraftIds.size === reviewQueue.length) {
      setSelectedDraftIds(new Set());
    } else {
      setSelectedDraftIds(new Set(reviewQueue.map(item => item.id)));
    }
  };

  useEffect(() => {
    // Messages loaded by useInfiniteQuery — no need to call loadMessages.
    loadStats();
    loadTemplates();
    loadLabels();
    // Załaduj kolejkę draftów AI — używana nie tylko w zakładce "Do przeglądu",
    // ale też jako źródło dla bannera pod otwartą wiadomością w Inboxie.
    // Bez tego banner był niewidoczny dopóki user nie wszedł do folderu draftów.
    loadReviewQueue();
    // Load AI providers to auto-detect available provider
    aiApi.getProviders().then(res => {
      const providers = res.data?.data || [];
      if (providers.length > 0) setAiProvider(providers[0].name);
    }).catch(() => {});
    // Check if any email account is configured
    inboxEmailApi.getConfigs().then(res => {
      const configs = res.data?.data || [];
      setHasEmailConfig(configs.some((c: { isActive: boolean }) => c.isActive));
    }).catch(() => setHasEmailConfig(false));
    // Load user swipe preferences — one-shot. Zmiana w Ustawieniach wymaga
    // refresh strony (to jest ustawienie, nie live feature toggle).
    userApi.getPreferences().then(res => {
      const p = (res.data || {}) as Record<string, unknown>;
      setSwipePrefs({
        enabled: typeof p.inboxSwipeEnabled === 'boolean' ? p.inboxSwipeEnabled : false,
        left: (typeof p.inboxSwipeLeftAction === 'string' ? p.inboxSwipeLeftAction : 'trash') as InboxSwipeAction,
        right: (typeof p.inboxSwipeRightAction === 'string' ? p.inboxSwipeRightAction : 'resolved') as InboxSwipeAction,
      });
    }).catch(() => { /* ignore — default OFF */ });
    // Fetch folders tree (flatten) — do dropdownu "Przenieś do folderu".
    if (!storeIdParam) {
      setImapFoldersFlat([]);
      return;
    }
    const isCurrent = mailboxRequests.begin('folders', storeIdParam);
    inboxEmailApi.getFoldersTree(storeIdParam).then(res => {
      if (!isCurrent()) return;
      const tree = res.data?.data || [];
      const flat: { id: string; path: string; name: string; configId: string; specialUse: string | null; totalCount: number }[] = [];
      interface TreeNode { id: string; path: string; name: string; specialUse: string | null; totalCount?: number; children: TreeNode[] }
      interface TreeCfg { id: string; folders: TreeNode[] }
      for (const cfg of tree as TreeCfg[]) {
        const walk = (nodes: TreeNode[]) => {
          for (const n of nodes) {
            flat.push({ id: n.id, path: n.path, name: n.name, configId: cfg.id, specialUse: n.specialUse, totalCount: n.totalCount ?? 0 });
            if (n.children?.length) walk(n.children);
          }
        };
        walk(cfg.folders);
      }
      setImapFoldersFlat(flat);
    }).catch(() => { if (isCurrent()) setImapFoldersFlat([]); });
  }, [loadStats, loadTemplates, loadLabels, storeIdParam, mailboxRequests]);

  // Ref dla handlera order:updated — socket listener wpięty raz, ale musi
  // widzieć aktualny customerData/selectedMessage bez re-attachingu.
  const customerCtxRef = useRef<{
    customerData: CustomerData | null;
    selectedMessage: typeof selectedMessage;
    storeIdParam: string | undefined;
  }>({ customerData: null, selectedMessage: null, storeIdParam: undefined });
  useEffect(() => {
    customerCtxRef.current = { customerData, selectedMessage, storeIdParam };
  });

  // Preload statusow tree przy mount — uzywane przez plakietki statusu w
  // panelu klienta (kolor identyczny jak w module Statusy/Logistyka).
  useEffect(() => {
    if (orderStatusesCache) return;
    api.get<StatusGroup[]>('/statuses/tree')
      .then(res => setOrderStatusesCache(res.data || []))
      .catch(() => undefined);
  }, [orderStatusesCache]);

  // Fetch order gdy user otworzy modal zamówienia z panelu klienta.
  useEffect(() => {
    if (!orderModalId) {
      setOrderModalData(null);
      setOrderModalLoading(false);
      return;
    }
    let cancelled = false;
    setOrderModalLoading(true);
    orderApi.getOne(orderModalId)
      .then(orderRes => {
        if (cancelled) return;
        setOrderModalData(orderRes.data as Order);
      })
      .catch(() => {
        if (!cancelled) showToast('Nie udało się załadować zamówienia', 'error');
      })
      .finally(() => {
        if (!cancelled) setOrderModalLoading(false);
      });
    return () => { cancelled = true; };
  }, [orderModalId]);

  // WebSocket: real-time message updates
  useEffect(() => {
    if (!selectedStoreId) return;
    const cleanup = onSocketReady((s) => {
      subscribeToMessages(selectedStoreId);
      s.emit('subscribe:orders');

      // Cold-start race guard: gdy socket connectuje po pierwszym fetch (np.
      // incognito po restarcie — React Query bez cache, worker w trakcie
      // catch-up sync), message:new eventy wystrzelone w tym oknie przepadają.
      // Force refetch na każdym (re)connect wyrównuje stan z serwerem.
      refreshInboxInBackground();

      const handleNewMessage = async (payload: unknown) => {
        console.log('[Inbox] message:new received', payload);
        // Dociągamy świeży stan z serwera, ale podczas pisania lub pracy w
        // modalu odkładamy refresh do końca interakcji, żeby nie migał ekran.
        refreshInboxInBackground();
        // Oznacz id jako "świeżo przyszły" — itemContent Virtuoso doda
        // data-new-item="true" → CSS keyframes subtleFadeIn odpali się w
        // przeglądarce. Po 2s id usuwamy żeby kolejne re-rendery nie
        // odpaliły animacji ponownie.
        const payloadId = typeof payload === 'object' && payload !== null && 'id' in payload
          ? (payload as { id?: unknown }).id : null;
        if (typeof payloadId === 'string' && payloadId) {
          setNewMessageIds(prev => {
            const next = new Set(prev);
            next.add(payloadId);
            return next;
          });
          setTimeout(() => {
            setNewMessageIds(prev => {
              if (!prev.has(payloadId)) return prev;
              const next = new Set(prev);
              next.delete(payloadId);
              return next;
            });
          }, 2000);
        }
      };

      const handleMessageUpdate = (payload: { messageId?: string; bodyReady?: boolean } | undefined) => {
        console.log('[Inbox] message:updated received', payload);
        if (payload && payload.bodyReady && payload.messageId) {
          queryClient.invalidateQueries({ queryKey: ['message-body', payload.messageId] });
        }
        refreshInboxInBackground(false);
      };

      // Wiadomości usunięte w webmailu zostały wykryte przez prune → odśwież listę
      const handlePruned = (payload: { count: number }) => {
        console.log(`[Inbox] messages:pruned count=${payload.count} — invalidate list`);
        refreshInboxInBackground();
      };

      // Order status zmienił się w innym module (Logistyka / OrdersPage) — panel
      // klienta w Wiadomościach trzyma listę zamówień w useState (nie useQuery),
      // więc bez tego eventu pokazuje stary status do F5.
      const handleOrderUpdated = (payload: { orderId?: string } | undefined) => {
        if (!payload?.orderId) return;
        const ctx = customerCtxRef.current;
        if (!ctx.customerData || !ctx.selectedMessage) return;
        const hasOrder = ctx.customerData.orders.some(o => o.id === payload.orderId);
        if (!hasOrder) return;
        const email = resolveMessageCustomerEmail(ctx.selectedMessage);
        if (!email) return;
        const isCurrent = mailboxRequests.begin('customer-order-update', ctx.storeIdParam || '');
        messagesApi.getCustomerHistory({ email, storeId: ctx.storeIdParam })
          .then(res => { if (isCurrent() && customerCtxRef.current.selectedMessage?.id === ctx.selectedMessage?.id) setCustomerData(res.data); })
          .catch(() => undefined);
      };

      console.log('[Inbox] Attaching socket handlers (socket.id=', s.id, ')');
      s.on('message:new', handleNewMessage);
      s.on('message:updated', handleMessageUpdate);
      s.on('messages:pruned', handlePruned);
      s.on('order:updated', handleOrderUpdated);

      return () => {
        s.off('message:new', handleNewMessage);
        s.off('message:updated', handleMessageUpdate);
        s.off('messages:pruned', handlePruned);
        s.off('order:updated', handleOrderUpdated);
        s.emit('unsubscribe:orders');
      };
    });

    return () => {
      cleanup?.();
      unsubscribeFromMessages(selectedStoreId);
    };
  }, [queryClient, refreshInboxInBackground, selectedStoreId]);

  // Load AI template personalization setting on mount
  useEffect(() => {
    aiApi.getSettings().then(res => {
      const settings = res.data?.data || res.data || {};
      if (settings.aiTemplatePersonalization) {
        setAiTemplatePersonalizationEnabled(true);
      }
    }).catch(() => {});
  }, []);

  // Load customer data & notes when selected message changes
  useEffect(() => {
    if (!selectedMessage) {
      setCustomerData(null);
      setNotes([]);
      return;
    }

    let cancelled = false;
    setCustomerData(null);
    setNotes([]);
    // Odebrane: Reply-To -> From. Szkice: To. Dzięki temu szkic utworzony
    // przez skrzynkę sklepu nie pokazuje zamówień przypisanych do adresu sklepu.
    const customerEmail = resolveMessageCustomerEmail(selectedMessage);
    const isSentMessage = selectedMessage.imapFolder?.specialUse === '\\Sent'
      && !selectedMessage.isDraft;

    if (!isSentMessage && customerEmail) {
      setCustomerLoading(true);
      messagesApi.getCustomerHistory({
        email: customerEmail,
        storeId: selectedMessage.store?.id,
      }).then(res => {
        if (!cancelled) setCustomerData(res.data);
      }).catch(() => {
        if (!cancelled) setCustomerData(null);
      }).finally(() => { if (!cancelled) setCustomerLoading(false); });
    } else {
      setCustomerData(null);
    }

    // Load notes — w inboxie pokazujemy notatki przypięte do konkretnego wątku.
    customerNotesApi.list({ conversationId: selectedMessage.id }).then(res => {
      if (!cancelled) setNotes(res.data.data || []);
    }).catch(() => { if (!cancelled) setNotes([]); });
    return () => { cancelled = true; };
  }, [selectedMessage?.id]);

  // Auto mark-as-read po 400ms — user zgłosił że 3s było za długie
  // ("otwieram mail, a kropka nieprzeczytana wisi jeszcze kilka sekund").
  // 400ms daje jeszcze chwilę na cofnięcie się z maila zanim go zamarknę,
  // ale w normalnym flow user już widzi mail jako przeczytany od razu po
  // otwarciu. Clear przy zmianie maila / zamknięciu. Tylko jeśli nieprzeczytany
  // i mail z Inboxa (nie draft/sent).
  useEffect(() => {
    if (!selectedMessage || selectedMessage.isRead) return;
    const specialUse = selectedMessage.imapFolder?.specialUse;
    if (specialUse === '\\Drafts' || specialUse === '\\Sent') return;
    const timer = setTimeout(() => {
      mutations.toggleRead.mutate({ id: selectedMessage.id, isRead: true });
    }, 400);
    return () => clearTimeout(timer);
  }, [selectedMessage?.id, selectedMessage?.isRead]);

  const addNote = async () => {
    if (!selectedMessage || !noteInput.trim()) return;
    setNoteSubmitting(true);
    try {
      const res = await customerNotesApi.create({
        content: noteInput.trim(),
        conversationId: selectedMessage.id,
      });
      setNotes(prev => [res.data.data, ...prev]);
      setNoteInput('');
    } catch {
      showToast('Błąd dodawania notatki', 'error');
    } finally {
      setNoteSubmitting(false);
    }
  };

  const deleteNote = async (noteId: string) => {
    if (!selectedMessage) return;
    try {
      await customerNotesApi.remove(noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch {
      showToast('Błąd usuwania notatki', 'error');
    }
  };

  const createCoupon = async () => {
    if (!couponForm.code || !couponForm.amount) return;
    const amount = Number(couponForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      showToast('Wartość rabatu musi być większa od zera', 'error');
      return;
    }
    if (couponForm.discountType === 'percent' && amount > 100) {
      showToast('Rabat procentowy nie może przekraczać 100%', 'error');
      return;
    }
    const storeId = selectedStoreId || selectedMessage?.store?.id;
    if (!storeId) {
      showToast('Wybierz sklep aby utworzyć kupon', 'error');
      return;
    }
    setCouponLoading(true);
    try {
      await messagesApi.createCoupon({
        storeId,
        code: couponForm.code,
        discountType: couponForm.discountType,
        amount: couponForm.amount,
        singleUse: couponForm.singleUse,
      });
      // Insert coupon code into compose editor
      const discountLabel = couponForm.discountType === 'percent' ? `${couponForm.amount}%` : `${couponForm.amount} PLN`;
      const couponText = `<p><strong>Kupon rabatowy: ${couponForm.code}</strong> (${discountLabel}${couponForm.singleUse ? ', jednorazowy' : ''})</p>`;
      setComposeData(prev => ({
        ...prev,
        bodyHtml: prev.bodyHtml + couponText,
      }));
      setShowCouponModal(false);
      setCouponForm({ code: '', discountType: 'percent', amount: '', singleUse: true });
      showToast('Kupon utworzony i wstawiony');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Błąd tworzenia kuponu', 'error');
    } finally {
      setCouponLoading(false);
    }
  };

  const openMessage = async (msg: InboxMessage) => {
    setSelectedMessage(msg);
    setShowSenderCard(false);
    const isCurrent = mailboxRequests.begin('message-detail', selectedStoreId);
    try {
      const response = await messagesApi.getMessage(msg.id);
      if (!isCurrent()) return;
      setSelectedMessage(response.data.data);
      invalidateMessages();
      loadStats();
    } catch {
      if (isCurrent()) showToast('Błąd otwarcia wiadomości', 'error');
    }
  };

  const changeConversationStatus = (id: string, status: string) => {
    const labels: Record<string, string> = { open: 'Odebrane', pending: 'Oczekujące', important: 'Ważne', resolved: 'Rozwiązane' };
    const selectedBeforeStatusChange = selectedMessage;
    const isSelected = selectedBeforeStatusChange?.id === id;
    const sourcePath = selectedBeforeStatusChange?.imapFolder?.path || 'INBOX';
    const resolvedTargetPath = selectedBeforeStatusChange?.imapConfigId
      ? statusMapping.resolved[selectedBeforeStatusChange.imapConfigId]
      : null;
    const starredTargetPath = selectedBeforeStatusChange?.imapConfigId
      ? statusMapping.starred[selectedBeforeStatusChange.imapConfigId]
      : null;
    const shouldRemoveSelectedFromCurrentView =
      isSelected && (
        (status === 'resolved' && (
          (!selectedImapFolderId && folderFilter === 'inbox' && statusFilter !== 'resolved')
          || folderFilter === 'starred'
          || Boolean(selectedImapFolderId && resolvedTargetPath && resolvedTargetPath !== sourcePath)
          || Boolean(
            selectedImapFolderId
            && !resolvedTargetPath
            && selectedBeforeStatusChange?.isStarred
            && starredTargetPath === sourcePath,
          )
        ))
        || (status !== 'resolved' && (
          folderFilter === 'resolved'
          || Boolean(selectedImapFolderId && sourcePath.toLowerCase() !== 'inbox')
        ))
      );

    if (shouldRemoveSelectedFromCurrentView) {
      setNextOrEmpty(id, { removedConversationStatus: status });
    } else if (isSelected) {
      setSelectedMessage(current => current?.id === id
        ? {
            ...current,
            conversationStatus: status,
            resolvedAt: status === 'resolved' ? new Date().toISOString() : null,
            ...(status === 'resolved' ? { isStarred: false } : {}),
          }
        : current);
    }

    mutations.changeStatus.mutate(
      { id, status },
      {
        onSuccess: () => {
          showToast(`Status: ${labels[status] || status}`);
        },
        onError: () => {
          if (selectedBeforeStatusChange) {
            setSelectedMessage(current => {
              if (current && current.id !== id && current.id !== selectedBeforeStatusChange.id) return current;
              return selectedBeforeStatusChange;
            });
          }
        },
      },
    );
  };

  const toggleSelectedStar = () => {
    if (!selectedMessage) return;
    const previousMessage = selectedMessage;
    const nextStarred = !selectedMessage.isStarred;
    const sourcePath = selectedMessage.imapFolder?.path || 'INBOX';
    const mappedPath = selectedMessage.imapConfigId
      ? statusMapping.starred[selectedMessage.imapConfigId]
      : null;
    const mappedMoveChangesFolder = Boolean(mappedPath && (
      (nextStarred && sourcePath !== mappedPath)
      || (!nextStarred && sourcePath === mappedPath)
    ));
    const clearsResolved = nextStarred && selectedMessage.conversationStatus === 'resolved';
    const resolvedFallbackMovesToInbox = clearsResolved
      && !mappedPath
      && sourcePath.toLowerCase() !== 'inbox';
    const shouldLeaveCurrentView = (folderFilter === 'starred' && !nextStarred)
      || mappedMoveChangesFolder
      || (clearsResolved && folderFilter === 'resolved')
      || resolvedFallbackMovesToInbox;

    if (shouldLeaveCurrentView) {
      setNextOrEmpty(selectedMessage.id);
    } else {
      setSelectedMessage(current => current?.id === selectedMessage.id
        ? {
            ...current,
            isStarred: nextStarred,
            ...(clearsResolved ? { conversationStatus: 'open', resolvedAt: null } : {}),
          }
        : current);
    }

    mutations.toggleStar.mutate(
      {
        id: previousMessage.id,
        isStarred: nextStarred,
        ...(shouldLeaveCurrentView ? {
          removeFrom: { folderFilter, imapFolderId: selectedImapFolderId },
        } : {}),
      },
      {
        onSuccess: () => showToast(nextStarred ? 'Dodano do wyróżnionych' : 'Usunięto z wyróżnionych'),
        onError: () => {
          if (shouldLeaveCurrentView) {
            setSelectedMessage(previousMessage);
            return;
          }
          setSelectedMessage(current => {
            if (current && current.id !== previousMessage.id) return current;
            return previousMessage;
          });
        },
      },
    );
  };

  // Undo — wywołuje atomic restore-any endpoint (odwraca isDeleted/isArchived
  // i enqueue IMAP move do INBOX jeśli potrzeba). Idempotent, odporny na race
  // z asynchronicznym IMAP MOVE — nie polega na tym w jakim stanie IMAP-owo
  // jest mail (może być dalej w INBOX bo MOVE jeszcze nie zdążył).
  const undoDeleteOrArchive = async (id: string) => {
    try {
      await messagesApi.restoreAny(id);
      invalidateMessages();
      loadStats();
      showToast('Przywrócono');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: string } } };
      const backendMsg = axiosErr?.response?.data?.error;
      const status = axiosErr?.response?.status;
      const msg = backendMsg
        || (status === 404 ? 'Wiadomość nie istnieje — możliwe że została trwale usunięta.' : 'Nie udało się cofnąć operacji.');
      // Dłuższy timeout (8s) — user musi zdążyć przeczytać co poszło nie tak.
      showToast(msg, 'error', { durationMs: 8000 });
    }
  };

  // Przenosi wątek do wybranego folderu IMAP. Hook usuwa go z bieżącej listy
  // optymistycznie, a backend czyści przy tym status Rozwiązane i gwiazdkę.
  // #4: po move/archive/trash otwartej wiadomości skacz na NASTĘPNĄ w aktualnym
  // folderze (ew. poprzednią gdy jesteśmy na końcu). Gdy lista pusta = null
  // (empty state). Capture'ujemy next PRZED mutacją, bo mutations są optimistic
  // i messages może już nie zawierać `id` jak ją wywołamy później.
  function clearSelectedMessageAfterRemoval() {
    mailboxRequests.begin('message-detail', selectedStoreId);
    setSelectedMessage(null);
    setShowInfoPanel(false);
    setComposeMode('none');
    setShowAiDraft(false);
    setAiDraft('');
    setAiError('');
  }

  function setNextOrEmpty(id: string, options?: { removedConversationStatus?: string }) {
    const candidates = options?.removedConversationStatus
      ? messages.filter(message => {
          if (message.id === id) return false;
          if (statusFilter !== 'all') {
            return message.conversationStatus === statusFilter;
          }
          return message.conversationStatus !== options.removedConversationStatus;
        })
      : messages.filter(message => message.id !== id);

    if (candidates.length === 0) {
      clearSelectedMessageAfterRemoval();
      return;
    }

    const idx = messages.findIndex(m => m.id === id);
    if (idx === -1) {
      clearSelectedMessageAfterRemoval();
      return;
    }
    const next = candidates.find(message => messages.findIndex(item => item.id === message.id) > idx)
      ?? [...candidates].reverse().find(message => messages.findIndex(item => item.id === message.id) < idx)
      ?? null;
    if (next) void openMessage(next);
    else clearSelectedMessageAfterRemoval();
  }

  const moveToFolder = (folderId: string) => {
    if (!selectedMessage) return;
    const previousMessage = selectedMessage;
    const id = previousMessage.id;
    setShowMoveMenu(false);
    setNextOrEmpty(id);
    mutations.moveToFolder.mutate(
      { id, imapFolderId: folderId },
      {
        onSuccess: () => showToast('Wątek przeniesiony'),
        onError: () => setSelectedMessage(current => current?.id === id ? current : previousMessage),
      },
    );
  };

  const archiveMessage = (id: string) => {
    if (selectedMessage?.id === id) setNextOrEmpty(id);
    markPendingAndRun(id, () => {
      mutations.archiveMessage.mutate(id, {
        onSuccess: () => showToast('Wiadomość zarchiwizowana', 'success', {
          durationMs: 6000,
          action: { label: 'Cofnij', onClick: () => undoDeleteOrArchive(id) },
        }),
      });
    });
  };

  // Trash helper - soft delete z Inbox albo expunge z Kosza (backend
  // DELETE /messages/:id sam rozpoznaje po folderze).
  const doTrashOrExpunge = (id: string, isExpunge: boolean) => {
    // Szkic: nie trafia do kosza — backend kasuje go od razu (draftów się
    // nie archiwizuje). Komunikat musi to odzwierciedlać, inaczej user
    // szuka skasowanego szkicu w Koszu.
    const msg = selectedMessage?.id === id
      ? selectedMessage
      : (selectedMessage?.replies?.find(r => r.id === id) ?? messages.find(m => m.id === id));
    const isDraftMsg = msg?.isDraft === true;
    if (selectedMessage?.id === id) {
      // Kasujemy root — przeskocz na następną wiadomość z folderu (#4),
      // a gdy folder pusty = null (empty state).
      setNextOrEmpty(id);
    } else if (selectedMessage?.replies?.some(r => r.id === id)) {
      // Kasujemy reply (np. szkic w wątku) — wywal go lokalnie z replies,
      // żeby UI nie pokazywał już nieistniejącej wiadomości. Bez tego user
      // widzi szkic dalej mimo komunikatu "usunięto" i musiał robić F5.
      setSelectedMessage(prev => prev ? {
        ...prev,
        replies: (prev.replies || []).filter(r => r.id !== id),
      } : null);
    }
    markPendingAndRun(id, () => {
      mutations.trashMessage.mutate(id, {
        onSuccess: () => {
          if (isDraftMsg) {
            showToast('Szkic został usunięty');
          } else if (isExpunge) {
            showToast('Wiadomość została usunięta na stałe');
          } else {
            showToast('Wiadomość przeniesiona do kosza', 'success', {
              durationMs: 6000,
              action: { label: 'Cofnij', onClick: () => undoDeleteOrArchive(id) },
            });
          }
        },
      });
    });
  };

  const deleteMessage = (id: string) => {
    // Jeśli mail jest w Trash (fizycznie albo soft-deleted w widoku Kosza)
    // → klik Usuń = TRWAŁE usunięcie (expunge). Pokaż potwierdzenie bo
    // nie ma Cofnij po expunge.
    const msg = selectedMessage?.id === id ? selectedMessage : messages.find(m => m.id === id);
    const isInTrash =
      (!debouncedSearchQuery.trim() && folderFilter === 'trash') ||
      msg?.imapFolder?.specialUse === '\\Trash' ||
      msg?.isDeleted === true;
    if (isInTrash) {
      setExpungeConfirm({ ids: [id], mode: 'single' });
      return;
    }
    doTrashOrExpunge(id, false);
  };

  // Mapowanie akcji swipe na istniejące handlery wiersza listy. Zwraca funkcję
  // bezargumentową wykonującą akcję albo null gdy akcja nie obsługiwana dla
  // tego maila (np. 'resolved' bez mappingu, 'drafts' na liście Odebranych).
  const runSwipeAction = (msg: InboxMessage, action: InboxSwipeAction): void => {
    if (!action || action === 'none') return;
    switch (action) {
      case 'resolved':
      case 'pending':
      case 'important':
        changeConversationStatus(msg.id, action);
        return;
      case 'archive':
        archiveMessage(msg.id);
        return;
      case 'trash':
        deleteMessage(msg.id);
        return;
      case 'drafts':
        // Szkice to osobny folder, nie akcja per-wiadomość. Bezpiecznie:
        // no-op (user teoretycznie nie powinien móc wybrać tego w settings
        // dla istniejących maili — ale defensywnie).
        return;
    }
  };

  const generateAiReply = async () => {
    if (!selectedMessage) return;
    if (!aiProvider) {
      setAiError('Brak skonfigurowanego dostawcy AI. Skonfiguruj w panelu Super Admin → AI.');
      return;
    }
    const revision = composerRevisionRef.current;
    setAiLoading(true);
    setAiError('');
    try {
      const response = await aiApi.generateReply({
        messageId: selectedMessage.id,
        provider: aiProvider,
      });
      if (revision !== composerRevisionRef.current) return;
      setAiDraft(response.data.data.draft);
      setShowAiDraft(true);
      window.dispatchEvent(new Event('token-balance-changed'));
    } catch (err: any) {
      if (revision !== composerRevisionRef.current) return;
      setAiError(err.response?.data?.error || 'Błąd generowania odpowiedzi AI');
      window.dispatchEvent(new Event('token-balance-changed'));
    } finally {
      if (revision === composerRevisionRef.current) setAiLoading(false);
    }
  };

  // --- Suggestion popup logic ---
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4' });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        setTranscribing(true);
        try {
          const res = await aiApi.transcribe(blob);
          if (res.data.success === false || !res.data.data.text) {
            showToast(res.data.error || 'Nie wykryto mowy w nagraniu', 'error');
          } else {
            setSuggestionText(prev => (prev ? prev + ' ' : '') + res.data.data.text);
          }
        } catch (err: unknown) {
          const axiosErr = err as { response?: { data?: { error?: string } } };
          showToast(axiosErr.response?.data?.error || 'Błąd transkrypcji audio', 'error');
        } finally {
          setTranscribing(false);
        }
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingTime(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
      // Hard limit — analogicznie do compose recording (tokeny Whisper bez capa
      // gdy user zostawi mikrofon). Auto-stop odpala transkrypcję z tego
      // co już nagrane.
      recordingLimitRef.current = setTimeout(() => {
        showToast(`Osiągnięto limit nagrywania ${VOICE_RECORDING_LIMIT_SECONDS}s — transkrybuję`, 'success');
        stopRecording();
      }, VOICE_RECORDING_LIMIT_SECONDS * 1000);
    } catch {
      showToast('Brak dostępu do mikrofonu', 'error');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    if (recordingLimitRef.current) {
      clearTimeout(recordingLimitRef.current);
      recordingLimitRef.current = null;
    }
  };

  // --- Compose voice recording (inserts text into editor) ---
  const startComposeRecording = async () => {
    const revision = composerRevisionRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (revision !== composerRevisionRef.current) { stream.getTracks().forEach(track => track.stop()); return; }
      const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4' });
      composeChunksRef.current = [];
      composeCancelledRef.current = false; // reset flagi anulowania przed każdym nagraniem
      recorder.ondataavailable = (e) => composeChunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        // Gdy user zamknął modal w trakcie nagrywania (cancelComposeRecording
        // ustawia flagę), pomijamy transkrypcję całkowicie — nic nie leci do
        // Whisper, nic się nie wstawia do composera.
        if (composeCancelledRef.current || revision !== composerRevisionRef.current) {
          composeCancelledRef.current = false;
          return;
        }
        const blob = new Blob(composeChunksRef.current, { type: recorder.mimeType });
        setComposeTranscribing(true);
        try {
          const res = await aiApi.transcribe(blob);
          if (revision !== composerRevisionRef.current) return;
          if (res.data.success === false || !res.data.data.text) {
            showToast(res.data.error || 'Nie wykryto mowy w nagraniu', 'error');
          } else {
            const transcribedText = res.data.data.text;
            setComposeData(prev => ({
              ...prev,
              bodyHtml: prev.bodyHtml
                ? prev.bodyHtml.replace(/<\/p>\s*$/, '') + ' ' + transcribedText + '</p>'
                : `<p>${transcribedText}</p>`,
            }));
            showToast('Transkrypcja wstawiona do wiadomości');
          }
        } catch (err: unknown) {
          const axiosErr = err as { response?: { data?: { error?: string } } };
          showToast(axiosErr.response?.data?.error || 'Błąd transkrypcji audio', 'error');
        } finally {
          if (revision === composerRevisionRef.current) setComposeTranscribing(false);
        }
      };
      recorder.start();
      composeMediaRecorderRef.current = recorder;
      setIsComposeRecording(true);
      setComposeRecordingTime(0);
      composeRecordingIntervalRef.current = setInterval(() => {
        setComposeRecordingTime(t => t + 1);
      }, 1000);
      // Hard limit: jeśli user zostawił mikrofon włączony, auto-stop po
      // VOICE_RECORDING_LIMIT_SECONDS. Transkrypcja leci na tym co już nagrane,
      // user dostaje toast z info o limicie.
      composeRecordingLimitRef.current = setTimeout(() => {
        showToast(`Osiągnięto limit nagrywania ${VOICE_RECORDING_LIMIT_SECONDS}s — transkrybuję`, 'success');
        stopComposeRecording();
      }, VOICE_RECORDING_LIMIT_SECONDS * 1000);
    } catch {
      showToast('Brak dostępu do mikrofonu', 'error');
    }
  };

  const stopComposeRecording = () => {
    composeMediaRecorderRef.current?.stop();
    setIsComposeRecording(false);
    if (composeRecordingIntervalRef.current) {
      clearInterval(composeRecordingIntervalRef.current);
      composeRecordingIntervalRef.current = null;
    }
    if (composeRecordingLimitRef.current) {
      clearTimeout(composeRecordingLimitRef.current);
      composeRecordingLimitRef.current = null;
    }
  };

  // Anuluj nagrywanie BEZ transkrypcji — używane gdy user zamknie modal composera
  // w trakcie nagrywania (Esc / kliknięcie X / wyjście z wątku). Ustawia flagę
  // cancel, stopuje recorder (onstop obsłuży flagę) i czyści timery.
  const cancelComposeRecording = () => {
    if (!isComposeRecording && !composeMediaRecorderRef.current) return;
    composeCancelledRef.current = true;
    stopComposeRecording();
  };

  const handleSuggestionSubmit = async () => {
    if (!suggestionText.trim() || !suggestionContext) return;
    const revision = composerRevisionRef.current;
    setSuggestionLoading(true);
    // M2: Remember last suggestion for this context
    const contextKey = suggestionContext.reviewItemId || suggestionContext.messageId || '';
    if (contextKey) lastSuggestionsRef.current.set(contextKey, suggestionText.trim());
    try {
      if (suggestionContext.type === 'review_queue' && suggestionContext.reviewItemId) {
        const res = await messagesApi.regenerateAutoReply(suggestionContext.reviewItemId, {
          suggestion: suggestionText.trim(),
          provider: aiProvider || undefined,
        });
        setReviewQueue(q => q.map(item => item.id === suggestionContext.reviewItemId ? res.data.data : item));
        showToast('Draft AI został przeliczony z sugestią');
      } else if (suggestionContext.type === 'compose' && suggestionContext.messageId) {
        const response = await aiApi.generateReply({
          messageId: suggestionContext.messageId,
          provider: aiProvider || 'gemini',
          suggestion: suggestionText.trim(),
        });
        if (revision !== composerRevisionRef.current) return;
        setAiDraft(response.data.data.draft);
        setShowAiDraft(true);
        showToast('Odpowiedź wygenerowana z sugestią');
      }
      setShowSuggestionPopup(false);
      setSuggestionText('');
      setSuggestionContext(null);
      window.dispatchEvent(new Event('token-balance-changed'));
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Błąd generowania z sugestią', 'error');
      window.dispatchEvent(new Event('token-balance-changed'));
    } finally {
      setSuggestionLoading(false);
    }
  };

  const startReply = () => {
    if (!selectedMessage) return;
    setComposerOwnerId(selectedMessage.id);
    setComposeData(createReplyComposer(selectedMessage));
    composeModeRef.current = 'reply';
    setComposerAttachments([]);
    setShowCcBcc(false);
    setComposeMode('reply');
    setShowAiDraft(false);
  };

  const startForward = async () => {
    if (!selectedMessage) return;
    const revision = composerRevisionRef.current;
    // Zero-persistence: body nie jest w pamięci — fetchujemy lazy z IMAP przed kompozycją.
    let originalBody = '';
    try {
      const res = await messagesApi.getMessageBody(selectedMessage.id);
      originalBody = res.data.bodyHtml || (res.data.bodyText ? `<pre>${res.data.bodyText}</pre>` : '');
    } catch {
      originalBody = selectedMessage.snippet ? `<p>${selectedMessage.snippet}</p>` : '';
      showToast('Nie udało się pobrać oryginalnej treści — użyto skrótu.', 'error');
    }

    if (revision !== composerRevisionRef.current) return;
    setComposerOwnerId(selectedMessage.id);
    const quotedBody = `<br/><hr/><p>---------- Przekazana wiadomość ----------</p>
      <p>Od: ${selectedMessage.fromName || selectedMessage.from}</p>
      <p>Data: ${new Date(selectedMessage.receivedAt).toLocaleString('pl-PL')}</p>
      <p>Temat: ${selectedMessage.subject || '(brak tematu)'}</p>
      ${selectedMessage.cc ? `<p>DW: ${selectedMessage.cc}</p>` : ''}
      <br/>${originalBody}`;
    setComposeData({
      to: '',
      cc: '',
      bcc: '',
      subject: `Fwd: ${selectedMessage.subject || ''}`,
      bodyHtml: quotedBody,
      parentId: '',
    });
    setComposerAttachments([]);
    setShowCcBcc(false);
    setComposeMode('reply');
  };

  // #14 + #R2-2 - "Kontynuuj edycję szkicu". Pre-fill compose z istniejącego
  // drafta (zapisanego w folderze Szkice albo w wątku Inbox). Body lazy z DB,
  // bo zero-persistence w głównej liście. Po wysłaniu nowej wersji backend
  // skasuje stary szkic (ten sam parentId + isDraft=true), więc nie zostanie
  // duplikat. Argument `draftMsg` opcjonalny - bez niego edytuje
  // selectedMessage (zachowanie z #14, dla folderu Szkice). Z argumentem
  // edytuje konkretną wiadomość-szkic z wewnątrz wątku Inbox (#R2-2).
  const editDraft = async (draftMsg?: InboxMessage) => {
    const target = draftMsg ?? selectedMessage;
    if (!target || !target.isDraft) return;
    const revision = composerRevisionRef.current;
    let originalBody = '';
    try {
      const res = await messagesApi.getMessageBody(target.id);
      originalBody = res.data.bodyHtml || (res.data.bodyText ? `<pre>${res.data.bodyText}</pre>` : '');
    } catch {
      originalBody = target.snippet ? `<p>${target.snippet}</p>` : '';
      showToast('Nie udało się pobrać treści szkicu - zacznij od pustego.', 'error');
    }
    if (revision !== composerRevisionRef.current) return;
    setComposerOwnerId(selectedMessage?.id || null);
    setComposeData({
      to: target.to || '',
      cc: target.cc || '',
      bcc: '',
      subject: target.subject || '',
      bodyHtml: originalBody,
      parentId: (target as InboxMessage & { parentId?: string }).parentId || '',
    });
    setComposerAttachments([]);
    setShowCcBcc(Boolean(target.cc));
    setComposeMode('reply');
    setShowAiDraft(false);
  };

  const startNewMessage = () => {
    setComposeData({ to: '', cc: '', bcc: '', subject: '', bodyHtml: '', parentId: '' });
    setComposerAttachments([]);
    setShowCcBcc(false);
    setComposeMode('new');
    setSelectedMessage(null);
  };

  const hasComposeContent = () => {
    const plainText = composeData.bodyHtml.replace(/<[^>]*>/g, '').trim();
    return !!(composeData.to.trim() || composeData.subject.trim() || plainText);
  };

  // C3: upload załączników do composera
  const handleAttachFiles = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;

    const oversize = files.find((f) => f.size > 10 * 1024 * 1024);
    if (oversize) {
      showToast(`Plik "${oversize.name}" przekracza 10 MB`, 'error');
      return;
    }
    if (composerAttachments.length + files.length > 5) {
      showToast('Maksymalnie 5 załączników w wiadomości', 'error');
      return;
    }

    const revision = composerRevisionRef.current;
    setComposerAttaching(true);
    try {
      const uploaded = await messagesApi.uploadAttachments(files);
      if (revision !== composerRevisionRef.current) return;
      setComposerAttachments((prev) => [...prev, ...uploaded]);
    } catch (err) {
      showToast('Błąd wgrywania pliku', 'error');
      console.error('[Composer] upload failed:', err);
    } finally {
      if (revision === composerRevisionRef.current) setComposerAttaching(false);
    }
  };

  const handleRemoveAttachment = (storedAs: string) => {
    setComposerAttachments((prev) => prev.filter((a) => a.storedAs !== storedAs));
  };

  const handleComposerPaste = (e: React.ClipboardEvent) => {
    if (e.clipboardData?.files && e.clipboardData.files.length > 0) {
      e.preventDefault();
      handleAttachFiles(e.clipboardData.files);
    }
  };

  const handleComposerDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      setIsDraggingFile(true);
    }
  };

  const handleComposerDragLeave = (e: React.DragEvent) => {
    if (e.currentTarget === e.target) setIsDraggingFile(false);
  };

  const handleComposerDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingFile(false);
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      handleAttachFiles(e.dataTransfer.files);
    }
  };

  const closeCompose = () => {
    // Zamknięcie modala = całkowicie przerwij nagrywanie (bez transkrypcji).
    // User chciał się wyłączyć, nie zapisać półgodzinnego mruczenia.
    if (isComposeRecording) {
      cancelComposeRecording();
    }
    if (hasComposeContent() || composerAttachments.length > 0) {
      setShowDraftConfirm(true);
    } else {
      setComposeMode('none');
      setComposeData({ to: '', cc: '', bcc: '', subject: '', bodyHtml: '', parentId: '' });
      setComposerAttachments([]);
      setShowCcBcc(false);
    }
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = composeData.bodyHtml;
      const bodyText = tempDiv.textContent || tempDiv.innerText || '';

      await messagesApi.createMessage({
        to: composeData.to || undefined,
        cc: composeData.cc || undefined,
        bcc: composeData.bcc || undefined,
        subject: composeData.subject,
        bodyText,
        bodyHtml: composeData.bodyHtml,
        parentId: composeData.parentId || undefined,
        storeId: selectedStoreId || selectedMessage?.store?.id || undefined,
        isDraft: true,
      });
      showToast('Zapisano szkic');
      setShowDraftConfirm(false);
      setComposeMode('none');
      setComposeData({ to: '', cc: '', bcc: '', subject: '', bodyHtml: '', parentId: '' });
      setComposerAttachments([]);
      setShowCcBcc(false);
      invalidateMessages();
      // Odśwież otwarty wątek — bez tego szkic zapisany jako reply nie pokazuje
      // się w replies selectedMessage aż do zamknięcia/otwarcia wątku.
      // pkt 3 z sesji — "czasami widzę szkic, czasami nie" — to efekt tego
      // lokalnego stanu desynchronizowanego z backendem.
      if (selectedMessage) {
        try {
          const refetch = await messagesApi.getMessage(selectedMessage.id);
          setSelectedMessage(current => current?.id === selectedMessage.id ? refetch.data.data : current);
        } catch {
          /* ignore — invalidateMessages i tak pociągnie listę */
        }
      }
    } catch {
      showToast('Błąd zapisu szkicu', 'error');
    } finally {
      setSavingDraft(false);
    }
  };

  const discardDraft = () => {
    setShowDraftConfirm(false);
    setComposeMode('none');
    setComposeData({ to: '', cc: '', bcc: '', subject: '', bodyHtml: '', parentId: '' });
    setComposerAttachments([]);
    setShowCcBcc(false);
  };

  const previewMessageAttachment = async (messageId: string, attachment: {
    partId: string;
    filename: string;
    mimeType?: string;
  }) => {
    const normalizedMime = attachment.mimeType?.toLowerCase().split(';')[0].trim() || '';
    const isPdf = normalizedMime === 'application/pdf' || attachment.filename.toLowerCase().endsWith('.pdf');
    const previewKey = `${messageId}:${attachment.partId}`;
    setPreviewingAttachmentKey(previewKey);
    setLightboxFilename(attachment.filename);
    setLightboxType(isPdf ? 'pdf' : 'image');
    try {
      if (isPdf) {
        // PDF pobieramy przez klienta API z nagłówkiem autoryzacji. Bez tego
        // iframe potrafił dostać stronę logowania zamiast binarnego dokumentu.
        const response = await messagesApi.getMessagePartBlob(messageId, attachment.partId);
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        setLightboxUrl(URL.createObjectURL(pdfBlob));
      } else {
        setLightboxUrl(messagesApi.getMessagePartUrl(messageId, attachment.partId));
      }
    } catch {
      showToast('Nie udało się pobrać podglądu załącznika.', 'error');
    } finally {
      setPreviewingAttachmentKey(null);
    }
  };

  const sendMessage = () => {
    if (sendSubmissionLockRef.current || composerAttaching) return;
    if (composeMode === 'reply' && !replyMatchesSelection(composerOwnerId || '', selectedMessage?.id)) {
      showToast('Otwórz odpowiedź ponownie dla bieżącej wiadomości.', 'error');
      return;
    }
    if (
      missingTemplateFieldPattern.test(composeData.bodyHtml)
      || missingTemplateFieldPattern.test(composeData.subject)
    ) {
      showToast(
        'Uzupełnij lub usuń czerwono oznaczone pola przed wysłaniem wiadomości.',
        'error',
        { durationMs: 8000 },
      );
      return;
    }
    sendSubmissionLockRef.current = true;
    setSendingMessage(true);
    const outgoingComposeMode = composeMode === 'none'
      ? (composeData.parentId ? 'reply' : 'new')
      : composeMode;
    const outgoingComposeData = { ...composeData };
    const selectedMessageIdAtSend = selectedMessage?.id || null;
    const parentIdForCleanup = composeData.parentId;
    const stripUnmatched = (value: string) => value.replace(/\{\{[^}]+\}\}/g, '');
    const cleanBodyHtml = stripUnmatched(composeData.bodyHtml);
    const cleanSubject = stripUnmatched(composeData.subject);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanBodyHtml;
    const bodyText = tempDiv.textContent || tempDiv.innerText || '';

    const storeIdAtSend = selectedStoreId || selectedMessage?.store?.id || '';
    const pendingId = crypto.randomUUID();
    if (parentIdForCleanup && selectedMessageIdAtSend) {
      setPendingReplies(current => [...current, {
        id: pendingId, storeId: storeIdAtSend,
        conversationRootId: selectedMessage?.conversationRootId || selectedMessageIdAtSend,
        selectedMessageId: selectedMessageIdAtSend, state: 'sending', bodyText,
        filenames: composerAttachments.map(attachment => attachment.filename), draft: outgoingComposeData,
      }]);
    }
    const updatePending = (update: Partial<PendingReply>) => setPendingReplies(current => current.map(reply => reply.id === pendingId ? { ...reply, ...update } : reply));
    const request = messagesApi.createMessage({
      to: composeData.to,
      cc: composeData.cc || undefined,
      bcc: composeData.bcc || undefined,
      subject: cleanSubject,
      bodyText,
      bodyHtml: cleanBodyHtml,
      parentId: composeData.parentId || undefined,
      storeId: selectedStoreId || selectedMessage?.store?.id || undefined,
      isDraft: false,
      attachments: composerAttachments.length > 0
        ? composerAttachments.map((attachment) => ({
            storedAs: attachment.storedAs,
            filename: attachment.filename,
            mimeType: attachment.mimeType,
          }))
        : undefined,
    });

    // SMTP i zapis wątku kończą się w tle. Operator od razu odzyskuje panel i
    // może przejść do kolejnej wiadomości. Wynik nadal jest pokazany toastem.
    composeModeRef.current = 'none';
    setComposeMode('none');
    setComposeData({ to: '', cc: '', bcc: '', subject: '', bodyHtml: '', parentId: '' });
    setComposerAttachments([]);
    setShowCcBcc(false);
    setSendingMessage(false);
    window.setTimeout(() => {
      sendSubmissionLockRef.current = false;
    }, 0);
    showToast('Wysyłam wiadomość w tle');

    const restoreBodyAfterFailure = (): boolean => {
      if (composeModeRef.current !== 'none') return false;
      if (
        selectedMessageIdAtSend
        && customerCtxRef.current.selectedMessage?.id !== selectedMessageIdAtSend
      ) return false;
      composeModeRef.current = outgoingComposeMode;
      setComposeMode(outgoingComposeMode);
      setComposerOwnerId(selectedMessageIdAtSend);
      setComposeData(outgoingComposeData);
      setShowCcBcc(Boolean(outgoingComposeData.cc || outgoingComposeData.bcc));
      return true;
    };

    void request.then((response) => {
      if (response.data.warning) {
        updatePending({ state: 'failed', error: response.data.warning });
        const restored = restoreBodyAfterFailure();
        if (restored) setPendingReplies(current => current.filter(reply => reply.id !== pendingId));
        showToast(
          `${response.data.warning}${restored ? ` Treść została przywrócona do edytora${composerAttachments.length > 0 ? ', załączniki trzeba dodać ponownie' : ''}.` : ''}`,
          'error',
          { durationMs: 10000 },
        );
        return;
      }

      updatePending({ state: 'sent', serverMessageId: response.data.data?.id });
      if (composerMessageRef.current === selectedMessageIdAtSend) {
        setAiDraft('');
        setShowAiDraft(false);
      }
      if (parentIdForCleanup) {
        setReviewQueue(prev => prev.filter(item => item.messageId !== parentIdForCleanup));
        setExpandedDraftId(prev => {
          const stillExists = reviewQueue.find(item => item.id === prev && item.messageId !== parentIdForCleanup);
          return stillExists ? prev : null;
        });
      }
      showToast('Wiadomość wysłana');
      refreshInboxInBackground();
      if (parentIdForCleanup && selectedMessageIdAtSend) {
        void messagesApi.getMessage(selectedMessageIdAtSend).then(detailRes => {
          setSelectedMessage(current => current?.id === selectedMessageIdAtSend ? detailRes.data.data : current);
        }).catch(() => undefined);
      }
    }).catch((err: any) => {
      updatePending({ state: 'failed', error: err.response?.data?.error || 'Nie potwierdzono wysłania wiadomości. Sprawdź wątek przed ponowną próbą.' });
      const restored = restoreBodyAfterFailure();
      if (restored) setPendingReplies(current => current.filter(reply => reply.id !== pendingId));
      const errorMsg = err.response?.data?.error
        || `Nie potwierdzono wysłania wiadomości. Sprawdź wątek przed ponowną próbą.${restored ? ` Treść została przywrócona do edytora${composerAttachments.length > 0 ? ', załączniki trzeba dodać ponownie' : ''}.` : ''}`;
      showToast(errorMsg, 'error', { durationMs: 10000 });
    });
  };

  const applyTemplate = async (template: MessageTemplateItem) => {
    setShowTemplates(false);
    const revision = composerRevisionRef.current;
    // If we have a selected message, use the backend apply endpoint (with variable substitution + optional AI)
    if (selectedMessage) {
      setTemplateApplying(true);
      try {
        const res = await messagesApi.applyTemplate(template.id, {
          messageId: selectedMessage.id,
          aiPersonalize: aiTemplatePersonalizationEnabled,
        });
        if (revision !== composerRevisionRef.current) return;
        setComposeData(prev => ({
          ...prev,
          bodyHtml: res.data.bodyHtml,
          subject: res.data.subject || prev.subject,
        }));
        if (res.data.missingVariables?.length > 0) {
          showMissingTemplateWarning(res.data.missingVariables);
        } else if (res.data.tokensUsed > 0) {
          showToast(`Szablon spersonalizowany AI (${res.data.tokensUsed} tokenów)`);
        }
      } catch (error) {
        const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
        showToast(message || 'Nie udało się wczytać danych szablonu.', 'error');
      } finally {
        setTemplateApplying(false);
      }
    } else {
      // Bez wiadomości nie ma kontekstu klienta. Nadal wczytujemy szablon,
      // ale brakujące wartości oznaczamy w miejscu ich wystąpienia.
      const parsedBody = markMissingTemplateValues(template.bodyHtml, 'html');
      const parsedSubject = markMissingTemplateValues(template.subject || '', 'text');
      setComposeData(prev => ({
        ...prev,
        bodyHtml: parsedBody.content,
        subject: parsedSubject.content || prev.subject,
      }));
      showMissingTemplateWarning(mergeMissingTemplateVariables(
        parsedBody.missingVariables,
        parsedSubject.missingVariables,
      ));
    }
  };

  // Slash command: filter templates by query.
  // Przy samym "/" (bez query) pokazujemy WSZYSTKIE szablony — wcześniejsze
  // ograniczenie do tych z shortcut'em znikało gdy user nie miał ani jednego
  // szablonu ze skrótem, więc menu wyglądało jak "nie działa".
  const slashFilteredTemplates = useMemo(() => slashQuery === null ? [] : filterMessageTemplates(templates, slashQuery), [slashQuery, templates]);

  const slashTemplateGroups = useMemo(() => {
    const groups = new Map<string, {
      key: string;
      name: string;
      templates: Array<{ template: MessageTemplateItem; flatIndex: number }>;
    }>();
    slashFilteredTemplates.forEach((template, flatIndex) => {
      const key = template.category?.id || 'uncategorized';
      const name = template.category?.name?.trim() || 'Bez kategorii';
      const group = groups.get(key) || { key, name, templates: [] };
      group.templates.push({ template, flatIndex });
      groups.set(key, group);
    });
    return [...groups.values()];
  }, [slashFilteredTemplates]);

  // Handle Quill text change — detect slash commands.
  // Używamy Quill API (getText + getSelection) zamiast parse'owania HTML.
  // Wcześniejsza wersja brała `div.textContent` z `val` — Quill wstawia
  // tam trailing newline po `<p>` plus czasem zero-width chars (\u200B,
  // \uFEFF), przez co regex `\/([a-z0-9_-]*)$` nigdy nie matchował na
  // koniec stringu.
  const handleEditorChange = useCallback((val: string) => {
    setComposeData(prev => ({ ...prev, bodyHtml: val }));

    const editor = quillRef.current?.getEditor();
    let probe: string;
    let cursorIndex: number | null = null;
    if (editor) {
      const selection = editor.getSelection();
      if (selection) {
        cursorIndex = selection.index;
        probe = editor.getText(0, selection.index);
      } else {
        probe = editor.getText();
      }
    } else {
      const div = document.createElement('div');
      div.innerHTML = val;
      probe = div.textContent || '';
    }

    // Defensive cleanup — strip zero-width / format chars i trailing whitespace.
    const cleanProbe = probe.replace(/[\u200B-\u200F\uFEFF]/g, '').trimEnd();
    const slashMatch = cleanProbe.match(/\/([a-z0-9_-]*)$/i);

    if (slashMatch) {
      setSlashQuery(slashMatch[1]);
      setSlashIndex(0);
      // Cache pozycji `/sza` w plain text — używana w applySlashTemplate
      // gdy focus jest stracony po kliku na button w Portalu.
      if (cursorIndex != null) {
        slashStartRef.current = {
          start: cursorIndex - slashMatch[0].length,
          length: slashMatch[0].length,
        };
      }
      // Wylicz pozycję dropdownu w viewport (dla position: fixed w Portalu).
      if (editor && cursorIndex != null) {
        const bounds = editor.getBounds(cursorIndex);
        const container = (editor as unknown as { container?: HTMLElement }).container;
        const containerRect = container?.getBoundingClientRect();
        if (bounds && containerRect) {
          setSlashDropdownPos({
            top: containerRect.top + bounds.top + bounds.height + 4,
            left: containerRect.left + bounds.left,
            width: Math.max(280, Math.min(420, containerRect.width - bounds.left - 16)),
          });
        }
      }
    } else {
      setSlashQuery(null);
      setSlashDropdownPos(null);
      slashStartRef.current = null;
    }
  }, []);

  // Reset slash command state przy zmianie kontekstu (nowy mail / nowy
  // composer). Bez tego cache `slashStartRef` zostawał z poprzedniego maila —
  // klik na element dropdownu próbował deleteText na nieaktualnej pozycji
  // (out-of-range w nowym editorze), Quill rzucał exception, klik wyglądał
  // jak "nie reaguje". Po resecie applySlashTemplate fallbackuje na świeże
  // szukanie `/sza` w editorze.
  useEffect(() => {
    slashStartRef.current = null;
    setSlashQuery(null);
    setSlashDropdownPos(null);
  }, [selectedMessage?.id, composeMode]);

  // Click-outside — zamyka dropdown gdy user kliknie gdziekolwiek poza
  // dropdownem. Bez tego dropdown blokował klik na inne UI elementy
  // (np. "Odrzuć szkic"), bo z-index dropdownu (9999) nakładał się na nie.
  // Zmiana w editorze sama zamyka dropdown przez handleEditorChange (gdy
  // user kontynuuje pisanie i nie pasuje już do `/sza`), ale klik w
  // niezwiązany element musi też zamknąć.
  useEffect(() => {
    if (slashQuery === null) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (slashDropdownRef.current && target && !slashDropdownRef.current.contains(target)) {
        // Dla kliku w editor — nie zamykaj (handleEditorChange decyduje).
        const editor = quillRef.current?.getEditor();
        const editorContainer = (editor as unknown as { container?: HTMLElement } | undefined)?.container;
        if (editorContainer && editorContainer.contains(target)) return;
        setSlashQuery(null);
        setSlashDropdownPos(null);
        slashStartRef.current = null;
      }
    };
    // Capture phase żeby złapać przed innymi handlerami które mogą
    // stopPropagation (np. modal backdrop close).
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [slashQuery]);

  // Keyboard navigation w slash dropdownie. Dropdown jest renderowany przez
  // Portal w document.body, więc focus zostaje w editorze — events na
  // dropdown div'ie nie łapają się. Global keydown listener filtrowany przez
  // `slashQuery !== null`.
  useEffect(() => {
    if (slashQuery === null) return;
    const handler = (e: KeyboardEvent) => {
      if (slashFilteredTemplates.length === 0 && e.key !== 'Escape') return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashIndex(prev => Math.min(prev + 1, slashFilteredTemplates.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (slashFilteredTemplates[slashIndex]) {
          applySlashTemplateRef.current?.(slashFilteredTemplates[slashIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setSlashQuery(null);
        setSlashDropdownPos(null);
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [slashQuery, slashFilteredTemplates, slashIndex]);

  // Ref do `applySlashTemplate` żeby useEffect powyżej nie zależał od jego
  // identity (callback rebind co render → re-attach listener). applySlashTemplate
  // zdefiniowany niżej, ref aktualizowany przez useEffect.
  const applySlashTemplateRef = useRef<((t: MessageTemplateItem) => void) | null>(null);

  // Substytucja placeholderów {{...}} w szablonie z danych dostępnych
  // we frontendzie. Strategia hybrydowa (decyzja user'a 1.49.15):
  // - Jeśli mamy daną → podstaw konkretną wartość
  // - Jeśli NIE mamy danej → zostaw {{placeholder}} jako visual indicator
  //   dla operatora (zobaczy że czegoś brakuje przed wysyłką)
  // - Backend przy wysyłce (`email.service.ts:107` parseTemplate) zamienia
  //   wszelkie pozostałe niezamienione na puste — klient nigdy nie widzi
  //   surowych {{...}}.
  const substituteTemplateVariables = useCallback(
    (html: string): string => {
      // Guard: gdy backend zwróci szablon bez bodyHtml (legacy template,
      // bug w DB, etc.), funkcja musi zwrócić pusty string zamiast wybuchać
      // przy `result.replace(...)`. Wcześniej brak guarda powodował
      // TypeError na 1.49.18 — applySlashTemplate przerywał na pierwszym
      // wywołaniu (cached.length=1 zostawał, bo handleEditorChange
      // też używał substituteTemplateVariables pośrednio przez memo).
      if (typeof html !== 'string') return '';
      const replacements: Record<string, string> = {};

      // Imię / nazwisko / pełne imię — preferuj customerData.customer.name
      // (z bazy zamówień, najpewniejsze), fallback na fromName z wiadomości.
      const fullName = customerData?.customer?.name || selectedMessage?.fromName || '';
      const trimmedName = fullName.trim();
      if (trimmedName) {
        const parts = trimmedName.split(/\s+/);
        replacements.imie = parts[0];
        if (parts.length >= 2) {
          replacements.nazwisko = parts.slice(1).join(' ');
        }
        replacements.imie_nazwisko = trimmedName;
      }

      // Email klienta
      const customerEmail =
        customerData?.customer?.email || resolveMessageCustomerEmail(selectedMessage) || '';
      if (customerEmail) replacements.email = customerEmail;

      // Telefon
      if (customerData?.customer?.phone) {
        replacements.telefon = customerData.customer.phone;
      }

      // Najnowsze zamówienie (orders sorted desc by createdAt — pierwsze = najnowsze).
      // UWAGA: Prisma Decimal → JSON serializuje się jako STRING. TypeScript
      // interface mówi `totalAmount: number`, ale runtime to "299.00".
      // Bez Number() conversion `.toFixed()` rzuca TypeError i ZAWALA całą
      // funkcję (1.49.21 zdiagnozował tym konkretnym mailem). Wszystkie
      // operacje na wartościach numerycznych zabezpieczone.
      const newestOrder = customerData?.orders?.[0];
      if (newestOrder) {
        if (newestOrder.orderNumber) replacements.numer_zamowienia = newestOrder.orderNumber;
        try {
          const dateRaw = newestOrder.externalCreatedAt || newestOrder.createdAt;
          if (dateRaw) {
            replacements.data_zamowienia = new Date(dateRaw).toLocaleDateString('pl-PL');
          }
        } catch {
          // invalid date — pomiń
        }
        const totalNum = Number(newestOrder.totalAmount);
        if (Number.isFinite(totalNum)) {
          replacements.wartosc = totalNum.toLocaleString('pl-PL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }
        const statusVal = newestOrder.externalStatus || newestOrder.status;
        if (statusVal) replacements.status = statusVal;
        if (newestOrder.shippingMethod) {
          replacements.metoda_wysylki = newestOrder.shippingMethod;
        }
        if (newestOrder.paymentMethod) {
          replacements.metoda_platnosci = newestOrder.paymentMethod;
        }
        // Lista produktów — items może być cokolwiek (JsonValue), bezpiecznie
        const items = Array.isArray(newestOrder.items)
          ? (newestOrder.items as Array<{ name?: unknown; quantity?: unknown }>)
          : null;
        if (items && items.length > 0) {
          const formatItem = (i: { name?: unknown; quantity?: unknown }) => {
            const name = typeof i.name === 'string' ? i.name : 'Produkt';
            const qty = i.quantity != null ? ` x${String(i.quantity)}` : '';
            return `${name}${qty}`;
          };
          replacements.produkty = items.map(formatItem).join(', ');
          replacements.produkty_lista = items.map(i => `- ${formatItem(i)}`).join('\n');
        }
        const newestShipment = newestOrder.shipments?.[0];
        if (newestShipment?.trackingNumber) {
          replacements.tracking = newestShipment.trackingNumber;
        }
      }

      // Adres dostawy
      const shipping = customerData?.shippingAddress as Record<string, string> | undefined;
      if (shipping) {
        const street = [shipping.address_1, shipping.address_2].filter(Boolean).join(' ');
        if (street) replacements.ulica = street;
        if (shipping.city) replacements.miasto = shipping.city;
        if (shipping.postcode) replacements.kod_pocztowy = shipping.postcode;
        if (shipping.country) replacements.kraj = shipping.country;
        const fullAddress = [
          street,
          [shipping.postcode, shipping.city].filter(Boolean).join(' '),
          shipping.country,
        ]
          .filter(Boolean)
          .join(', ');
        if (fullAddress) replacements.adres = fullAddress;
      }

      // Substytuuj — niezamienione zostają jako {{placeholder}}.
      let result = html;
      for (const [key, value] of Object.entries(replacements)) {
        if (!value) continue;
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        result = result.replace(regex, value);
      }
      return result;
    },
    [customerData, selectedMessage],
  );

  // Apply slash command template.
  // Używa Quill API directly do usunięcia "/sza" + wstawienia szablonu w
  // poprawnym miejscu. Wcześniejsza wersja brała `lastIndexOf('/')` z
  // surowego HTML stringu — trafiało w `/` z `</p>` zamiast w `/` użytkownika,
  // przez co w composerze zostawało "/sza<" obok szablonu.
  const applySlashTemplate = useCallback(async (template: MessageTemplateItem) => {
    // UWAGA: NIE używamy composeMode jako guarda. W fullscreen mode composer
    // może być aktywny w UI mimo `composeMode === 'none'` (świadomy hack
    // na linii ~2173: "W fullscreen NIE resetujemy composeMode"). Editor
    // istnienie (`quillRef.current?.getEditor()`) jest jedynym wiarygodnym
    // wskaźnikiem że composer jest gotowy do operacji.
    // substituteTemplateVariables ma try/catch wewnątrz — defensywne
    // konwersje typów (Number() na totalAmount które runtime jest stringiem
    // mimo TS interface mówiącego number). Bez tego cała funkcja crashed
    // dla maili gdzie klient miał zamówienia.
    const revision = composerRevisionRef.current;
    let filledBodyHtml: string;
    let filledSubject: string | null | undefined;
    let missingVariables: MissingTemplateVariableDto[] = [];
    try {
      if (selectedMessage) {
        setTemplateApplying(true);
        const response = await messagesApi.applyTemplate(template.id, {
          messageId: selectedMessage.id,
          aiPersonalize: aiTemplatePersonalizationEnabled,
        });
        filledBodyHtml = response.data.bodyHtml;
        filledSubject = response.data.subject;
        missingVariables = response.data.missingVariables || [];
      } else {
        const parsedBody = markMissingTemplateValues(
          substituteTemplateVariables(template.bodyHtml),
          'html',
        );
        const parsedSubject = markMissingTemplateValues(
          template.subject ? substituteTemplateVariables(template.subject) : '',
          'text',
        );
        filledBodyHtml = parsedBody.content;
        filledSubject = parsedSubject.content || template.subject;
        missingVariables = mergeMissingTemplateVariables(
          parsedBody.missingVariables,
          parsedSubject.missingVariables,
        );
      }
    } catch (err) {
      console.error('[applySlashTemplate] substitution failed:', err);
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      showToast(message || 'Nie udało się wczytać danych szablonu.', 'error');
      setSlashQuery(null);
      setSlashDropdownPos(null);
      slashStartRef.current = null;
      return;
    } finally {
      setTemplateApplying(false);
    }

    if (revision !== composerRevisionRef.current) return;
    if (!filledBodyHtml) {
      setSlashQuery(null);
      setSlashDropdownPos(null);
      slashStartRef.current = null;
      return;
    }

    showMissingTemplateWarning(missingVariables);

    const editor = quillRef.current?.getEditor();
    if (!editor) {
      setComposeData(prev => ({
        ...prev,
        bodyHtml: prev.bodyHtml.replace(/\/[a-z0-9_-]*$/i, '') + filledBodyHtml,
        subject: filledSubject || prev.subject,
      }));
      setSlashQuery(null);
      setSlashDropdownPos(null);
      slashStartRef.current = null;
      return;
    }

    // Strategia 2-poziomowa znajdowania `/sza` w editorze:
    //   1) Cache (slashStartRef) z handleEditorChange — preferred bo dokładny.
    //      Weryfikujemy czy cached pozycja faktycznie zawiera `/sza` (cache
    //      może być nieaktualny po zmianie maila/composera).
    //   2) Fallback: szukamy `/sza` świeżo w pełnym tekście editora.
    //   3) Last resort: wstaw szablon na końcu bez usuwania niczego.
    let slashStart: number | null = null;
    let slashLength: number | null = null;

    const cached = slashStartRef.current;
    if (cached) {
      try {
        const verifyText = editor.getText(cached.start, cached.length);
        if (/^\/[a-z0-9_-]*$/i.test(verifyText)) {
          slashStart = cached.start;
          slashLength = cached.length;
        }
      } catch {
        // Out of range — cache nieaktualny, ignoruj
      }
    }

    if (slashStart === null) {
      const fullText = editor
        .getText()
        .replace(/[\u200B-\u200F\uFEFF]/g, '')
        .trimEnd();
      const slashMatch = fullText.match(/\/([a-z0-9_-]*)$/i);
      if (slashMatch && slashMatch.index !== undefined) {
        slashStart = slashMatch.index;
        slashLength = slashMatch[0].length;
      }
    }

    try {
      if (slashStart !== null && slashLength !== null) {
        editor.deleteText(slashStart, slashLength, 'user');
        editor.clipboard.dangerouslyPasteHTML(slashStart, filledBodyHtml, 'user');
      } else {
        const cursorIndex = editor.getSelection()?.index ?? editor.getLength();
        editor.clipboard.dangerouslyPasteHTML(cursorIndex, filledBodyHtml, 'user');
      }
      setComposeData(prev => ({
        ...prev,
        bodyHtml: editor.root.innerHTML,
        subject: filledSubject || prev.subject,
      }));
    } catch (err) {
      console.error('[applySlashTemplate] Quill manipulation failed, using string fallback:', err);
      setComposeData(prev => ({
        ...prev,
        bodyHtml: prev.bodyHtml.replace(/\/[a-z0-9_-]*$/i, '') + filledBodyHtml,
        subject: filledSubject || prev.subject,
      }));
    }

    setSlashQuery(null);
    setSlashDropdownPos(null);
    slashStartRef.current = null;
  }, [aiTemplatePersonalizationEnabled, selectedMessage, substituteTemplateVariables]);

  // Synchronizacja refa po każdym render (dla global keyboard handlera).
  useEffect(() => {
    applySlashTemplateRef.current = applySlashTemplate;
  }, [applySlashTemplate]);

  // Auto-scroll do POCZĄTKU ostatniej wiadomości (nie na sam koniec skrolla).
  // User chce widzieć początek ostatniej wiadomości — żeby móc ją przeczytać od góry,
  // nie skoczyć na jej stopkę/załączniki.
  useEffect(() => {
    if (selectedMessage) {
      setTimeout(() => {
        lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [selectedMessage?.id]);

  const latestPendingId = pendingInThread.at(-1)?.id;
  useEffect(() => {
    if (latestPendingId) pendingReplyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [latestPendingId]);

  // A successful local bubble is retired only once its persisted message is visible.
  useEffect(() => {
    if (!selectedMessage) return;
    const visibleIds = new Set([selectedMessage.id, ...(selectedMessage.replies || []).map(reply => reply.id)]);
    setPendingReplies(current => {
      const next = current.filter(reply => !reply.serverMessageId || !visibleIds.has(reply.serverMessageId));
      return next.length === current.length ? current : next;
    });
  }, [selectedMessage, pendingReplies]);

  // Custom folders CRUD usunięte — zarządzanie folderami przeniesione do natywnego
  // drzewa IMAP w InboxSidebar. Tworzenie/usuwanie folderów odbywa się przez
  // `inboxEmailApi.createImapFolder(configId, path)` i `deleteImapFolder(folderId)`.

  const addLabelToMessage = async (messageId: string, labelId: string) => {
    try {
      await messagesApi.addLabelToMessage(messageId, labelId);
      if (selectedMessage?.id === messageId) {
        const response = await messagesApi.getMessage(messageId);
        setSelectedMessage(current => current?.id === messageId ? response.data.data : current);
      }
      invalidateMessages();
    } catch {
      showToast('Błąd dodawania etykiety', 'error');
    }
  };

  const removeLabelFromMessage = async (messageId: string, labelId: string) => {
    try {
      await messagesApi.removeLabelFromMessage(messageId, labelId);
      if (selectedMessage?.id === messageId) {
        const response = await messagesApi.getMessage(messageId);
        setSelectedMessage(current => current?.id === messageId ? response.data.data : current);
      }
      invalidateMessages();
    } catch {
      showToast('Błąd usuwania etykiety', 'error');
    }
  };

  // Akcje które NIE usuwają wiadomości z bieżącego widoku — user chce
  // kontynuować pracę nad tym samym zestawem (np. oznacz jako przeczytane,
  // potem usuń). Destruktywne (trash/archive/move/restore) czyszczą zaznaczenie
  // bo maile znikają z widoku.
  const NON_CLEARING_BULK_ACTIONS = new Set([
    'read', 'unread', 'star', 'unstar',
    'resolve', 'pending', 'important',
    'add_label', 'remove_label',
  ]);

  const doBulkAction = (action: string, ids: string[], isExpunge = false) => {
    const count = ids.length;
    const keepSelection = NON_CLEARING_BULK_ACTIONS.has(action);
    // Undo dla soft-trash z NON-trash view: user mógł pomyłkowo wyrzucić
    // całą skrzynkę, dajemy 5s żeby cofnąć. Expunge bez undo (już jest
    // destruktywny confirm). W widoku Kosza soft-delete nie zachodzi,
    // więc undo też bez sensu.
    const canUndo = action === 'trash' && !isExpunge && !isInTrashView();
    mutations.bulkAction.mutate(
      { ids, action },
      {
        onSuccess: () => {
          if (!keepSelection) {
            setSelectedIds(new Set());
            setSelectAllInFolder(false);
          }
          if (isExpunge) {
            showToast(`Usunięto na stałe ${count} ${count === 1 ? 'wiadomość' : 'wiadomości'}`);
          } else if (canUndo) {
            showToast(bulkActionToastMessage(action, count), 'success', {
              durationMs: 5000,
              action: {
                label: 'Cofnij',
                onClick: () => {
                  mutations.bulkAction.mutate(
                    { ids, action: 'restore' },
                    {
                      onSuccess: () => {
                        showToast(`Przywrócono ${count} ${count === 1 ? 'wiadomość' : 'wiadomości'}`);
                      },
                    },
                  );
                },
              },
            });
          } else {
            showToast(bulkActionToastMessage(action, count));
          }
        },
      },
    );
  };

  const BULK_CONFIRM_THRESHOLD = 100;

  const bulkAction = (action: string) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const count = ids.length;
    // Bulk trash w widoku Kosza = TRWAŁE usunięcie (backend robi expunge
    // dla maili z specialUse=\Trash). Confirm ZAWSZE, niezależnie od N.
    // Pokrywa oba sposoby wejścia: wirtualny folder 'trash' LUB konkretny
    // IMAP folder ze specialUse='\\Trash' (drzewko "Wszystkie foldery").
    if (action === 'trash' && isInTrashView()) {
      setExpungeConfirm({ ids, mode: 'bulk' });
      return;
    }
    // Bulk soft-delete (inny folder → do Kosza) przy dużej liczbie: confirm
    // żeby user nie zbrzmiał Kosza przez pomyłkę. Archive celowo bez confirmu
    // (reversible przez restore-any).
    if (action === 'trash' && count > BULK_CONFIRM_THRESHOLD) {
      setPendingBulkAction({ action, count, isExpunge: false });
      return;
    }
    doBulkAction(action, ids);
  };

  // Bulk move-to-folder: bulkAction hook nie przyjmuje folderId w sygnaturze,
  // więc wywołujemy messagesApi bezpośrednio i ręcznie ręcznie odświeżamy
  // listę + liczniki (spójnie z invalidateStats).
  const bulkMoveToFolder = async (folderId: string) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      await messagesApi.bulkAction({ storeId: selectedStoreId, ids, action: 'move_to_folder', folder: folderId });
      // Po przenoszeniu wygaszamy tryb zaznaczania (user zakończył akcję),
      // żeby nie trzeba było klikać "Anuluj". Kolejne klikanie maila znów
      // otwiera podgląd, nie zaznacza.
      setSelectedIds(new Set());
      setSelectionMode(false);
      setShowBulkMoveMenu(false);
      showToast(`Przeniesiono ${ids.length} ${ids.length === 1 ? 'wiadomość' : 'wiadomości'} do folderu`);
      invalidateMessages();
      loadStats();
      queryClient.invalidateQueries({ queryKey: ['imap-folders-tree'] });
    } catch {
      showToast('Błąd przenoszenia wiadomości', 'error');
    }
  };

  // Gmail-style: klik zaznacza WIDOCZNE (załadowane w useInfiniteQuery).
  // Drugi klik = odznacz wszystko. Trzeci stan "wszystkie w folderze" osiąga
  // się przez banner handleSelectAllInFolder().
  const toggleSelectAll = () => {
    if (selectAllInFolder || selectedIds.size > 0) {
      setSelectedIds(new Set());
      setSelectAllInFolder(false);
    } else {
      setSelectedIds(new Set(messages.map(m => m.id)));
    }
  };

  // Pobiera WSZYSTKIE ID w folderze (z aktualnymi filtrami) — backend cap
  // MAX_IDS_PER_REQUEST=50_000. Większe foldery zwracają 409 TOO_MANY_IDS.
  const handleSelectAllInFolder = async () => {
    setSelectAllLoading(true);
    try {
      const params: Record<string, unknown> = {
        source: sourceFilter,
        ...(selectedImapFolderId ? { imapFolderId: selectedImapFolderId } : { folder: folderFilter }),
        search: debouncedSearchQuery || undefined,
        storeId: messageListStoreIdParam,
        customerEmail: customerEmailParam,
      };
      if (activeLabelFilter) params.labelId = activeLabelFilter;
      if (statusFilter !== 'all') params.conversationStatus = statusFilter;

      const response = await messagesApi.getMessageIds(params as any);
      setSelectedIds(new Set(response.data.ids));
      setSelectAllInFolder(true);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        const total = err.response.data?.error?.details?.total;
        const max = err.response.data?.error?.details?.max;
        showToast(
          total && max
            ? `Folder zawiera ${total} wiadomości. Maksymalne zaznaczenie to ${max}.`
            : err.response.data?.error?.message || 'Zbyt wiele wiadomości do zaznaczenia',
          'error',
        );
      } else {
        showToast('Nie udało się zaznaczyć wszystkich', 'error');
      }
    } finally {
      setSelectAllLoading(false);
    }
  };

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedIds(newSet);
    // Manual toggle = zrywa "wszystkie w folderze" (bo subset != all).
    if (selectAllInFolder) setSelectAllInFolder(false);
  };

  // ESC closes compose
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // #7 - gdy popup zamówienia jest otwarty, oddajemy ESC jego własnemu
        // handlerowi (OrderDetailModal). Bez tego skrzynka łapała ESC pierwsza
        // i zamykała wiadomość pod modalem zamiast modala na wierzchu.
        if (orderModalId || similarOrderSource || reviewTemplateId || complaintModalId) return;
        if (lightboxUrl) setLightboxUrl(null);
        else if (showCouponModal) setShowCouponModal(false);

        else if (showSenderCard) setShowSenderCard(false);
        else if (showAddLabel) setShowAddLabel(false);
        else if (showAdvancedSearch) setShowAdvancedSearch(false);
        else if (showTemplates) setShowTemplates(false);
        else if (showDraftConfirm) setShowDraftConfirm(false);
        else if (composeMode !== 'none') closeCompose();
        // #R3-3 - w fullscreen ESC najpierw wychodzi z fullscreen, dopiero
        // kolejny ESC zamyka wiadomość. User może chcieć wyjść z fullscreen
        // bez tracenia kontekstu otwartego maila.
        else if (isFullscreen) void exitFullscreen();
        else if (selectedMessage) setSelectedMessage(null);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [complaintModalId, similarOrderSource, reviewTemplateId, composeMode, showTemplates, showAddLabel, showAdvancedSearch, selectedMessage, showSenderCard, showCouponModal, lightboxUrl, showDraftConfirm, orderModalId, isFullscreen]);

  // Make Quill toolbar buttons non-tabbable so Tab jumps to editor body
  useEffect(() => {
    if (composeMode === 'none') return;
    const timer = setTimeout(() => {
      document.querySelectorAll('.inbox-editor .ql-toolbar button, .inbox-editor .ql-toolbar .ql-picker-label').forEach(el => {
        (el as HTMLElement).tabIndex = -1;
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [composeMode]);

  const applyAdvancedSearch = () => {
    const parts: string[] = [];
    if (advSearch.from) parts.push(`from:${advSearch.from.includes(' ') ? `"${advSearch.from}"` : advSearch.from}`);
    if (advSearch.to) parts.push(`to:${advSearch.to.includes(' ') ? `"${advSearch.to}"` : advSearch.to}`);
    if (advSearch.subject) parts.push(`subject:${advSearch.subject.includes(' ') ? `"${advSearch.subject}"` : advSearch.subject}`);
    if (advSearch.dateFrom) parts.push(`after:${advSearch.dateFrom}`);
    if (advSearch.dateTo) parts.push(`before:${advSearch.dateTo}`);
    if (advSearch.hasAttachment) parts.push('has:attachment');
    if (advSearch.isUnread) parts.push('is:unread');
    if (advSearch.isStarred) parts.push('is:starred');
    if (advSearch.labelName) parts.push(`label:${advSearch.labelName.includes(' ') ? `"${advSearch.labelName}"` : advSearch.labelName}`);
    setSearchQuery(parts.join(' '));
       setShowAdvancedSearch(false);
  };

  const clearAdvancedSearch = () => {
    setAdvSearch({ from: '', to: '', subject: '', dateFrom: '', dateTo: '', hasAttachment: false, isUnread: false, isStarred: false, labelName: '' });
    setSearchQuery('');
     };

  // Skróty klawiszowe WYŁĄCZONE (2026-04-16) — blokowały native browser
  // shortcuts jak Cmd+R (refresh), Cmd+F (search) itp. Nikt z nich nie
  // korzystał w tym zespole. Escape (zamykanie modali) dalej działa przez
  // handleEsc useEffect powyżej.

  // C2: focusedIndex + scrollIntoView usunięte razem z keyboard nav (WYŁĄCZONE
  // 2026-04-16). Jeśli będzie kiedyś re-enable, użyj
  // virtuosoRef.current?.scrollToIndex({ index, align: 'nearest' }).

  // Definicja wirtualnych folderów systemowych (Inbox/Sent/Drafts/Archive/Spam/Trash)
  // jest teraz enkapsulowana w InboxSidebar — strona przekazuje tylko selekcję.

  // Sanitize email HTML — zdefiniowane na top-level jako `sanitizeEmailHtmlTopLevel`
  // i używane przez MessageBodyRenderer (lazy fetch z IMAP).

  // Compose editor component (reused for both new message and reply)
  // Layout: outer = flex column z ograniczoną wysokością, żeby przy długiej
  // treści maila pasek "Wyślij/Anuluj" zostawał widoczny - patrz scroll
  // wrapper niżej. Reply zawsze wypełnia wysokość przydzieloną mu przez sekcję
  // compose, która uwzględnia też zewnętrzny banner propozycji AI.
  const threadReviewDraft = selectedMessage
    ? reviewQueue.find(item => item.messageId === selectedMessage.id && !handledThreadDraftIds.has(item.id)) || null
    : null;

  // Dotychczas draft był widoczny jako osobny banner nawet przed kliknięciem
  // "Odpowiedz". Zachowujemy tę dostępność, ale od razu otwieramy composer,
  // żeby propozycja pojawiła się wewnątrz jego pola treści. Zależności celowo
  // nie obejmują composeMode: ręczne zamknięcie edytora nie może go natychmiast
  // otworzyć ponownie dla tego samego draftu.
  useEffect(() => {
    if (!threadReviewDraft || !selectedMessage || composeModeRef.current !== 'none') return;
    setComposerOwnerId(selectedMessage.id);
    setComposeData(createReplyComposer(selectedMessage));
    setComposerAttachments([]);
    setShowCcBcc(false);
    setComposeMode('reply');
    setShowAiDraft(false);
  }, [selectedMessage?.id, threadReviewDraft?.id]);

  const composerAiProposal = threadReviewDraft
    ? {
        reviewId: threadReviewDraft.id,
        text: threadReviewDraft.draftText || '',
        html: threadReviewDraft.draftHtml || plainTextToEmailHtml(threadReviewDraft.draftText || ''),
      }
    : showAiDraft && aiDraft
      ? { reviewId: null, text: aiDraft, html: plainTextToEmailHtml(aiDraft) }
      : null;

  const acceptComposerAiProposal = () => {
    if (!composerAiProposal || !selectedMessage) return;
    setComposeData(prev => ({
      ...prev,
      bodyHtml: composerAiProposal.html,
      to: replyMatchesSelection(prev.parentId, selectedMessage.id) ? prev.to : createReplyComposer(selectedMessage).to,
      subject: prev.subject || `Re: ${selectedMessage.subject || ''}`,
      parentId: selectedMessage.id,
    }));
    if (composerAiProposal.reviewId) {
      setHandledThreadDraftIds(previous => new Set(previous).add(composerAiProposal.reviewId!));
    } else {
      setAiDraft('');
      setShowAiDraft(false);
    }
  };

  const rejectComposerAiProposal = () => {
    if (!composerAiProposal) return;
    if (composerAiProposal.reviewId) {
      void rejectReviewItem(composerAiProposal.reviewId);
      return;
    }
    setAiDraft('');
    setShowAiDraft(false);
  };

  const applyReviewTemplate = async (template: MessageTemplateItem) => {
    const item = reviewQueue.find(entry => entry.id === reviewTemplateId);
    if (!item || templateApplying) return;
    setTemplateApplying(true);
    try {
      const { data } = await messagesApi.applyTemplate(template.id, {
        messageId: item.messageId,
        aiPersonalize: aiTemplatePersonalizationEnabled,
      });
      const draftHtml = DOMPurify.sanitize(data.bodyHtml);
      const textDocument = new DOMParser().parseFromString(
        draftHtml.replace(/<br\s*\/?>(?!$)/gi, '\n').replace(/<\/p>/gi, '\n\n'), 'text/html',
      );
      const draftText = textDocument.body.textContent?.trim() || '';
      await messagesApi.updateAutoReplyDraft(item.id, { draftHtml, draftText });
      setReviewQueue(queue => queue.map(entry => entry.id === item.id ? { ...entry, draftHtml, draftText } : entry));
      setReviewTemplateId(null);
      if (data.missingVariables?.length) showMissingTemplateWarning(data.missingVariables);
      else showToast('Szablon wstawiony do odpowiedzi');
    } catch (error) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      showToast(message || 'Nie udało się wstawić szablonu.', 'error');
    } finally {
      setTemplateApplying(false);
    }
  };

  const renderTemplateChoices = (onSelect: (template: MessageTemplateItem) => void) => (
    <MessageTemplateChoices templates={templates} onSelect={onSelect} disabled={templateApplying} />
  );

  const renderComposeEditor = (isReply: boolean) => (
    <div
      className={`relative bg-surface-raised/40 rounded-xl border border-border-subtle overflow-hidden animate-fade-in flex flex-col ${
        isReply && isFullscreen ? 'h-full min-h-0' : !isReply ? 'max-h-[calc(100vh-180px)]' : ''
      }`}
      onPaste={handleComposerPaste}
      onDragOver={handleComposerDragOver}
      onDragLeave={handleComposerDragLeave}
      onDrop={handleComposerDrop}
    >
      {/* C3: drag-drop overlay — pokazuje się nad composerem gdy user przeciąga plik */}
      {isDraggingFile && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-accent-primary/5 border-2 border-dashed border-accent-primary/40 rounded-xl pointer-events-none backdrop-blur-[2px]">
          <div className="flex items-center gap-2 px-4 py-2 bg-surface-raised rounded-lg border border-accent-primary/30 text-accent-primary text-sm font-medium">
            <Paperclip className="w-4 h-4" /> Upuść pliki aby dołączyć
          </div>
        </div>
      )}

      {/* Compose header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <h3 className="text-sm font-semibold text-text-primary">
          {isReply ? 'Odpowiedz' : 'Nowa wiadomość'}
        </h3>
        <button
          onClick={closeCompose}
          className="btn-ghost p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Fields */}
      <div className="px-4 py-3 space-y-2 border-b border-border-subtle">
        <div className="flex items-center gap-2">
          <label className="text-xs text-text-muted w-10 flex-shrink-0">Do</label>
          <input
            type="email"
            value={composeData.to}
            onChange={(e) => setComposeData(prev => ({ ...prev, to: e.target.value }))}
            className="flex-1 bg-transparent text-sm text-text-primary border-none outline-none placeholder-text-disabled"
            placeholder="email@example.com"
          />
          <button
            onClick={() => setShowCcBcc(!showCcBcc)}
            className="text-xs text-text-muted hover:text-accent-primary transition-colors flex-shrink-0"
          >
            {showCcBcc ? 'Ukryj CC/BCC' : 'CC/BCC'}
          </button>
        </div>
        {showCcBcc && (
          <>
            <div className="flex items-center gap-2">
              <label className="text-xs text-text-muted w-10 flex-shrink-0">DW</label>
              <input
                type="text"
                value={composeData.cc}
                onChange={(e) => setComposeData(prev => ({ ...prev, cc: e.target.value }))}
                className="flex-1 bg-transparent text-sm text-text-primary border-none outline-none placeholder-text-disabled"
                placeholder="email1@example.com, email2@example.com"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-text-muted w-10 flex-shrink-0">UDW</label>
              <input
                type="text"
                value={composeData.bcc}
                onChange={(e) => setComposeData(prev => ({ ...prev, bcc: e.target.value }))}
                className="flex-1 bg-transparent text-sm text-text-primary border-none outline-none placeholder-text-disabled"
                placeholder="email@example.com"
              />
            </div>
          </>
        )}
        <div className="flex items-center gap-2">
          <label className="text-xs text-text-muted w-10 flex-shrink-0">Temat</label>
          <input
            type="text"
            value={composeData.subject}
            onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
            className={`flex-1 bg-transparent text-sm border-none outline-none placeholder-text-disabled ${
              missingTemplateFieldPattern.test(composeData.subject) ? 'text-danger font-semibold' : 'text-text-primary'
            }`}
            placeholder="Temat wiadomości"
          />
        </div>
        {/* C3: lista załączników composera */}
        {composerAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {composerAttachments.map((att) => (
              <div
                key={att.storedAs}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface-hover/60 border border-border text-xs"
              >
                <Paperclip className="w-3 h-3 text-text-muted flex-shrink-0" />
                <span className="text-text-primary truncate max-w-[200px]">{att.filename}</span>
                <span className="text-[10px] text-text-muted flex-shrink-0">{formatFileSize(att.size)}</span>
                <button
                  onClick={() => handleRemoveAttachment(att.storedAs)}
                  className="text-text-muted hover:text-danger transition-colors flex-shrink-0"
                  title="Usuń załącznik"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* W fullscreenie prawy panel ma własny scroll. W zwykłym widoku
          zawartość composera rośnie naturalnie i przewija się razem z całym
          wątkiem, dzięki czemu odpowiedź nie zasłania wiadomości. */}
      <div className={isReply && isFullscreen ? 'flex-1 min-h-0 overflow-y-auto' : ''}>

      {/* Editor */}
      <div className="relative">
        {isReply && composerAiProposal && (
          <div className="border-b border-border-subtle bg-surface-raised px-4 py-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-accent-primary">
              <Sparkles className="h-3.5 w-3.5" />
              Propozycja AI
            </div>
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-text-primary">
              {composerAiProposal.text}
            </div>
            <div className="mt-3 flex items-center justify-between gap-2 border-t border-border-subtle pt-3">
              <div className="flex items-center gap-2">
                <Button type="button" size="sm" onClick={acceptComposerAiProposal} leftIcon={<Check />}>
                  Użyj
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={rejectComposerAiProposal}
                  disabled={Boolean(composerAiProposal.reviewId && reviewActionLoading === composerAiProposal.reviewId)}
                  className="text-danger hover:text-danger"
                  leftIcon={<X />}
                >
                  Odrzuć
                </Button>
              </div>
            </div>
          </div>
        )}

        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={composeData.bodyHtml}
          onChange={handleEditorChange}
          modules={quillModules}
          formats={quillFormats}
          placeholder="Wpisz treść wiadomości... (/ dla szablonów)"
          className="inbox-editor"
        />

        {/* Slash dropdown jest renderowany przez Portal w `document.body`
            (poniżej w drzewie JSX, ale pomijając całe overflow:hidden /
            clipping konteksty rodziców editora). Trzy poprzednie próby
            (1.49.2/1.49.10/1.49.11) zawodziły bo dropdown był DOM-renderowany,
            ale clipped przez scroll wrapper z `overflow-y-auto` (linia 3407).
            Position fixed + viewport coords liczone z Quill getBounds()
            obchodzi to całkowicie. */}

      </div>

      {/* Stopka jest częścią treści odpowiedzi. Nie jest przyklejona do
          dolnej krawędzi, więc pojawia się dokładnie pod edytowanym mailem. */}
      {activeSignature && (
        <div
          className="px-4 py-2 text-xs text-text-muted select-none pointer-events-none opacity-75 border-t border-border-subtle bg-surface-raised/30"
          title="Stopka doklejana automatycznie - edytuj w Ustawieniach wiadomości"
        >
          <div
            className="email-body-sandbox"
            dangerouslySetInnerHTML={{ __html: activeSignature.html }}
          />
        </div>
      )}

      {/* /scroll wrapper */}
      </div>

      {/* Send bar with AI & template icons on the right */}
      <div className="flex-shrink-0 flex items-center gap-2 px-4 py-3 border-t border-border-subtle bg-surface-raised/40">
        <Button
          onClick={sendMessage}
          loading={sendingMessage}
          disabled={sendingMessage || composerAttaching || !composeData.to || !composeData.bodyHtml || composeData.bodyHtml === '<p><br></p>' || (isReply && !replyMatchesSelection(composerOwnerId || '', selectedMessage?.id))}
          leftIcon={<Send />}
        >
          {sendingMessage ? 'Wysyłam...' : 'Wyślij'}
        </Button>
        <Button
          onClick={closeCompose}
          variant="ghost"
        >
          Anuluj
        </Button>
        {aiError && (
          <span className="text-xs text-danger flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {aiError}
          </span>
        )}

        {/* Right side: AI + Mic + Attach + Coupon + Templates icons */}
        <div className="ml-auto flex items-center gap-1">
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isReply && selectedMessage && aiProvider) {
                  setShowAiMenu(!showAiMenu);
                }
              }}
              disabled={aiLoading || (!isReply) || !aiProvider}
              title={!aiProvider ? 'Skonfiguruj dostawcę AI w ustawieniach' : 'Opcje AI'}
              className="p-2 rounded-lg text-text-muted hover:text-accent-primary hover:bg-accent-primary/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
            </button>
            {showAiMenu && (
              <div className="absolute right-0 bottom-full mb-1 w-52 bg-surface-raised border border-border rounded-xl shadow-2xl z-50 overflow-hidden" onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => { setShowAiMenu(false); generateAiReply(); }}
                  className="w-full px-3 py-2.5 text-left text-xs text-text-secondary hover:bg-surface-hover/50 flex items-center gap-2 transition-colors"
                >
                  <Sparkles className="w-3.5 h-3.5 text-accent-primary" />
                  Wygeneruj odpowiedź
                </button>
                <button
                  onClick={() => {
                    setShowAiMenu(false);
                    setSuggestionContext({ type: 'compose', messageId: selectedMessage?.id });
                    // M2: Restore last suggestion from ref
                    setSuggestionText(selectedMessage?.id ? (lastSuggestionsRef.current.get(selectedMessage.id) || '') : '');
                    setShowSuggestionPopup(true);
                  }}
                  className="w-full px-3 py-2.5 text-left text-xs text-text-secondary hover:bg-surface-hover/50 flex items-center gap-2 transition-colors"
                >
                  <MessageSquare className="w-3.5 h-3.5 text-info" />
                  Zasugeruj odpowiedź
                </button>
              </div>
            )}
          </div>
          {composeTranscribing ? (
            <div className="p-2 text-info" title="Transkrybuję...">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : isComposeRecording ? (
            (() => {
              const secondsLeft = Math.max(0, VOICE_RECORDING_LIMIT_SECONDS - composeRecordingTime);
              const isNearLimit = secondsLeft <= VOICE_RECORDING_WARN_SECONDS_LEFT;
              return (
                <button
                  onClick={stopComposeRecording}
                  className={`flex items-center gap-1.5 px-2 py-2 rounded-lg transition-all animate-pulse ${
                    isNearLimit
                      ? 'bg-danger/30 text-danger hover:bg-danger/40 ring-1 ring-danger/60'
                      : 'bg-danger/20 text-danger hover:bg-danger/30'
                  }`}
                  title={`Nagrywanie ${composeRecordingTime}s / ${VOICE_RECORDING_LIMIT_SECONDS}s — kliknij by zatrzymać`}
                >
                  <MicOff className="w-4 h-4" />
                  <span className="text-[10px] font-semibold tabular-nums">
                    {composeRecordingTime}s / {VOICE_RECORDING_LIMIT_SECONDS}s
                  </span>
                </button>
              );
            })()
          ) : (
            <button
              onClick={startComposeRecording}
              title="Nagraj głos (transkrypcja do tekstu)"
              className="p-2 rounded-lg text-text-muted hover:text-info hover:bg-info-bg transition-all"
            >
              <Mic className="w-4 h-4" />
            </button>
          )}
          {/* C3: załączniki — hidden input + paperclip button (zaraz po mikrofonie) */}
          <input
            ref={composerFileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) handleAttachFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => composerFileInputRef.current?.click()}
            disabled={composerAttaching || composerAttachments.length >= 5}
            title={composerAttachments.length >= 5 ? 'Maksymalnie 5 załączników' : 'Dodaj załącznik (możesz też przeciągnąć plik lub wkleić Ctrl+V)'}
            className="p-2 rounded-lg text-text-muted hover:text-accent-primary hover:bg-accent-primary/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {composerAttaching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          </button>
          <button
            onClick={() => { setCouponForm(f => ({ ...f, code: generateCouponCode() })); setShowCouponModal(true); }}
            title="Wygeneruj kupon rabatowy"
            className="p-2 rounded-lg text-text-muted hover:text-accent-primary hover:bg-accent-primary/10 transition-all"
          >
            <Tag className="w-4 h-4" />
          </button>
          {templateApplying && (
            <div className="flex items-center gap-1 text-xs text-accent-primary">
              <Loader2 className="w-3 h-3 animate-spin" /> AI...
            </div>
          )}
          <div className="relative">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              title="Szablony wiadomości"
              className="p-2 rounded-lg text-text-muted hover:text-accent-primary hover:bg-accent-primary/10 transition-all"
            >
              <FileText className="w-4 h-4" />
            </button>
            {/* Templates dropdown */}
            {showTemplates && (
              <div className="absolute right-0 bottom-full mb-1 w-80 bg-surface-raised border border-border rounded-xl shadow-2xl z-50 overflow-hidden">
                {renderTemplateChoices(applyTemplate)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Draft save confirmation dialog */}
      {showDraftConfirm && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 rounded-xl backdrop-blur-sm">
          <div className="bg-surface-raised border border-border rounded-xl p-5 shadow-2xl max-w-sm w-full mx-4">
            <p className="text-sm text-text-primary font-medium mb-4 text-center">Zapisać wiadomość jako szkic?</p>
            <div className="flex items-center gap-2">
              <Button
                onClick={saveDraft}
                disabled={savingDraft}
                loading={savingDraft}
                size="sm"
                leftIcon={<Save />}
              >
                Zapisz szkic
              </Button>
              <Button onClick={discardDraft} variant="ghost" size="sm" className="text-danger hover:text-danger">
                Odrzuć
              </Button>
              <Button onClick={() => setShowDraftConfirm(false)} variant="ghost" size="sm">
                Anuluj
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      className="fixed left-0 right-0 bottom-0 z-[40] flex overflow-hidden bg-surface-base transition-[top] duration-slow ease-smooth"
      style={{ top: isFullscreen ? '0px' : '80px' }}
    >
      {/* === COLUMN 1: Folders sidebar === */}
      {/* #R5 - explicit transition-[width] z brandingowym duration-slow (400ms)
          i ease-smooth żeby zwijanie/rozwijanie sidebara było wyraźnie
          animowane. Wcześniej `transition-all duration-300` był za subtelny. */}
      <div className={`relative flex-shrink-0 transition-[width] duration-slow ease-smooth overflow-hidden ${(inboxSidebarCollapsed || isFullscreen) ? 'w-14' : 'w-56'}`}>
        <InboxSidebar
          /* #R3-3 follow-up - w fullscreen używamy istniejącego mechanizmu zwijania
              sidebara (przekazujemy collapsed=true do komponentu) zamiast tylko CSS
              w-14. Bez tego tekst sidebara był obcinany i wyglądał chaotycznie.
              #R7-4 - w fullscreen NIE przekazujemy onToggle - sidebar zawsze
              zwinięty, button "ZWIŃ MENU" znika (i tak nie miałby sensu). */
          collapsed={inboxSidebarCollapsed || isFullscreen}
          onToggle={isFullscreen ? undefined : () => setInboxSidebarCollapsed(!inboxSidebarCollapsed)}
          onFullscreenToggle={() => { void toggleFullscreen(); }}
          isFullscreen={isFullscreen}
          stores={stores}
          storesLoadFailed={storesLoadFailed}
          selectedStoreId={selectedStoreId}
          onStoreChange={handleStoreChange}
          onStoresReorder={handleStoresReorder}
          selection={{ virtual: selectedImapFolderId ? null : folderFilter, imapFolderId: selectedImapFolderId }}
          onSelectionChange={(next) => {
            if (next.imapFolderId) {
              setSelectedImapFolderId(next.imapFolderId);
              // Przy konkretnym folderze wywalamy virtualny filter do stałej etykiety
              // "custom" — nie używana jako filtr, ale dla UI conditional tabs wartość
              // 'inbox' byłaby myląca. Ustawiamy na pusty string rzutowany na FolderFilter.
              setFolderFilter('inbox');
              persistMailboxSelection('inbox', next.imapFolderId);
            } else {
              setSelectedImapFolderId(null);
              const nextView = (next.virtual as FolderFilter) || 'inbox';
              setFolderFilter(nextView);
              persistMailboxSelection(nextView);
            }
            setActiveLabelFilter(null);
            setSelectedMessage(null);
            setComposeMode('none');
            // #2 - reset filtra "Rozwiązane / Pending / Ważne" przy zmianie folderu.
            // User skarżył się że widzi pustą skrzynkę bo z poprzedniego folderu
            // został aktywny statusFilter (np. 'resolved') - filter persisted
            // niewidocznie i wyglądało jak brakujące maile.
            setStatusFilter('all');
          }}
          folderCounts={stats?.folderCounts || null}
          activeLabelFilter={activeLabelFilter}
          onLabelFilterChange={(labelId) => {
            if (labelId) {
              setActiveLabelFilter(labelId);
              setFolderFilter('inbox');
              setSelectedImapFolderId(null);
              persistMailboxSelection('inbox');
            }
            else { setActiveLabelFilter(null); }
            setSelectedMessage(null);
          }}
          labels={labels}
          onNewMessage={startNewMessage}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          reviewQueueCount={stats?.reviewQueueCount}
          resolvedCount={stats?.statusCounts?.resolved}
          badgeDisplay={badgeDisplay}
        />
      </div>

      {/* === COLUMN 2: Message list === */}
      <div className={`${selectedMessage ? 'w-80' : 'flex-1'} flex-shrink-0 border-r border-border-subtle flex flex-col overflow-hidden transition-all`}>
        {/* Source filter + Search */}
        <div className="flex items-center gap-2 px-3 border-b border-border-subtle flex-shrink-0 h-[52px]">
          <div className="flex items-center gap-0.5 bg-surface-raised/50 p-0.5 rounded-lg border border-border-subtle flex-shrink-0">
            {([
              { key: 'email' as SourceFilter, icon: Mail, count: stats?.emailUnread },
              { key: 'facebook' as SourceFilter, icon: Facebook, count: stats?.fbUnread },
            ]).map(tab => (
              <button
                key={tab.key}
                onClick={() => { setSourceFilter(tab.key); }}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                  sourceFilter === tab.key
                    ? 'bg-accent-primary/20 text-accent-primary'
                    : 'text-text-muted hover:text-text-primary hover:bg-surface-hover/50'
                }`}
                title={tab.key === 'email' ? 'E-mail' : 'Facebook'}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.count != null && tab.count > 0 && (
                  <span className="text-[10px] text-info">{tab.count}</span>
                )}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); }}
              placeholder="Szukaj we wszystkich folderach..."
              className="w-full bg-surface-raised/50 text-sm text-text-primary border border-border-subtle rounded-lg pl-8 pr-8 py-1.5 outline-none focus:border-accent-primary/30 placeholder-text-disabled"
            />
            <button
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
                showAdvancedSearch ? 'text-accent-primary' : 'text-text-disabled hover:text-text-primary'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Conversation status tabs — only mapped conversation statuses. */}
        {folderFilter === 'inbox' && stats?.mappedStatuses?.some(status => ['pending', 'important'].includes(status)) && (
          <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border-subtle flex-shrink-0 bg-surface-raised/20">
            {([
              { key: 'pending', label: 'Oczekujące', icon: Clock, color: 'text-warning' },
              { key: 'important', label: 'Ważne', icon: AlertTriangle, color: 'text-danger' },
            ] as const).filter(tab => stats.mappedStatuses!.includes(tab.key)).map(tab => {
              const count = stats?.statusCounts?.[tab.key];
              return (
                <button
                  key={tab.key}
                  onClick={() => { setStatusFilter(statusFilter === tab.key ? 'all' : tab.key); }}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-all ${
                    statusFilter === tab.key
                      ? 'bg-surface-hover/60 text-text-primary'
                      : 'text-text-muted hover:text-text-primary hover:bg-surface-hover/50'
                  }`}
                >
                  <tab.icon className={`w-3 h-3 ${statusFilter === tab.key ? tab.color : ''}`} />
                  {tab.label}
                  {count != null && count > 0 && (
                    <span className={`text-[10px] px-1 rounded ${
                      statusFilter === tab.key ? 'bg-surface-hover/60' : 'bg-surface-hover/50 text-text-disabled'
                    }`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Advanced search (dropdown) */}
        {showAdvancedSearch && (
          <div className="p-3 bg-surface-raised/50 border-b border-border-subtle space-y-2 flex-shrink-0 animate-fade-in">
            <div className="grid grid-cols-2 gap-2">
              <Input type="text" value={advSearch.from} onChange={e => setAdvSearch(p => ({ ...p, from: e.target.value }))} placeholder="Od (nadawca)" />
              <Input type="text" value={advSearch.to} onChange={e => setAdvSearch(p => ({ ...p, to: e.target.value }))} placeholder="Do (odbiorca)" />
              <Input type="text" value={advSearch.subject} onChange={e => setAdvSearch(p => ({ ...p, subject: e.target.value }))} placeholder="Temat" />
              <Input type="date" value={advSearch.dateFrom} onChange={e => setAdvSearch(p => ({ ...p, dateFrom: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={applyAdvancedSearch} size="sm" leftIcon={<Search />}>Szukaj</Button>
              <Button onClick={clearAdvancedSearch} variant="ghost" size="sm">Wyczyść</Button>
              <label className="flex items-center gap-1 text-[11px] text-text-secondary ml-auto cursor-pointer">
                <input type="checkbox" checked={advSearch.hasAttachment} onChange={e => setAdvSearch(p => ({ ...p, hasAttachment: e.target.checked }))} className="rounded border-border w-3 h-3" />
                <Paperclip className="w-3 h-3" />
              </label>
              <label className="flex items-center gap-1 text-[11px] text-text-secondary cursor-pointer">
                <input type="checkbox" checked={advSearch.isUnread} onChange={e => setAdvSearch(p => ({ ...p, isUnread: e.target.checked }))} className="rounded border-border w-3 h-3" />
                <EyeOff className="w-3 h-3" />
              </label>
            </div>
          </div>
        )}

        {/* Bulk actions widoczne od 1 zaznaczonego maila — niezależnie od tego
            czy selectionMode został włączony przyciskiem "Zaznacz" czy Cmd+klik. */}
        {selectedIds.size >= 1 && (
          <div className="flex items-center gap-0.5 px-1.5 py-1 bg-surface-raised/50 border-b border-border-subtle flex-shrink-0">
            <span
              className="inline-flex items-center justify-center min-w-[22px] h-5 px-1 mx-0.5 rounded-[5px] bg-accent-primary/20 text-[11px] font-semibold text-accent-primary tabular-nums leading-none self-center"
              title={`Zaznaczono ${selectedIds.size} ${selectedIds.size === 1 ? 'wiadomość' : 'wiadomości'}`}
            >
              {selectedIds.size}
            </span>
            {isInTrashView() ? (
              // Kosz: tylko Przywróć + Usuń trwale (expunge — backend dwustopniowe delete).
              // Wcześniej ten warunek sprawdzał tylko `folderFilter === 'trash'` (wirtualny
              // Kosz), więc user w IMAP folderze Kosz z drzewa "Wszystkie foldery" widział
              // NORMALNY toolbar z "Usuń" → soft-delete dialog → bezsensowne.
              <>
                <button onClick={() => bulkAction('restore')} className="btn-ghost text-xs px-1.5 py-0.5 text-success" title="Przywróć do skrzynki"><RotateCcw className="w-3.5 h-3.5" /></button>
                <button onClick={() => bulkAction('trash')} className="btn-ghost text-xs px-1.5 py-0.5 text-danger" title="Usuń trwale"><Trash2 className="w-3.5 h-3.5" /></button>
              </>
            ) : (
              <>
                {/* Grupa 1: Zmiana folderu */}
                <div className="relative">
                  <button
                    onClick={() => setShowBulkMoveMenu(v => !v)}
                    className={`btn-ghost text-xs px-1.5 py-0.5 ${showBulkMoveMenu ? 'bg-accent-primary/20 text-accent-primary' : ''}`}
                    title="Przenieś do folderu"
                  >
                    <FolderTree className="w-3.5 h-3.5" />
                  </button>
                  {showBulkMoveMenu && (() => {
                    // Folder picker dla bulk — bierzemy foldery pierwszego configu
                    // (standardowy setup = jedna skrzynka aktywna w kontekście).
                    const cfgFolders = imapFoldersFlat.filter(f => f.configId === imapFoldersFlat[0]?.configId);
                    const SYSTEM_ORDER = ['\\Inbox', '\\Archive', '\\Trash'];
                    const systemFolders = cfgFolders
                      .filter(f => SYSTEM_ORDER.includes(f.specialUse ?? ''))
                      .sort((a, b) =>
                        SYSTEM_ORDER.indexOf(a.specialUse ?? '') - SYSTEM_ORDER.indexOf(b.specialUse ?? '')
                      );
                    const customFolders = cfgFolders.filter(f =>
                      !f.specialUse || !['\\Inbox', '\\Sent', '\\Drafts', '\\Archive', '\\Trash', '\\Junk'].includes(f.specialUse)
                    );
                    const systemLabelFor = (sp: string | null) =>
                      sp === '\\Inbox' ? 'Odebrane' : sp === '\\Archive' ? 'Archiwum' : sp === '\\Trash' ? 'Kosz' : '';
                    return (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowBulkMoveMenu(false)} />
                        <div className="absolute left-0 top-full mt-1 z-50 w-60 max-h-[400px] overflow-y-auto bg-surface-raised border border-border rounded-xl shadow-xl py-1.5">
                          <div className="px-3 pt-1 pb-0.5 text-[9px] font-semibold text-text-disabled uppercase tracking-wider">Foldery systemowe</div>
                          {systemFolders.length === 0 && (
                            <div className="px-3 py-1.5 text-[11px] text-text-disabled italic">brak</div>
                          )}
                          {systemFolders.map(f => (
                            <button
                              key={f.id}
                              onClick={() => bulkMoveToFolder(f.id)}
                              className="w-full text-left px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-hover/50 flex items-center gap-2"
                            >
                              {f.specialUse === '\\Inbox' && <Inbox className="w-3.5 h-3.5 text-accent-primary" />}
                              {f.specialUse === '\\Archive' && <Archive className="w-3.5 h-3.5 text-text-secondary" />}
                              {f.specialUse === '\\Trash' && <Trash2 className="w-3.5 h-3.5 text-danger" />}
                              <span className="flex-1">{systemLabelFor(f.specialUse)}</span>
                            </button>
                          ))}
                          {customFolders.length > 0 && (
                            <>
                              <div className="border-t border-border-subtle my-1" />
                              <div className="px-3 pt-1 pb-0.5 text-[9px] font-semibold text-text-disabled uppercase tracking-wider">Inne foldery</div>
                              {customFolders.map(f => (
                                <button
                                  key={f.id}
                                  onClick={() => bulkMoveToFolder(f.id)}
                                  className="w-full text-left px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-hover/50 flex items-center gap-2"
                                >
                                  <FolderTree className="w-3.5 h-3.5 text-text-muted" />
                                  <span className="truncate flex-1">{f.path}</span>
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Separator */}
                <div className="w-px h-5 bg-surface-hover/60 mx-0.5" />

                {/* Grupa 2: Statusy konwersacji */}
                <button onClick={() => bulkAction('resolve')} className="btn-ghost text-xs px-1.5 py-0.5 text-success" title="Rozwiąż"><CheckCircle2 className="w-3.5 h-3.5" /></button>
                <button onClick={() => bulkAction('pending')} className="btn-ghost text-xs px-1.5 py-0.5 text-warning" title="Oczekujące"><Clock className="w-3.5 h-3.5" /></button>
                <button onClick={() => bulkAction('important')} className="btn-ghost text-xs px-1.5 py-0.5 text-danger" title="Ważne"><AlertTriangle className="w-3.5 h-3.5" /></button>

                {/* Separator */}
                <div className="w-px h-5 bg-surface-hover/60 mx-0.5" />

                {/* Grupa 3: Stan przeczytania */}
                <button onClick={() => bulkAction('read')} className="btn-ghost text-xs px-1.5 py-0.5" title="Przeczytane"><Eye className="w-3.5 h-3.5" /></button>
                <button onClick={() => bulkAction('unread')} className="btn-ghost text-xs px-1.5 py-0.5" title="Nieprzeczytane"><EyeOff className="w-3.5 h-3.5" /></button>

                {/* Separator */}
                <div className="w-px h-5 bg-surface-hover/60 mx-0.5" />

                {/* Grupa 4: Archiwum / Kosz */}
                <button onClick={() => bulkAction('archive')} className="btn-ghost text-xs px-1.5 py-0.5" title="Archiwizuj"><Archive className="w-3.5 h-3.5" /></button>
                <button onClick={() => bulkAction('trash')} className="btn-ghost text-xs px-1.5 py-0.5 text-danger" title="Usuń"><Trash2 className="w-3.5 h-3.5" /></button>
              </>
            )}
          </div>
        )}

        {/* Gmail-style banner: "Zaznaczono X widocznych. Zaznacz wszystkie Y w folderze" */}
        {selectedIds.size >= 1 && !selectAllInFolder && selectedIds.size === messages.length && totalInFolder > messages.length && (
          <div className="flex items-center justify-center gap-2 px-3 py-2 bg-accent-primary/5 border-b border-accent-primary/20 text-xs text-text-secondary flex-shrink-0">
            <span>
              Zaznaczono {messages.length} widocznych{' '}
              {messages.length === 1 ? 'wiadomość' : messages.length < 5 ? 'wiadomości' : 'wiadomości'}.
            </span>
            <button
              onClick={handleSelectAllInFolder}
              disabled={selectAllLoading}
              className="text-accent-primary hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selectAllLoading ? 'Ładowanie...' : `Zaznacz wszystkie ${totalInFolder} w folderze`}
            </button>
          </div>
        )}
        {selectAllInFolder && (
          <div className="flex items-center justify-center gap-2 px-3 py-2 bg-accent-primary/10 border-b border-accent-primary/30 text-xs text-text-primary flex-shrink-0">
            <span>
              Zaznaczono wszystkie <strong className="text-accent-primary tabular-nums">{selectedIds.size}</strong>{' '}
              {selectedIds.size === 1 ? 'wiadomość' : 'wiadomości'} w folderze.
            </span>
            <button
              onClick={() => { setSelectedIds(new Set()); setSelectAllInFolder(false); }}
              className="text-text-secondary hover:text-text-primary hover:underline font-medium"
            >
              Odznacz wszystkie
            </button>
          </div>
        )}

        {/* Review Queue view */}
        {debouncedSearchQuery && <p className="px-4 py-2 text-xs text-text-muted border-b border-border-subtle">Wyniki ze wszystkich folderów tego sklepu</p>}
        {folderFilter === 'review_queue' && !debouncedSearchQuery ? (
          <div className="flex-1 overflow-y-auto">
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border-subtle bg-surface-raised/20">
              <Bot className="w-4 h-4 text-info" />
              <span className="text-sm font-medium text-text-primary">Drafty AI — kolejka do przeglądu</span>
              <span className="text-[11px] text-text-muted ml-auto">{reviewQueue.length} {reviewQueue.length === 1 ? 'draft' : 'draftów'}</span>
            </div>
            {aiDailyReport && aiDailyReport.totalExceptions > 0 && (
              <div className="grid grid-cols-2 gap-1.5 px-3 py-2 border-b border-border-subtle bg-warning/5 text-[10px]">
                <span className="inline-flex items-center gap-1 text-text-secondary">
                  <Clock className="w-3 h-3 text-warning" /> Po terminie: {aiDailyReport.overdueResponses}
                </span>
                <span className="inline-flex items-center gap-1 text-text-secondary">
                  <AlertTriangle className="w-3 h-3 text-danger" /> Wysokie ryzyko: {aiDailyReport.highRiskOpen}
                </span>
                <span className="inline-flex items-center gap-1 text-text-secondary">
                  <AlertCircle className="w-3 h-3 text-danger" /> Błędy: {aiDailyReport.aiErrors + aiDailyReport.sendingErrors}
                </span>
                <span className="inline-flex items-center gap-1 text-text-secondary" title={aiDailyReport.knowledgeSuggestions.join('\n')}>
                  <BookOpen className="w-3 h-3 text-accent-primary" /> Braki wiedzy: {aiDailyReport.knowledgeGaps}
                </span>
              </div>
            )}
            {/* Bulk selection bar for drafts */}
            <div className="flex items-center gap-2 px-3 py-1.5 text-[11px] text-text-muted border-b border-border-subtle">
              {draftSelectionMode ? (
                <>
                  <input type="checkbox" checked={selectedDraftIds.size === reviewQueue.length && reviewQueue.length > 0} onChange={toggleSelectAllDrafts} className="rounded border-border w-3 h-3" />
                  <span>Zaznacz wszystkie</span>
                  {selectedDraftIds.size > 0 && (
                    <span className="text-text-secondary ml-1">({selectedDraftIds.size})</span>
                  )}
                  <div className="ml-auto flex items-center gap-1">
                    {selectedDraftIds.size > 0 && (
                      <>
                        <button onClick={bulkApproveDrafts} disabled={Boolean(reviewActionLoading)} className="btn-ghost text-[11px] px-2 py-0.5 text-success hover:bg-success/10 flex items-center gap-1">
                          {reviewActionLoading === 'bulk' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                          Zatwierdź i wyślij
                        </button>
                        <button onClick={bulkRejectDrafts} disabled={reviewActionLoading === 'bulk'} className="btn-ghost text-[11px] px-2 py-0.5 text-danger hover:bg-danger/10 flex items-center gap-1">
                          <Trash2 className="w-3 h-3" />
                          Odrzuć zaznaczone
                        </button>
                      </>
                    )}
                    <button onClick={() => { setDraftSelectionMode(false); setSelectedDraftIds(new Set()); }} className="text-[11px] text-text-muted hover:text-text-primary transition-colors px-1">Anuluj</button>
                  </div>
                </>
              ) : (
                <button onClick={() => setDraftSelectionMode(true)} className="text-[11px] text-text-muted hover:text-text-primary transition-colors">Zaznacz</button>
              )}
            </div>
            {reviewQueueLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
              </div>
            ) : reviewQueue.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-text-muted">
                <Bot className="w-10 h-10 mb-3 opacity-30" />
                <p className="text-sm">Brak draftów do przeglądu</p>
                <p className="text-xs text-text-disabled mt-1">Nowe drafty pojawią się po przetworzeniu wiadomości</p>
              </div>
            ) : (
              <div className="px-4 pt-4 pb-4 space-y-1.5">
                {(() => {
                  let lastDayKey: string | null = null;
                  return reviewQueue.map(item => {
                  const isExpanded = expandedDraftId === item.id;
                  const categoryLabel =
                    item.message.aiCategory === 'tracking' ? 'Tracking' :
                    item.message.aiCategory === 'return' ? 'Zwrot' :
                    item.message.aiCategory === 'complaint' ? 'Reklamacja' :
                    item.message.aiCategory === 'question' ? 'Pytanie' :
                    item.message.aiCategory === 'payment' ? 'Płatność' :
                    item.message.aiCategory === 'order_confirmation' ? 'Zamówienie' :
                    item.message.aiCategory === 'spam' ? 'Spam' :
                    item.message.aiCategory === 'other' ? 'Inne' :
                    item.message.aiCategory || '—';
                  // Separator dnia — renderujemy gdy dzień się zmienia w iteracji
                  const itemDate = new Date(item.createdAt);
                  const itemDayKey = getDayKey(itemDate);
                  const showDaySeparator = itemDayKey !== lastDayKey;
                  lastDayKey = itemDayKey;
                  return (
                    <Fragment key={item.id}>
                      {showDaySeparator && (
                        <div className="flex items-center gap-3 pt-3 pb-2 first:pt-0">
                          <div className="flex-1 h-px bg-surface-hover/60" />
                          <span className="inline-flex items-center text-xs text-text-secondary bg-surface-hover/60 border border-border px-3 py-1 rounded-md whitespace-nowrap">
                            {formatDayLabel(itemDate)}
                          </span>
                          <div className="flex-1 h-px bg-surface-hover/60" />
                        </div>
                      )}
                    <div className={`rounded-xl border transition-all ${
                      isExpanded
                        ? 'border-accent-primary/40 bg-surface-raised/70 shadow-[0_0_0_1px_var(--border-accent)]'
                        : 'border-border bg-surface-raised/40 hover:bg-surface-raised/60 hover:border-border-strong'
                    }`}>
                      <div className="flex items-center gap-2">
                        {draftSelectionMode && (
                          <div className="flex-shrink-0 pl-3">
                            <input
                              type="checkbox"
                              checked={selectedDraftIds.has(item.id)}
                              onChange={() => toggleDraftSelect(item.id)}
                              className="rounded border-border w-3.5 h-3.5"
                            />
                          </div>
                        )}
                      <button
                        onClick={() => setExpandedDraftId(isExpanded ? null : item.id)}
                        className="w-full text-left px-4 py-4"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-info-bg flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-info" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-text-primary truncate">{item.message.fromName || item.message.from}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border flex-shrink-0 ${
                                item.message.aiCategory === 'tracking' ? 'text-info bg-info-bg border-info-border' :
                                item.message.aiCategory === 'return' ? 'text-warning bg-warning-bg border-warning-border' :
                                item.message.aiCategory === 'complaint' ? 'text-danger bg-danger-bg border-danger-border' :
                                item.message.aiCategory === 'question' ? 'text-success bg-success-bg border-success-border' :
                                item.message.aiCategory === 'payment' ? 'text-warning bg-warning-bg border-warning-border' :
                                item.message.aiCategory === 'order_confirmation' ? 'text-accent-primary bg-accent-primary/10 border-accent-primary/30' :
                                'text-text-secondary bg-surface-hover border-border'
                              }`}>{categoryLabel}</span>
                              {item.confidence != null && (
                                <span className="text-[10px] text-text-muted flex-shrink-0">{Math.round(item.confidence * 100)}%</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="text-[11px] text-text-muted truncate">{extractEmail(item.message.from) || item.message.from}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(extractEmail(item.message.from) || item.message.from, 'Skopiowano email');
                                }}
                                className="p-0.5 rounded text-text-disabled hover:text-accent-primary hover:bg-surface-muted flex-shrink-0"
                                title="Kopiuj email"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {/* #R2-1 - quick actions: ikony POWIĄZANYCH FOLDERÓW
                                (tylko te które user ma zmapowane w ustawieniach
                                Wiadomości dla skrzynki tego draftu). Brak mappingu
                                = ikonka ukryta. */}
                            {hasStatusMappingFor(item.message.imapConfigId, 'resolved') && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); quickChangeStatusDraft(item, 'resolved', 'Oznaczono jako rozwiązane'); }}
                                disabled={reviewActionLoading === item.id}
                                className="p-1.5 rounded-md text-text-muted hover:text-success hover:bg-success/10 transition-colors disabled:opacity-30"
                                title="Przenieś do rozwiązanych"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {hasStatusMappingFor(item.message.imapConfigId, 'pending') && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); quickChangeStatusDraft(item, 'pending', 'Oznaczono jako oczekujące'); }}
                                disabled={reviewActionLoading === item.id}
                                className="p-1.5 rounded-md text-text-muted hover:text-warning hover:bg-warning-bg transition-colors disabled:opacity-30"
                                title="Oznacz jako oczekujące - przeniesie do zmapowanego folderu"
                              >
                                <Clock className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {hasStatusMappingFor(item.message.imapConfigId, 'important') && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); quickChangeStatusDraft(item, 'important', 'Oznaczono jako ważne'); }}
                                disabled={reviewActionLoading === item.id}
                                className="p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-danger/10 transition-colors disabled:opacity-30"
                                title="Oznacz jako ważne - przeniesie do zmapowanego folderu"
                              >
                                <AlertTriangle className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); quickTrashDraft(item); }}
                              disabled={reviewActionLoading === item.id}
                              className="p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-danger/10 transition-colors disabled:opacity-30"
                              title="Przenieś do kosza i odrzuć draft"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            <span className="inline-flex items-center text-[10px] text-text-secondary bg-surface-hover/60 border border-border px-2 py-1 rounded-md tabular-nums ml-1">
                              {new Date(item.createdAt).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      </button>
                      </div>
                      {isExpanded && (
                        <div className="px-3 pb-3 space-y-3 animate-fade-in">
                          <div className="flex gap-3">
                            <div className="flex-1 space-y-3 min-w-0">
                              {/* Subject */}
                              <div className="bg-surface-raised/50 rounded-lg p-3 border border-border-subtle">
                                <div className="flex items-center gap-1.5 mb-5">
                                  <Tag className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
                                  <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Tytuł wiadomości</p>
                                </div>
                                <p className="text-sm text-text-primary break-words leading-relaxed">{item.message.subject || '(brak tematu)'}</p>
                              </div>
                              {/* Original message — fetch pełnej treści z IMAP (nie snippet) */}
                              <div className="bg-surface-raised/50 rounded-lg p-3 border border-border-subtle">
                                <div className="flex items-center gap-1.5 mb-5">
                                  <Mail className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
                                  <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Wiadomość klienta</p>
                                </div>
                                {(() => {
                                  const body = draftBodies[item.message.id];
                                  if (!body || body.loading) {
                                    return (
                                      <div className="flex items-center gap-2 text-sm text-text-muted py-2">
                                        <Loader2 className="w-3 h-3 animate-spin" /> Ładuję treść…
                                      </div>
                                    );
                                  }
                                  const rawText = body.text || item.message.snippet || '(brak treści)';
                                  const normalized = rawText.replace(/\n{3,}/g, '\n\n').trim();
                                  const { original, quoted } = splitQuotedPlainText(normalized);
                                  return (
                                    <>
                                      <p className="text-sm text-text-primary whitespace-pre-wrap max-h-60 overflow-y-auto leading-relaxed">{original}</p>
                                      {quoted && <QuotedContentToggle quotedText={quoted} />}
                                    </>
                                  );
                                })()}
                              </div>
                              {/* AI Draft */}
                              <div className="bg-info-bg rounded-lg p-3 border border-info-border">
                                <div className="flex items-center gap-1.5 mb-5">
                                  <Sparkles className="w-3.5 h-3.5 text-info flex-shrink-0" />
                                  <p className="text-xs font-semibold text-info uppercase tracking-wider">Propozycja AI</p>
                                </div>
                                <div className="text-sm text-text-primary leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: item.draftHtml }} />
                                {/* Podgląd stopki — pokazujemy jak wygląda finalna wiadomość po approve. */}
                                {(() => {
                                  const cfg = inboxConfigs.find(c => (
                                    c.id === item.message.imapConfigId
                                    && c.isActive
                                    && c.signatureHtml
                                  ));
                                  if (!cfg?.signatureHtml) return null;
                                  return (
                                    <div className="mt-3 pt-2 border-t border-info-border">
                                      <p className="text-[10px] uppercase tracking-wider text-text-disabled mb-1">Stopka (doklejana automatycznie)</p>
                                      <div
                                        className="email-body-sandbox text-xs opacity-75"
                                        style={{ marginTop: cfg.signatureMarginTop ?? 10 }}
                                        dangerouslySetInnerHTML={{ __html: cfg.signatureHtml }}
                                      />
                                    </div>
                                  );
                                })()}
                              </div>
                              <ReviewAttachments
                                value={reviewAttachments[item.id] || []}
                                onChange={files => setReviewAttachments(previous => ({ ...previous, [item.id]: files }))}
                                onBusyChange={busy => setReviewUploading(previous => ({ ...previous, [item.id]: busy }))}
                                disabled={Boolean(reviewActionLoading)}
                              />
                              {/* Actions */}
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                  onClick={() => approveReviewItem(item.id)}
                                  disabled={Boolean(reviewActionLoading) || reviewUploading[item.id]}
                                  loading={reviewActionLoading === item.id}
                                  size="sm"
                                  leftIcon={<Check />}
                                >
                                  Zatwierdź i wyślij
                                </Button>
                                <button
                                  onClick={() => {
                                    setSuggestionContext({ type: 'review_queue', reviewItemId: item.id, messageId: item.messageId });
                                    // M2: Restore last suggestion from ref if available
                                    setSuggestionText(lastSuggestionsRef.current.get(item.id) || '');
                                    setShowSuggestionPopup(true);
                                  }}
                                  disabled={reviewActionLoading === item.id}
                                  className="btn-ghost text-xs px-3 py-1.5 text-info hover:bg-info-bg flex items-center gap-1.5"
                                >
                                  <MessageSquare className="w-3 h-3" />
                                  Zasugeruj odpowiedź
                                </button>
                                <button
                                  onClick={() => setReviewTemplateId(item.id)}
                                  disabled={reviewActionLoading === item.id || templateApplying}
                                  title="Szablony wiadomości"
                                  aria-label="Szablony odpowiedzi"
                                  className="btn-ghost text-xs px-3 py-1.5 text-accent-primary hover:bg-accent-primary/10 flex items-center gap-1.5"
                                >
                                  <FileText className="w-3 h-3" />
                                  Szablony
                                </button>
                                <button
                                  onClick={() => openQuickEdit(item)}
                                  disabled={reviewActionLoading === item.id}
                                  className="btn-ghost text-xs px-3 py-1.5 text-accent-primary hover:bg-accent-primary/10 flex items-center gap-1.5"
                                  title="Edytuj treść draftu bez ponownego generowania przez AI"
                                >
                                  <Edit2 className="w-3 h-3" />
                                  Szybka edycja
                                </button>
                                <button
                                  onClick={() => rejectReviewItem(item.id)}
                                  disabled={reviewActionLoading === item.id}
                                  className="btn-ghost text-xs px-3 py-1.5 text-danger hover:bg-danger/10 flex items-center gap-1.5"
                                >
                                  <X className="w-3 h-3" />
                                  Odrzuć
                                </button>
                                <div className="flex items-center gap-1.5 ml-auto">
                                  <button
                                    onClick={() => {
                                      const msgId = item.message.id;
                                      const isCurrent = mailboxRequests.begin('message-detail', selectedStoreId);
                                      messagesApi.getMessage(msgId).then(res => {
                                        if (!isCurrent()) return;
                                        setSelectedMessage(res.data.data || res.data);
                                        setFolderFilter('inbox');
                                        setSelectedImapFolderId(null);
                                        persistMailboxSelection('inbox');
                                      }).catch(() => showToast('Nie znaleziono wiadomości', 'error'));
                                    }}
                                    className="btn-ghost text-xs px-2 py-1.5 flex items-center gap-1.5"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    Otwórz oryginał
                                  </button>
                                </div>
                              </div>
                            </div>
                            {/* Customer info side panel — zawsze widoczny */}
                            <div className="w-56 flex-shrink-0 bg-surface-raised/40 rounded-xl border border-border-subtle p-3 space-y-3">
                              <div className="flex items-center justify-between">
                                <p className="text-xs font-semibold text-text-secondary flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Klient</p>
                              </div>
                                {draftCustomerLoading ? (
                                  <Loader2 className="w-4 h-4 animate-spin text-accent-primary" />
                                ) : draftCustomerData && draftCustomerData.orderCount > 0 ? (
                                  <>
                                    <div className="text-xs space-y-1">
                                      {draftCustomerData.customer.name && <p className="text-text-primary font-medium">{draftCustomerData.customer.name}</p>}
                                      <button onClick={() => { navigator.clipboard.writeText(draftCustomerData.customer.email); showToast('Skopiowano'); }} className="text-accent-primary hover:underline text-xs">{draftCustomerData.customer.email}</button>
                                      {draftCustomerData.customer.phone && (
                                        <button onClick={() => { navigator.clipboard.writeText(draftCustomerData.customer.phone!); showToast('Skopiowano'); }} className="text-text-secondary hover:text-text-primary text-xs block">{draftCustomerData.customer.phone}</button>
                                      )}
                                    </div>
                                    <CustomerOrderHistory
                                      data={draftCustomerData}
                                      statuses={orderStatusesCache}
                                      theme={theme}
                                      onOpenOrder={setOrderModalId}
                            onOpenComplaint={setComplaintModalId}
                                      maxOrders={6}
                                      maxHeightClass="max-h-72"
                                    />
                                  </>
                                ) : (
                                  <p className="text-xs text-text-disabled">Brak danych o kliencie</p>
                                )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    </Fragment>
                  );
                });
                })()}
              </div>
            )}
          </div>
        ) : composeMode === 'new' ? (
          <div className="flex-1 overflow-y-auto p-3">
            {renderComposeEditor(false)}
          </div>
        ) : (
          /* Message items - scrollable */
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Select all row */}
            <div className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 text-[11px] text-text-muted border-b border-border-subtle">
              {selectionMode ? (
                <>
                  <button
                    onClick={toggleSelectAll}
                    className={`px-2.5 py-1 rounded-md border text-xs transition-colors ${
                      selectedIds.size === messages.length && messages.length > 0
                        ? 'border-accent-primary/40 bg-accent-primary/10 text-accent-primary'
                        : 'border-border bg-surface-hover/50 hover:bg-surface-hover text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    Zaznacz wszystkie
                  </button>
                  <button
                    onClick={() => { setSelectionMode(false); setSelectedIds(new Set()); }}
                    className="px-2.5 py-1 rounded-md border border-border bg-surface-hover/50 hover:bg-surface-hover text-xs text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Anuluj
                  </button>
                </>
              ) : (
                <button
                  onClick={() => { setSelectionMode(true); }}
                  className="px-2.5 py-1 rounded-md border border-border bg-surface-hover/50 hover:bg-surface-hover text-xs text-text-secondary hover:text-text-primary transition-colors"
                >
                  Zaznacz
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-5 h-5 animate-spin text-accent-primary" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                {hasEmailConfig === false ? (
                  <EmptyState
                    icon={Mail}
                    title="Brak skonfigurowanych kont e-mail"
                    description="Aby korzystać ze skrzynki, skonfiguruj konto IMAP w ustawieniach."
                    buttonLabel="Przejdź do Ustawień"
                    buttonTo="/messages/settings"
                    buttonIcon={Settings}
                  />
                ) : (
                  <EmptyState
                    icon={Inbox}
                    title="Skrzynka pusta — czysto!"
                    description="Wszystkie wiadomości obsłużone. Czas na kawę."
                    iconColorClass="from-success-bg to-success-bg border-success-border text-success"
                  />
                )}
              </div>
            ) : (
              <Virtuoso
                // #R10-3 - overscroll-behavior-x: contain dodatkowo na liście,
                // żeby touchpad swipe LEFT→RIGHT nie wpadał w macOS back gesture.
                // SwipeableRow ma to samo, ale Virtuoso to wrapper scrollowy
                // i bez tego browser nadal interpretuje horizontal momentum.
                style={{ flex: 1, overscrollBehaviorX: 'contain' }}
                data={messages}
                computeItemKey={(_, msg) => msg.id}
                overscan={10}
                increaseViewportBy={{ top: 400, bottom: 400 }}
                endReached={() => {
                  if (hasNextPage && !isFetchingNextPage && messages.length < totalInFolder) fetchNextPage();
                }}
                components={{
                  Footer: () =>
                    isFetchingNextPage ? (
                      <div className="flex items-center justify-center py-3">
                        <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
                      </div>
                    ) : null,
                }}
                itemContent={(_msgListIndex, msg) => {
                  const isBulkSelected = selectionMode && selectedIds.has(msg.id);
                  const isNewItem = newMessageIds.has(msg.id);
                  const isPendingDelete = pendingDeleteIds.has(msg.id);
                  return (
                <SwipeableRow
                  key={msg.id}
                  enabled={swipePrefs.enabled && !selectionMode}
                  leftAction={swipePrefs.left}
                  rightAction={swipePrefs.right}
                  onLeftSwipe={() => runSwipeAction(msg, swipePrefs.left)}
                  onRightSwipe={() => runSwipeAction(msg, swipePrefs.right)}
                  sharedWheelGuardRef={swipeWheelGuardRef}
                >
                <div
                  data-new-item={isNewItem ? 'true' : undefined}
                  onClick={(e) => {
                    // Ctrl/Cmd + klik — quick multi-select bez włączania trybu
                    // "Zaznacz" ręcznie. Po pierwszym takim kliku automatycznie
                    // wchodzimy w selectionMode, następne cmd/ctrl-kliki dodają
                    // kolejne maile do zaznaczenia. Bulk actions menu uaktywnia
                    // się gdy selectedIds.size >= 2 (selectionMode już true).
                    if (e.metaKey || e.ctrlKey) {
                      if (!selectionMode) setSelectionMode(true);
                      toggleSelect(msg.id, e);
                      return;
                    }
                    // W bulk mode klik w wiersz zaznacza mail (nie otwiera podglądu).
                    if (selectionMode) { toggleSelect(msg.id, e); return; }
                    // Toggle: drugi klik na otwartym mailu zamyka panel podglądu.
                    // Wyjątek: gdy user ma otwarty composer/AI draft (nie chcemy
                    // zgubić wpisanego tekstu kliknięciem w ten sam wiersz).
                    if (selectedMessage?.id === msg.id) {
                      if (composeMode !== 'none' || showAiDraft) return;
                      setSelectedMessage(null);
                      return;
                    }
                    openMessage(msg);
                  }}
                  className={`mx-2 my-1 px-3 py-3 rounded-xl transition-colors ${isPendingDelete ? 'pending-delete' : ''} ${selectionMode ? 'cursor-default' : 'cursor-pointer'} ${
                    isBulkSelected
                      ? 'bg-accent-primary/10 border-2 border-accent-primary'
                      : selectedMessage?.id === msg.id
                      ? 'bg-accent-deep/20 border border-accent-primary/20 shadow-sm'
                      : msg.isRead
                        ? 'hover:bg-surface-raised/60 border border-transparent'
                        : 'bg-surface-raised/40 border border-border-subtle hover:bg-surface-raised/60'
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    {selectionMode && (
                      <div className="flex-shrink-0 mt-1">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(msg.id)}
                          onChange={() => {}}
                          onClick={(e) => toggleSelect(msg.id, e)}
                          className="rounded border-border w-3.5 h-3.5"
                        />
                      </div>
                    )}
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      !msg.isRead
                        ? 'bg-gradient-to-br from-accent-primary/30 to-accent-info/30 border border-accent-primary/20 text-accent-primary'
                        : 'bg-surface-hover/50 border border-border-subtle text-text-muted'
                    }`}>
                      {getInitials(msg.fromName, msg.from)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        {!msg.isRead && <span className="w-2 h-2 rounded-full bg-accent-primary flex-shrink-0" />}
                        <span
                          className={`text-sm truncate [&_mark]:bg-accent-primary/30 [&_mark]:text-accent-primary [&_mark]:rounded-sm [&_mark]:px-0.5 ${!msg.isRead ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}
                          dangerouslySetInnerHTML={{ __html: highlightText(msg.fromName || msg.from, searchHighlightTerms) }}
                        />
                        {/* Badge "Szkic" obok daty po prawej — operator od razu widzi co jest
                            niewysłane, bez skanowania dolnych tagów. Aktywowany w folderze
                            Szkice albo w wątku z niewysłanym draftem (R2-2: hasDraftReply
                            z threadStats - flaga gdy dowolna reply w wątku ma isDraft=true). */}
                        {(msg.isDraft || msg.threadStats?.hasDraftReply) && (
                          <span className="text-[10px] px-1.5 py-0 rounded-full font-medium border text-warning bg-warning-bg border-warning-border inline-flex items-center gap-0.5 flex-shrink-0 ml-auto" title={msg.isDraft ? 'Szkic — wiadomość jeszcze nie wysłana' : 'W wątku jest niewysłany szkic'}>
                            <FileText className="w-2.5 h-2.5" /> Szkic
                          </span>
                        )}
                        <span className={`text-[10px] text-text-disabled flex-shrink-0 ${(msg.isDraft || msg.threadStats?.hasDraftReply) ? '' : 'ml-auto'}`}>
                          {new Date(msg.threadStats?.latestReplyAt || msg.receivedAt).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {msg.subject && (
                        <p
                          className={`text-xs truncate [&_mark]:bg-accent-primary/30 [&_mark]:text-accent-primary [&_mark]:rounded-sm [&_mark]:px-0.5 ${!msg.isRead ? 'text-text-primary' : 'text-text-secondary'}`}
                          dangerouslySetInnerHTML={{ __html: highlightText(msg.subject, searchHighlightTerms) }}
                        />
                      )}
                      {msg.matchSnippet ? (
                        <p
                          className="text-[11px] text-text-muted truncate mt-0.5 [&_mark]:bg-accent-primary/30 [&_mark]:text-accent-primary [&_mark]:rounded-sm [&_mark]:px-0.5"
                          title="Dopasowanie w treści"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.matchSnippet, { ALLOWED_TAGS: ['mark'], ALLOWED_ATTR: [] }) }}
                        />
                      ) : (
                        <p
                          className="text-[11px] text-text-disabled truncate mt-0.5 [&_mark]:bg-accent-primary/30 [&_mark]:text-accent-primary [&_mark]:rounded-sm [&_mark]:px-0.5"
                          dangerouslySetInnerHTML={{ __html: highlightText(msg.threadStats?.latestReplySnippet || msg.snippet || '', searchHighlightTerms) }}
                        />
                      )}
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {/* 1. Source icon (mail/fb) */}
                        {msg.source === 'email' && <Mail className="w-3 h-3 text-text-disabled" />}
                        {msg.source === 'facebook' && <Facebook className="w-3 h-3 text-info" />}
                        {msg.isStarred && (
                          <Star
                            className="w-3 h-3 flex-shrink-0 fill-current text-warning"
                            aria-label="Wyróżniona wiadomość"
                          />
                        )}
                        {/* 2. Attachments */}
                        {msg.hasAttachments && <Paperclip className="w-3 h-3 text-text-disabled" />}
                        {/* 3. Conversation count — total messages in the thread (root + replies) */}
                        {(() => {
                          const replies = msg.threadStats?.totalReplies ?? msg._count?.replies ?? 0;
                          const total = msg.threadStats?.totalMessages ?? replies + 1; // include the root message
                          if (total <= 1) return null;
                          const hasUnread = (msg.threadStats?.unreadReplies ?? 0) > 0;
                          return (
                            <span className={`text-[10px] flex items-center gap-0.5 ${hasUnread ? 'text-accent-primary font-semibold' : 'text-text-disabled'}`} title={hasUnread ? `${msg.threadStats?.unreadReplies} nieprzeczytanych` : `${total} wiadomości w wątku`}>
                              <MessageSquare className="w-3 h-3" /> {total}
                            </span>
                          );
                        })()}
                        {/* 4. Priority indicator */}
                        {msg.aiPriority != null && msg.aiPriority >= 60 && (
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                            msg.aiPriority >= 80 ? 'bg-danger' :
                            msg.aiPriority >= 60 ? 'bg-warning' :
                            ''
                          }`} title={`Priorytet: ${msg.aiPriority}`} />
                        )}
                        {(msg.aiRiskLevel === 'high' || msg.aiRiskLevel === 'critical') && (
                          <span
                            className="text-[10px] px-1.5 py-0 rounded-full font-medium border text-danger bg-danger/10 border-danger/30"
                            title={`Ryzyko AI: ${msg.aiRiskLevel}`}
                          >
                            Ryzyko {msg.aiRiskLevel === 'critical' ? 'krytyczne' : 'wysokie'}
                          </span>
                        )}
                        {msg.aiResponseDeadline && msg.conversationStatus !== 'resolved' && new Date(msg.aiResponseDeadline).getTime() <= Date.now() && (
                          <span className="text-[10px] px-1.5 py-0 rounded-full font-medium border text-warning bg-warning/10 border-warning/30 inline-flex items-center gap-0.5">
                            <Clock className="w-2.5 h-2.5" /> Po terminie
                          </span>
                        )}
                        {(msg.aiSentiment === 'negative' || msg.aiSentiment === 'angry') && (
                          <span className="text-[10px] text-text-muted" title={`Sentyment AI: ${msg.aiSentiment}`}>
                            {msg.aiSentiment === 'angry' ? 'Sentyment: zły' : 'Sentyment: negatywny'}
                          </span>
                        )}
                        {/* 5. AI category badge */}
                        {msg.aiCategory && (
                          <span className={`text-[10px] px-1.5 py-0 rounded-full font-medium border ${
                            msg.aiCategory === 'tracking' ? 'text-info bg-info-bg border-info-border' :
                            msg.aiCategory === 'return' ? 'text-warning bg-warning-bg border-warning-border' :
                            msg.aiCategory === 'complaint' ? 'text-danger bg-danger-bg border-danger-border' :
                            msg.aiCategory === 'question' ? 'text-success bg-success-bg border-success-border' :
                            msg.aiCategory === 'payment' ? 'text-warning bg-warning-bg border-warning-border' :
                            msg.aiCategory === 'order_confirmation' ? 'text-accent-primary bg-accent-primary/10 border-accent-primary/30' :
                            'text-text-secondary bg-surface-hover border-border'
                          }`}>
                            {msg.aiCategory === 'tracking' ? 'Tracking' :
                             msg.aiCategory === 'return' ? 'Zwrot' :
                             msg.aiCategory === 'complaint' ? 'Reklamacja' :
                             msg.aiCategory === 'question' ? 'Pytanie' :
                             msg.aiCategory === 'payment' ? 'Płatność' :
                             msg.aiCategory === 'order_confirmation' ? 'Potwierdzenie' :
                             msg.aiCategory === 'spam' ? 'Spam' :
                             msg.aiCategory === 'other' ? 'Inne' :
                             msg.aiCategory}
                          </span>
                        )}
                        {/* 6. Conversation status badge — only mapped statuses */}
                        {msg.conversationStatus && stats?.mappedStatuses?.includes(msg.conversationStatus) && (
                          <span className={`text-[10px] px-1.5 py-0 rounded-full font-medium inline-flex items-center gap-0.5 ${
                            msg.conversationStatus === 'pending' ? 'bg-warning-bg text-warning' :
                            msg.conversationStatus === 'important' ? 'bg-danger/20 text-danger' :
                            msg.conversationStatus === 'resolved' ? 'bg-success/20 text-success' :
                            ''
                          }`}>
                            {msg.conversationStatus === 'pending' && <><Clock className="w-2.5 h-2.5" /> Oczekuje</>}
                            {msg.conversationStatus === 'important' && <><AlertTriangle className="w-2.5 h-2.5" /> Ważne</>}
                            {msg.conversationStatus === 'resolved' && <><CheckCircle2 className="w-2.5 h-2.5" /> Rozwiązane</>}
                          </span>
                        )}
                        {/* 7. AI review queue badge */}
                        {reviewQueueMsgIds.has(msg.id) && (
                          <span className="text-[10px] px-1.5 py-0 rounded-full font-medium border text-info bg-info-bg border-info-border inline-flex items-center gap-0.5">
                            <Bot className="w-2.5 h-2.5" /> Do przeglądu
                          </span>
                        )}
                        {/* 8. Custom labels — show up to 2, overflow as "+N" */}
                        {msg.labels && msg.labels.length > 0 && (() => {
                          const MAX_VISIBLE = 2;
                          const visible = msg.labels.slice(0, MAX_VISIBLE);
                          const overflow = msg.labels.length - visible.length;
                          return (
                            <>
                              {visible.map(ml => (
                                <span
                                  key={ml.label.id}
                                  className="text-[10px] px-1.5 py-0 rounded-full truncate max-w-[80px]"
                                  style={{ backgroundColor: ml.label.color + '30', color: ml.label.color }}
                                  title={ml.label.name}
                                >
                                  {ml.label.name}
                                </span>
                              ))}
                              {overflow > 0 && (
                                <span
                                  className="text-[10px] px-1.5 py-0 rounded-full bg-surface-muted text-text-secondary"
                                  title={msg.labels.slice(MAX_VISIBLE).map(l => l.label.name).join(', ')}
                                >
                                  +{overflow}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                </SwipeableRow>
                  );
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* === COLUMN 3: Conversation area === */}
      {selectedMessage && (
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Conversation header */}
          <div className="px-4 border-b border-border-subtle flex items-center gap-3 flex-shrink-0 bg-surface-raised/30 h-[52px]">
            <button onClick={() => { setSelectedMessage(null); setShowInfoPanel(false); }} className="btn-ghost p-1.5 lg:hidden">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-text-primary font-semibold truncate text-sm">
                  {selectedMessage.subject || '(brak tematu)'}
                </h3>
                {/* #R2-3 - label aktualnego folderu (ostatnia gałąź imapFolder.path).
                    Pomocne po wyszukiwarce gdzie scope jest globalny - user widzi
                    od razu gdzie mail siedzi. Gdy wątek jest np. w "Reklamacje",
                    pokazuje się "Reklamacje", nie cała ścieżka. */}
                {selectedMessage.imapFolder?.name && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] text-text-muted bg-surface-hover/60 border border-border px-1.5 py-0.5 rounded-md flex-shrink-0 whitespace-nowrap"
                    title={selectedMessage.imapFolder.path}
                  >
                    <FolderOpen className="w-3 h-3" />
                    {selectedMessage.imapFolder.name}
                  </span>
                )}
              </div>
              <div className="relative">
                <p className="text-xs text-text-secondary">
                  Od:{' '}
                  <button onClick={() => setShowSenderCard(v => !v)} className="text-accent-primary hover:underline cursor-pointer">
                    {selectedMessage.fromName || selectedMessage.from}
                  </button>
                  {selectedMessage.source === 'facebook' && <span className="ml-1.5 text-info text-[10px]">[FB]</span>}
                </p>
                {showSenderCard && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowSenderCard(false)} />
                    <div className="absolute left-0 top-full mt-2 z-50 w-80 bg-surface-raised border border-border rounded-xl shadow-2xl p-4 animate-fade-in">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-primary/30 to-accent-info/30 border border-accent-primary/20 flex items-center justify-center text-text-primary font-bold flex-shrink-0">
                          {(selectedMessage.fromName || selectedMessage.from || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          {selectedMessage.fromName && <p className="text-sm font-semibold text-text-primary truncate">{selectedMessage.fromName}</p>}
                          <p className="text-xs text-text-secondary truncate">{selectedMessage.from}</p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-border-subtle flex items-center gap-2">
                        <button onClick={() => { navigator.clipboard.writeText(selectedMessage.from); showToast('Skopiowano'); setShowSenderCard(false); }} className="btn-ghost text-xs px-2.5 py-1.5 flex items-center gap-1.5 whitespace-nowrap">
                          <Copy className="w-3 h-3" /> Kopiuj e-mail
                        </button>
                        <button onClick={() => { setComposeMode('new'); setComposeData({ to: getReplyRecipient(selectedMessage), cc: '', bcc: '', subject: '', bodyHtml: '', parentId: '' }); setShowSenderCard(false); }} className="btn-ghost text-xs px-2.5 py-1.5 flex items-center gap-1.5 whitespace-nowrap">
                          <Mail className="w-3 h-3" /> Nowa wiadomość
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* Labels inline */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {selectedMessage.labels && selectedMessage.labels.slice(0, 2).map(ml => (
                <span key={ml.label.id} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: ml.label.color + '30', color: ml.label.color }}>
                  {ml.label.name}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {(() => {
                // Kosz w szerokim sensie — dowolny z trzech znaczników:
                // widok user'a, fizyczny folder IMAP, albo soft-delete flag.
                // Trzy niezależne źródła prawdy bo race z async IMAP MOVE
                // powoduje że nie zawsze się zgadzają. Jeśli którykolwiek
                // wskazuje na Kosz → pokaż toolbar Kosza.
                const isInTrashView =
                  (!debouncedSearchQuery.trim() && folderFilter === 'trash') ||
                  selectedMessage.imapFolder?.specialUse === '\\Trash' ||
                  selectedMessage.isDeleted === true;
                if (isInTrashView) {
                  // W Koszu tylko dwie opcje: Przywróć + Usuń trwale
                  return (
                    <>
                      <button onClick={() => undoDeleteOrArchive(selectedMessage.id)} className="btn-ghost p-1.5 text-success" title="Przywróć do skrzynki odbiorczej">
                        <RotateCcw className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteMessage(selectedMessage.id)} className="btn-ghost p-1.5 text-danger" title="Usuń trwale">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  );
                }
                return (
                  <>
                    <button
                      onClick={() => changeConversationStatus(
                        selectedMessage.id,
                        selectedMessage.conversationStatus === 'resolved' ? 'open' : 'resolved',
                      )}
                      className={`btn-ghost p-1.5 ${selectedMessage.conversationStatus === 'resolved' ? 'text-success bg-success/10' : 'hover:text-success hover:bg-success/10'}`}
                      title={selectedMessage.conversationStatus === 'resolved' ? 'Cofnij do odebranych' : 'Oznacz jako rozwiązane'}
                      aria-label={selectedMessage.conversationStatus === 'resolved' ? 'Cofnij do odebranych' : 'Oznacz jako rozwiązane'}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={toggleSelectedStar}
                      className={`btn-ghost p-1.5 ${selectedMessage.isStarred ? 'text-warning bg-warning-bg' : 'hover:text-warning hover:bg-warning-bg'}`}
                      title={selectedMessage.isStarred ? 'Usuń z wyróżnionych' : 'Dodaj do wyróżnionych'}
                      aria-label={selectedMessage.isStarred ? 'Usuń z wyróżnionych' : 'Dodaj do wyróżnionych'}
                    >
                      <Star className={`w-4 h-4 ${selectedMessage.isStarred ? 'fill-current' : ''}`} />
                    </button>
                    <div className="w-px h-4 bg-surface-hover/60 mx-1" />
                    {/* Dropdown "Przenieś do folderu" — na górze systemowe
                        (Odebrane/Archiwum/Kosz), potem custom foldery z IMAP.
                        Pozwala np. wrócić wątek z "Rozwiązane" z powrotem do
                        Inbox (brakowało tej możliwości przed 1.18.7). */}
                    <div className="relative">
                      <button
                        onClick={() => setShowMoveMenu(v => !v)}
                        className={`btn-ghost p-1.5 ${showMoveMenu ? 'bg-accent-primary/20 text-accent-primary' : ''}`}
                        title="Przenieś do folderu"
                      >
                        <FolderTree className="w-4 h-4" />
                      </button>
                      {showMoveMenu && (() => {
                        const msgConfigId = selectedMessage.imapFolder
                          ? imapFoldersFlat.find(f => f.id === selectedMessage.imapFolderId)?.configId
                          : imapFoldersFlat[0]?.configId;
                        const cfgFolders = imapFoldersFlat.filter(f => f.configId === msgConfigId);
                        // Systemowe foldery w stałej kolejności: Odebrane → Archiwum → Kosz.
                        // Bez tego sort'a kolejność zależała od IMAP server, user zgłosił że
                        // "Odebrane" pojawia się pod "Archiwum" — niezgodne z oczekiwaniem.
                        const SYSTEM_ORDER = ['\\Inbox', '\\Archive', '\\Trash'];
                        const systemFolders = cfgFolders
                          .filter(f => SYSTEM_ORDER.includes(f.specialUse ?? ''))
                          .sort((a, b) =>
                            SYSTEM_ORDER.indexOf(a.specialUse ?? '') - SYSTEM_ORDER.indexOf(b.specialUse ?? '')
                          );
                        const customFolders = cfgFolders.filter(f =>
                          !f.specialUse || !['\\Inbox', '\\Sent', '\\Drafts', '\\Archive', '\\Trash', '\\Junk'].includes(f.specialUse)
                        );
                        const systemLabelFor = (sp: string | null) =>
                          sp === '\\Inbox' ? 'Odebrane' : sp === '\\Archive' ? 'Archiwum' : sp === '\\Trash' ? 'Kosz' : '';
                        return (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setShowMoveMenu(false)} />
                            <div className="absolute right-0 top-full mt-1 z-50 w-60 max-h-[400px] overflow-y-auto bg-surface-raised border border-border rounded-xl shadow-xl py-1.5">
                              <div className="px-3 pt-1 pb-0.5 text-[9px] font-semibold text-text-disabled uppercase tracking-wider">Foldery systemowe</div>
                              {systemFolders.length === 0 && (
                                <div className="px-3 py-1.5 text-[11px] text-text-disabled italic">brak</div>
                              )}
                              {systemFolders.map(f => (
                                <button
                                  key={f.id}
                                  onClick={() => moveToFolder(f.id)}
                                  disabled={f.id === selectedMessage.imapFolderId}
                                  className="w-full text-left px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-hover/50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                  {f.specialUse === '\\Inbox' && <Inbox className="w-3.5 h-3.5 text-accent-primary" />}
                                  {f.specialUse === '\\Archive' && <Archive className="w-3.5 h-3.5 text-text-secondary" />}
                                  {f.specialUse === '\\Trash' && <Trash2 className="w-3.5 h-3.5 text-danger" />}
                                  <span className="flex-1">{systemLabelFor(f.specialUse)}</span>
                                  {f.totalCount > 0 && (
                                    <span className="text-[10px] text-text-muted tabular-nums">{f.totalCount}</span>
                                  )}
                                  {f.id === selectedMessage.imapFolderId && <CheckCircle2 className="w-3 h-3 text-success" />}
                                </button>
                              ))}
                              {customFolders.length > 0 && (
                                <>
                                  <div className="border-t border-border-subtle my-1" />
                                  <div className="px-3 pt-1 pb-0.5 text-[9px] font-semibold text-text-disabled uppercase tracking-wider">Inne foldery</div>
                                  {customFolders.map(f => (
                                    <button
                                      key={f.id}
                                      onClick={() => moveToFolder(f.id)}
                                      disabled={f.id === selectedMessage.imapFolderId}
                                      className="w-full text-left px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-hover/50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                      <FolderTree className="w-3.5 h-3.5 text-text-muted" />
                                      <span className="truncate flex-1">{f.path}</span>
                                      {f.totalCount > 0 && (
                                        <span className="text-[10px] text-text-muted tabular-nums">{f.totalCount}</span>
                                      )}
                                      {f.id === selectedMessage.imapFolderId && <CheckCircle2 className="w-3 h-3 text-success" />}
                                    </button>
                                  ))}
                                </>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <button onClick={() => archiveMessage(selectedMessage.id)} className="btn-ghost p-1.5" title="Archiwizuj">
                      <Archive className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteMessage(selectedMessage.id)} className="btn-ghost p-1.5 text-danger" title="Usuń cały wątek">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                );
              })()}
              <div className="w-px h-4 bg-surface-hover/60 mx-1" />
              {/* #R14-3b - przycisk fullscreen USUNIĘTY z toolbar. Przeniesiony
                  do bottom panelu InboxSidebar jako 50/50 split (Zwiń | Ekran).
                  Skrót: Cmd+Shift+F (globalny FullscreenContext). */}
              <button
                onClick={() => setShowInfoPanel(v => !v)}
                className={`p-1.5 rounded-lg transition-all ${showInfoPanel ? 'bg-accent-primary/20 text-accent-primary' : 'btn-ghost'}`}
                title="Panel informacji"
              >
                <Info className="w-4 h-4" />
              </button>
              <button onClick={() => { setSelectedMessage(null); setShowInfoPanel(false); }} className="btn-ghost p-1.5" title="Zamknij">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Chat timeline + compose. #R3-3 / #R8-1: w trybie fullscreen
                ZAWSZE split SIDE-BY-SIDE (lewa: thread, prawa: composer/buttons)
                niezależnie od composeMode - user wchodzi w fullscreen żeby
                pisać, więc layout od razu pod to przygotowany. Dla trybu
                normalnego układ pionowy (thread top, composer bottom). */}
            <div className={`flex-1 min-w-0 flex ${isFullscreen ? 'flex-row overflow-hidden' : 'flex-col overflow-y-auto'}`}>
              {/* Scrollable chat area */}
              <div className={`p-4 space-y-4 ${isFullscreen ? 'flex-1 overflow-y-auto border-r border-border-subtle' : 'flex-none overflow-visible'}`}>
                {(() => {
                  // Sort wątku po `receivedAt` (czas wysłania emaila), nie `createdAt`
                  // (czas zapisu do DB). Backend orderBy też używa `receivedAt: 'asc'` —
                  // bez tego import IMAP w nieoczekiwanej kolejności (np. Sent przed Inbox)
                  // sprawiał że odpowiedź wyglądała jak główna wiadomość, a oryginał poniżej.
                  const threadMessages = [selectedMessage, ...(selectedMessage.replies || [])].sort((a, b) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime());
                  return threadMessages.map((msg, idx) => {
                    const outgoing = isOutgoingMessage(msg, selectedMessage);
                    const initials = getInitials(msg.fromName, msg.from);
                    const isLast = idx === threadMessages.length - 1;
                    // Zero-persistence: manifest załączników jest już przez backend zwrócony
                    // jako tablica obiektów (bez content). Żadne parsowanie JSON nie jest potrzebne.
                    const msgAtts: Array<{ partId: string; filename: string; mimeType: string; size?: number; contentId?: string }> =
                      msg.hasAttachments && Array.isArray(msg.attachments) ? msg.attachments : [];

                    return (
                      <div key={msg.id} ref={isLast ? lastMessageRef : undefined}>
                        <div className={`rounded-2xl overflow-hidden ${outgoing ? 'bg-accent-deep/10 border border-accent-primary/20' : 'bg-surface-hover/30 border border-border-subtle'}`}>
                          {(() => {
                            const fromEmail = extractEmail(msg.from);
                            const toEmail = extractEmail(msg.to);
                            const replyToEmail = extractEmail(msg.replyTo);
                            const showReplyTo = replyToEmail && replyToEmail !== fromEmail;
                            return (
                              <div className="px-5 pt-4 pb-3">
                                <div className="flex items-center gap-2.5 w-full">
                                  <div
                                    className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                      outgoing
                                        ? 'bg-gradient-to-br from-accent-primary/30 to-accent-info/30 border border-accent-primary/20 text-accent-primary'
                                        : 'bg-info-bg border border-info-border text-info'
                                    }`}
                                  >
                                    {initials}
                                  </div>
                                  <div className="flex-1 min-w-0 space-y-0.5 text-xs leading-tight">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="font-semibold text-text-primary">{msg.fromName || fromEmail}</span>
                                      {fromEmail && msg.fromName && (
                                        <>
                                          <span className="text-text-secondary">{fromEmail}</span>
                                          <button
                                            type="button"
                                            onClick={() => copyToClipboard(fromEmail, 'Skopiowano email')}
                                            className="text-text-muted hover:text-accent-primary p-0.5 rounded hover:bg-surface-muted"
                                            title="Kopiuj email"
                                          >
                                            <Copy className="w-3 h-3" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                    {msg.to && (
                                      <div className="flex items-center gap-1.5 flex-wrap text-text-secondary">
                                        <span className="text-text-muted">to</span>
                                        <span className="text-accent-primary">{msg.to}</span>
                                        {toEmail && toEmail !== msg.to && (
                                          <span className="text-text-secondary">&lt;{toEmail}&gt;</span>
                                        )}
                                        <button
                                          type="button"
                                          onClick={() => copyToClipboard(toEmail || msg.to || '', 'Skopiowano email')}
                                          className="text-text-muted hover:text-accent-primary p-0.5 rounded hover:bg-surface-muted"
                                          title="Kopiuj email"
                                        >
                                          <Copy className="w-3 h-3" />
                                        </button>
                                      </div>
                                    )}
                                    {showReplyTo && msg.replyTo && (
                                      <div className="flex items-center gap-1.5 flex-wrap text-text-secondary">
                                        <span className="text-text-muted">reply-to</span>
                                        <span className="text-accent-primary">{msg.replyTo}</span>
                                        <button
                                          type="button"
                                          onClick={() => copyToClipboard(replyToEmail, 'Skopiowano email')}
                                          className="text-text-muted hover:text-accent-primary p-0.5 rounded hover:bg-surface-muted"
                                          title="Kopiuj email"
                                        >
                                          <Copy className="w-3 h-3" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  {msg.isDraft && (
                                    <>
                                      <span className="text-[10px] px-1.5 py-0 rounded-full font-medium border text-warning bg-warning-bg border-warning-border inline-flex items-center gap-0.5 flex-shrink-0 self-center" title="Szkic — wiadomość jeszcze nie wysłana">
                                        <FileText className="w-2.5 h-2.5" /> Szkic
                                      </span>
                                      {/* #R2-2 - Kontynuuj edycję szkicu z poziomu wątku Inbox.
                                          Pre-fill composer z body tego konkretnego draftu (nie
                                          selectedMessage który tu jest rootem wątku). */}
                                      <button
                                        type="button"
                                        onClick={() => editDraft(msg)}
                                        className="text-[11px] px-2 py-1 rounded-md font-medium border border-warning-border text-warning hover:bg-warning-bg transition-colors inline-flex items-center gap-1 flex-shrink-0 self-center"
                                        title="Kontynuuj edycję tego szkicu"
                                      >
                                        <Edit2 className="w-3 h-3" /> Kontynuuj
                                      </button>
                                    </>
                                  )}
                                  <span className="text-[10px] text-text-muted flex-shrink-0 whitespace-nowrap self-center">
                                    {new Date(msg.createdAt).toLocaleString('pl-PL')}
                                  </span>
                                  {/* Separator + przycisk kosza per-message. Odseparowany
                                      od kosza w toolbar nagłówka wątku (który kasuje root/cały
                                      wątek) — większy margines i inny kontekst wizualny.
                                      Tooltip kontekstowy: "Usuń szkic" dla draftów (nie idą
                                      do kosza, hard delete), "Usuń wiadomość" dla reszty. */}
                                  <div className="w-px h-6 bg-surface-hover/60 flex-shrink-0 self-center ml-2" />
                                  <button
                                    type="button"
                                    onClick={() => deleteMessage(msg.id)}
                                    className="p-1.5 rounded-lg text-text-muted hover:text-danger hover:bg-danger/10 transition-colors flex-shrink-0 self-center"
                                    title={msg.isDraft ? 'Usuń szkic' : 'Usuń wiadomość'}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            );
                          })()}
                              <MessageBodyRenderer messageId={msg.id} source={msg.source} />
                              {msgAtts.length > 0 && (
                                <div className="px-5 pb-4 pt-3.5 border-t border-border-subtle">
                                  <div className="flex flex-wrap gap-1.5">
                                    {msgAtts.map((att) => {
                                      const normalizedMime = att.mimeType?.toLowerCase().split(';')[0].trim() || '';
                                      const isImage = normalizedMime.startsWith('image/');
                                      const isPdf = normalizedMime === 'application/pdf' || att.filename.toLowerCase().endsWith('.pdf');
                                      const canPreview = isImage || isPdf;
                                      // Streaming z IMAP przez backend — `?download=1` wymusza attachment disposition.
                                      const downloadUrl = messagesApi.getMessagePartUrl(msg.id, att.partId, { download: true });
                                      return canPreview ? (
                                        <button
                                          key={att.partId}
                                          onClick={() => previewMessageAttachment(msg.id, att)}
                                          disabled={previewingAttachmentKey === `${msg.id}:${att.partId}`}
                                          className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface-hover/50 border border-border-subtle hover:border-accent-primary/30 transition-colors text-xs text-text-secondary hover:text-accent-primary disabled:opacity-60"
                                        >
                                          {previewingAttachmentKey === `${msg.id}:${att.partId}`
                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                            : <Paperclip className="w-3 h-3" />}
                                          <span className="truncate max-w-[150px]">{att.filename}</span>
                                          {att.size != null && att.size > 0 && (
                                            <span className="text-[10px] text-text-disabled">{formatFileSize(att.size)}</span>
                                          )}
                                        </button>
                                      ) : (
                                        <a key={att.partId} href={downloadUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface-hover/50 border border-border-subtle hover:border-accent-primary/30 transition-colors text-xs text-text-secondary hover:text-accent-primary">
                                          <Paperclip className="w-3 h-3" />
                                          <span className="truncate max-w-[150px]">{att.filename}</span>
                                          {att.size != null && att.size > 0 && (
                                            <span className="text-[10px] text-text-disabled">{formatFileSize(att.size)}</span>
                                          )}
                                        </a>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                        </div>
                      </div>
                    );
                  });
                })()}
                {pendingInThread.map((reply, index) => (
                  <div key={reply.id} ref={index === pendingInThread.length - 1 ? pendingReplyRef : undefined}>
                    <PendingReplyCard reply={reply} canRestore={composeMode === 'none'} onRestore={() => {
                      if (composeModeRef.current !== 'none') return;
                      composeModeRef.current = 'reply';
                      setComposeMode('reply');
                      setComposerOwnerId(selectedMessage.id);
                      setComposeData(reply.draft);
                      setShowCcBcc(Boolean(reply.draft.cc || reply.draft.bcc));
                      setPendingReplies(current => current.filter(item => item.id !== reply.id));
                      if (reply.filenames.length) showToast('Dodaj załączniki ponownie przed wysłaniem.', 'error');
                    }} />
                  </div>
                ))}
              </div>

              {/* W zwykłym widoku composer jest kolejnym elementem przewijanego
                  wątku. W fullscreenie pozostaje osobnym prawym panelem. */}
              <div className={isFullscreen
                ? 'flex-1 flex flex-col border-l border-border-subtle min-w-0 overflow-hidden'
                : composeMode === 'reply'
                  ? 'flex-shrink-0 flex flex-col border-t border-border-subtle'
                  : 'flex-shrink-0 border-t border-border-subtle'}>
                {composeMode === 'reply' ? (
                  // #R10-1 - key oparty o isFullscreen: animacja slide-in-right
                  // gra TYLKO przy wejściu/wyjściu z fullscreena (zmiana key =
                  // remount). Mail-switch w fullscreenie nie zmienia keya, więc
                  // wrapper żyje, animacja nie replay'uje.
                  <div
                    key={isFullscreen ? 'compose-fullscreen' : 'compose-normal'}
                    className={`${isFullscreen ? 'flex-1 min-h-0' : 'flex-none'} flex flex-col p-3 animate-slide-in-right`}
                  >
                    {renderComposeEditor(true)}
                  </div>
                ) : (
                  <div className={`flex items-center gap-2 px-4 py-3 ${isFullscreen ? 'flex-1 justify-center' : ''}`}>
                    {selectedMessage?.isDraft ? (
                      <Button onClick={() => editDraft()} variant="secondary" leftIcon={<Edit2 />}>
                        Kontynuuj edycję szkicu
                      </Button>
                    ) : (
                      <>
                        <Button onClick={startReply} variant="secondary" leftIcon={<Send />}>
                          Odpowiedz
                        </Button>
                        <Button onClick={startForward} variant="ghost" leftIcon={<Forward />} className="border border-border">
                          Przekaż
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* === COLUMN 4: Info panel (toggled) === */}
            {showInfoPanel && (
              <div className="w-72 flex-shrink-0 border-l border-border-subtle overflow-y-auto bg-surface-raised/20 animate-fade-in">
                <div className="p-3 space-y-0">
                  {/* Customer data — pokazujemy tylko dla wiadomości przychodzących, nie naszych wysłanych */}
                  {selectedMessage.imapFolder?.specialUse !== '\\Sent' && (
                    <div className="pb-3">
                      <p className="text-xs font-semibold text-text-secondary mb-2 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Klient</p>
                      {customerLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-accent-primary" />
                      ) : customerData && customerData.orderCount > 0 ? (
                        <div className="bg-surface-raised/40 rounded-xl border border-border-subtle p-3 space-y-3">
                          <div className="text-xs space-y-1">
                            {customerData.customer.name && <p className="text-text-primary font-medium">{customerData.customer.name}</p>}
                            <button onClick={() => { navigator.clipboard.writeText(customerData.customer.email); showToast('Skopiowano'); }} className="text-accent-primary hover:underline text-xs">{customerData.customer.email}</button>
                            {customerData.customer.phone && (
                              <button onClick={() => { navigator.clipboard.writeText(customerData.customer.phone!); showToast('Skopiowano'); }} className="text-text-secondary hover:text-text-primary text-xs block">{customerData.customer.phone}</button>
                            )}
                          </div>
                          <CustomerOrderHistory
                            data={customerData}
                            statuses={orderStatusesCache}
                            theme={theme}
                            onOpenOrder={setOrderModalId}
                            onOpenComplaint={setComplaintModalId}
                            maxOrders={8}
                            maxHeightClass="max-h-[32rem]"
                          />
                        </div>
                      ) : (
                        <p className="text-xs text-text-disabled">Brak danych</p>
                      )}
                    </div>
                  )}

                  {/* Separator */}
                  <div className="border-t border-border-subtle" />

                  {/* Quick actions */}
                  <div className="py-3">
                    <p className="text-xs font-semibold text-text-secondary mb-2 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Szybkie akcje</p>
                    <button
                      onClick={() => { setCouponForm(f => ({ ...f, code: generateCouponCode() })); setShowCouponModal(true); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border-subtle bg-surface-raised/50 hover:bg-surface-hover/50 hover:border-accent-primary/20 transition-all text-left"
                    >
                      <Tag className="w-4 h-4 text-accent-primary" />
                      <span className="text-sm text-text-secondary">Kod rabatowy</span>
                    </button>
                  </div>

                  {/* Separator */}
                  <div className="border-t border-border-subtle" />

                  {/* Notes */}
                  <div className="py-3">
                    <p className="text-xs font-semibold text-warning mb-2 flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" /> Notatki rozmowy</p>
                    <div className="space-y-2">
                      {notes.map(note => (
                        <div key={note.id} className="bg-surface-raised/50 rounded-lg p-2.5 border border-warning-border">
                          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                            <span className="text-[10px] font-medium text-warning">{note.userName}</span>
                            <span className="text-[10px] text-text-disabled">{new Date(note.createdAt).toLocaleDateString('pl-PL')}</span>
                            {note.orderId && note.order && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-accent-primary/10 text-accent-primary border border-accent-primary/20" title="Notatka dodana z karty zamówienia">
                                #{note.order.orderNumber}
                              </span>
                            )}
                            <button onClick={() => deleteNote(note.id)} className="ml-auto text-text-disabled hover:text-danger p-0.5"><X className="w-3 h-3" /></button>
                          </div>
                          <p className="text-xs text-text-secondary whitespace-pre-wrap">{note.content}</p>
                        </div>
                      ))}
                      <textarea
                        value={noteInput}
                        onChange={e => setNoteInput(e.target.value)}
                        placeholder="Dodaj notatkę..."
                        className="w-full bg-surface-raised/50 text-xs text-text-primary border border-border rounded-lg px-2.5 py-2 outline-none focus:border-warning-border resize-none placeholder-text-disabled"
                        rows={2}
                        onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote(); }}
                      />
                      <button onClick={addNote} disabled={!noteInput.trim() || noteSubmitting} className="w-full px-3 py-1.5 text-xs font-medium rounded-lg bg-warning-bg text-warning hover:bg-warning/20 transition-colors disabled:opacity-30">
                        {noteSubmitting ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : '+ Dodaj notatkę'}
                      </button>
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="border-t border-border-subtle" />

                  {/* Labels / Tags */}
                  <div className="pt-3">
                    <p className="text-xs font-semibold text-text-secondary mb-2 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Etykiety</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedMessage.labels && selectedMessage.labels.map(ml => (
                        <span key={ml.label.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full text-text-primary/80" style={{ backgroundColor: ml.label.color + '30', borderColor: ml.label.color, borderWidth: 1 }}>
                          {ml.label.name}
                          <button onClick={() => removeLabelFromMessage(selectedMessage.id, ml.label.id)} className="hover:text-danger transition-colors"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                      <div className="relative">
                        <button onClick={() => setShowAddLabel(!showAddLabel)} className="text-[11px] px-2 py-0.5 rounded-full border border-dashed border-border text-text-muted hover:text-accent-primary hover:border-accent-primary/30 transition-colors">
                          + Dodaj
                        </button>
                        {showAddLabel && (
                          <div className="absolute left-0 top-full mt-1 w-44 bg-surface-raised border border-border rounded-xl shadow-2xl z-50 overflow-hidden">
                            <div className="max-h-48 overflow-y-auto">
                              {labels.filter(l => !selectedMessage.labels?.some(ml => ml.label.id === l.id)).length === 0 ? (
                                <div className="px-3 py-3 text-center text-xs text-text-muted">{labels.length === 0 ? 'Brak etykiet' : 'Wszystkie przypisane'}</div>
                              ) : (
                                labels.filter(l => !selectedMessage.labels?.some(ml => ml.label.id === l.id)).map(l => (
                                  <button key={l.id} onClick={() => { addLabelToMessage(selectedMessage.id, l.id); setShowAddLabel(false); }} className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-surface-hover/50 transition-colors">
                                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: l.color }} />
                                    <span className="text-sm text-text-secondary">{l.name}</span>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Coupon modal */}
      {showCouponModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowCouponModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-surface-raised rounded-2xl border border-border shadow-2xl w-full max-w-md animate-fade-in" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
                <div className="flex items-center gap-2"><Tag className="w-5 h-5 text-accent-primary" /><h3 className="text-base font-semibold text-text-primary">Kupon rabatowy</h3></div>
                <button onClick={() => setShowCouponModal(false)} className="btn-ghost p-1"><X className="w-4 h-4" /></button>
              </div>
              <div className="px-5 py-4 space-y-4">
                <div className="rounded-lg border border-accent-primary/20 bg-accent-primary/5 px-3 py-2 text-xs text-text-secondary">
                  Kupon zostanie utworzony w sklepie:{' '}
                  <strong className="text-text-primary">
                    {stores.find(store => store.id === (selectedStoreId || selectedMessage?.store?.id))?.name || 'wybrany sklep'}
                  </strong>
                </div>
                <div>
                  <label className="text-xs text-text-secondary mb-1.5 block">Kod rabatowy</label>
                  <div className="flex gap-2">
                    <Input type="text" value={couponForm.code} onChange={e => setCouponForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} className="flex-1 font-mono" placeholder="EF-XXXXXX" />
                    <Button
                      onClick={() => setCouponForm(f => ({ ...f, code: generateCouponCode() }))}
                      variant="ghost"
                      size="sm"
                      className="!bg-surface-hover/50 !text-accent-primary hover:!bg-info-bg hover:!text-info hover:ring-1 hover:ring-info-border"
                    >
                      Generuj
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-text-secondary mb-1.5 block">Typ rabatu</label>
                  <Select value={couponForm.discountType} onChange={e => setCouponForm(f => ({ ...f, discountType: e.target.value as 'percent' | 'fixed_cart' }))}>
                    <option value="percent">Procentowy (%)</option>
                    <option value="fixed_cart">Kwotowy (PLN)</option>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-text-secondary mb-1.5 block">Wartość rabatu</label>
                  <Input type="number" value={couponForm.amount} onChange={e => setCouponForm(f => ({ ...f, amount: e.target.value }))} placeholder={couponForm.discountType === 'percent' ? 'np. 10' : 'np. 50'} min={0.01} max={couponForm.discountType === 'percent' ? 100 : undefined} step={0.01} />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={couponForm.singleUse} onChange={e => setCouponForm(f => ({ ...f, singleUse: e.target.checked }))} className="rounded border-border" />
                  <span className="text-sm text-text-secondary">Jednorazowy</span>
                </label>
              </div>
              <div className="flex items-center gap-2 px-5 py-4 border-t border-border-subtle">
                <Button onClick={createCoupon} disabled={couponLoading || !couponForm.code || !couponForm.amount} loading={couponLoading} leftIcon={<Tag />}>
                  Utwórz i wstaw
                </Button>
                <Button onClick={() => setShowCouponModal(false)} variant="ghost">Anuluj</Button>
              </div>
            </div>
          </div>
        </>
      )}


      {/* Lightbox overlay for images / PDFs */}
      {lightboxUrl && (
        <>
          <div className="fixed inset-0 bg-black/80 z-[60] backdrop-blur-sm" onClick={() => setLightboxUrl(null)} />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-8" onClick={() => setLightboxUrl(null)}>
            <div className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm text-text-primary font-medium">{lightboxFilename}</span>
                <a href={lightboxUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-accent-primary hover:underline flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Otwórz w nowej karcie
                </a>
                <button onClick={() => setLightboxUrl(null)} className="text-text-secondary hover:text-text-primary p-1"><X className="w-5 h-5" /></button>
              </div>
              {lightboxType === 'image' ? (
                <img
                  src={lightboxUrl}
                  className="max-w-[85vw] max-h-[80vh] rounded-xl shadow-2xl object-contain"
                  alt={lightboxFilename}
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'text-text-secondary text-sm py-12 px-8 bg-surface-raised rounded-xl border border-border';
                    fallback.textContent = 'Podgląd niedostępny — otwórz w nowej karcie';
                    target.parentNode?.appendChild(fallback);
                  }}
                />
              ) : (
                <iframe src={lightboxUrl} className="w-[80vw] h-[80vh] rounded-xl shadow-2xl bg-white" title={lightboxFilename} />
              )}
            </div>
          </div>
        </>
      )}

      {/* Expunge confirm dialog (trwałe usunięcie z Kosza) */}
      <ConfirmDialog
        isOpen={expungeConfirm !== null}
        title={expungeConfirm?.mode === 'bulk' ? 'Usunąć trwale zaznaczone wiadomości?' : 'Usunąć trwale tę wiadomość?'}
        message={
          expungeConfirm?.mode === 'bulk'
            ? `Usuniesz ${expungeConfirm.ids.length} wiadomości na stałe. Operacja jest nieodwracalna.`
            : 'Usuniesz tę wiadomość na stałe. Operacja jest nieodwracalna.'
        }
        confirmLabel="Usuń trwale"
        onConfirm={() => {
          if (!expungeConfirm) return;
          if (expungeConfirm.mode === 'single') {
            doTrashOrExpunge(expungeConfirm.ids[0], true);
          } else {
            doBulkAction('trash', expungeConfirm.ids, true);
          }
          setExpungeConfirm(null);
        }}
        onClose={() => setExpungeConfirm(null)}
      />

      {/* Bulk confirm dla soft-delete przy N>100 — ochrona przed omyłkowym
          zbrzdzeniem Kosza z innego folderu. Expunge (z Kosza) ma osobny dialog
          wyżej (confirm ZAWSZE, bez threshold, z default subtitle "nieodwracalne").
          Tu nadpisujemy subtitle bo soft-delete JEST odwracalny. */}
      <ConfirmDialog
        isOpen={pendingBulkAction !== null}
        title="Przenieść zaznaczone wiadomości do Kosza?"
        subtitle="Akcję można cofnąć — wiadomości można przywrócić z Kosza."
        message={
          pendingBulkAction
            ? `Przenosisz ${pendingBulkAction.count} wiadomości do Kosza. Z Kosza możesz je potem przywrócić do pierwotnego folderu albo usunąć trwale.`
            : ''
        }
        confirmLabel="Przenieś do Kosza"
        onConfirm={() => {
          if (!pendingBulkAction) return;
          const ids = Array.from(selectedIds);
          doBulkAction(pendingBulkAction.action, ids, false);
          setPendingBulkAction(null);
        }}
        onClose={() => setPendingBulkAction(null)}
      />

      {/* Modal szczegółów zamówienia — ten sam z Logistyki, bez nowej karty.
          Ładuje order + statuses po kliku w kafelek w panelu klienta. */}
      {complaintModalId && <ComplaintDetailModal complaintId={complaintModalId} onClose={() => setComplaintModalId(null)} onUpdated={() => {
        if (customerData) void messagesApi.getCustomerHistory({ email: customerData.customer.email, storeId: storeIdParam }).then(response => setCustomerData(response.data)).catch(() => showToast('Nie udało się odświeżyć zgłoszeń'));
        if (draftCustomerData) void messagesApi.getCustomerHistory({ email: draftCustomerData.customer.email, storeId: storeIdParam }).then(response => setDraftCustomerData(response.data)).catch(() => showToast('Nie udało się odświeżyć zgłoszeń'));
      }} />}
      {orderModalId && orderModalData && orderStatusesCache && (
        <OrderDetailModal
          key={orderModalData.id}
          order={orderModalData}
          statuses={orderStatusesCache}
          showUnassignedStatus={false}
          onClose={() => setOrderModalId(null)}
          onCreateSimilarOrder={setSimilarOrderSource}
          onOrderUpdated={(updated, options) => {
            setOrderModalData(updated);
            // Panel klienta (customerData.orders) nadal musi pokazać nowy status.
            setCustomerData(prev => prev ? {
              ...prev,
              orders: prev.orders.map(o => o.id === updated.id
                ? {
                    ...o,
                    status: updated.status,
                    statusId: updated.statusId,
                    externalStatus: updated.externalStatus,
                    orderStatus: (() => {
                      const resolved = findCustomerOrderStatus(orderStatusesCache, updated.statusId, updated.status);
                      return resolved
                        ? { id: resolved.id, name: resolved.name, color: resolved.color, settings: resolved.settings ?? null }
                        : o.orderStatus;
                    })(),
                    ...(typeof (updated as { customerNotesCount?: number }).customerNotesCount === 'number'
                      ? { customerNotesCount: (updated as { customerNotesCount?: number }).customerNotesCount }
                      : {}),
                  }
                : o),
            } : prev);
            if (options?.closeModal) setOrderModalId(null);
          }}
        />
      )}
      {similarOrderSource && <CreateOrderModal
        sourceOrder={similarOrderSource}
        initialStoreId={similarOrderSource.store?.id}
        onClose={() => setSimilarOrderSource(null)}
        onCreated={(created) => {
          setOrderModalData(created);
          setOrderModalId(created.id);
          if (customerData) void messagesApi.getCustomerHistory({ email: customerData.customer.email, storeId: storeIdParam })
            .then(response => setCustomerData(response.data)).catch(() => showToast('Nie udało się odświeżyć zamówień'));
          if (draftCustomerData) void messagesApi.getCustomerHistory({ email: draftCustomerData.customer.email, storeId: storeIdParam })
            .then(response => setDraftCustomerData(response.data)).catch(() => showToast('Nie udało się odświeżyć zamówień'));
        }}
      />}
      {orderModalId && orderModalLoading && !orderModalData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-surface-raised rounded-xl border border-border-subtle p-6 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-accent-primary" />
            <span className="text-sm text-text-primary">Ładuję zamówienie…</span>
          </div>
        </div>
      )}

      <Modal
        isOpen={reviewTemplateId !== null}
        onClose={() => { if (!templateApplying) setReviewTemplateId(null); }}
        title="Szablony odpowiedzi"
        description="Wybrany szablon zastąpi propozycję AI. Przed wysłaniem możesz sprawdzić i edytować treść."
        size="sm"
        closeOnEscape={!templateApplying}
        closeOnBackdrop={!templateApplying}
      >
        {templateApplying && <p className="flex items-center gap-2 text-sm text-text-muted"><Loader2 className="h-4 w-4 animate-spin" />Wstawianie szablonu...</p>}
        {renderTemplateChoices(applyReviewTemplate)}
      </Modal>

      {/* Quick edit draft modal — override treści draftu AI bez regeneracji */}
      {quickEditDraftId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onMouseDown={(e) => { quickEditDownInsideRef.current = e.target !== e.currentTarget; }}
          onClick={(e) => {
            if (quickEditSaving) return;
            if (quickEditDownInsideRef.current) return;
            if (e.target !== e.currentTarget) return;
            setQuickEditDraftId(null);
          }}
        >
          <div
            className="bg-surface-raised border border-border rounded-2xl shadow-2xl w-full max-w-2xl mx-4 p-5"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-text-primary mb-1">Szybka edycja draftu</h3>
            <p className="text-xs text-text-secondary mb-3">
              Zmień treść ręcznie - bez regenerowania przez AI. Po zapisie kliknij &ldquo;Zatwierdź i wyślij&rdquo;.
            </p>
            <textarea
              value={quickEditValue}
              onChange={e => setQuickEditValue(e.target.value)}
              rows={12}
              maxLength={50_000}
              placeholder="Treść odpowiedzi..."
              className="w-full bg-surface-base border border-border rounded-xl px-3 py-2.5 text-sm text-text-primary placeholder:text-text-disabled focus:border-accent-primary/50 focus:outline-none resize-y"
              disabled={quickEditSaving}
              autoFocus
            />
            <div className="flex items-center gap-2 mt-4 justify-between">
              <Button
                onClick={saveQuickEdit}
                disabled={quickEditSaving || !quickEditValue.trim()}
                loading={quickEditSaving}
                leftIcon={<Save />}
              >
                Zapisz zmiany
              </Button>
              <Button
                onClick={() => setQuickEditDraftId(null)}
                disabled={quickEditSaving}
                variant="ghost"
              >
                Anuluj
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Suggestion Popup */}
      {showSuggestionPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onMouseDown={(e) => { suggestionDownInsideRef.current = e.target !== e.currentTarget; }}
          onClick={(e) => {
            if (suggestionLoading) return;
            if (suggestionDownInsideRef.current) return;
            if (e.target !== e.currentTarget) return;
            setShowSuggestionPopup(false);
            setSuggestionContext(null);
          }}
        >
          <div className="bg-surface-raised border border-border rounded-2xl shadow-2xl w-full max-w-md mx-4 p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-text-primary mb-3">Zasugeruj odpowiedź</h3>
            <p className="text-xs text-text-secondary mb-3">Opowiedz swoimi słowami AI, jak powinna wyglądać odpowiedź.</p>
            <div className="relative">
              <textarea
                value={suggestionText}
                onChange={e => setSuggestionText(e.target.value)}
                maxLength={2000}
                rows={4}
                placeholder="Np. podziękuj za zamówienie i poinformuj o wysyłce w ciągu 2 dni..."
                className="w-full bg-surface-base border border-border rounded-xl px-3 py-2.5 pr-12 text-sm text-text-primary placeholder:text-text-disabled focus:border-info-border focus:outline-none resize-none"
                disabled={suggestionLoading}
              />
              <div className="absolute right-2 bottom-2 flex items-center gap-1">
                {transcribing ? (
                  <div className="p-1.5 text-info">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                ) : isRecording ? (
                  (() => {
                    const secondsLeft = Math.max(0, VOICE_RECORDING_LIMIT_SECONDS - recordingTime);
                    const isNearLimit = secondsLeft <= VOICE_RECORDING_WARN_SECONDS_LEFT;
                    return (
                      <button
                        onClick={stopRecording}
                        className={`flex items-center gap-1.5 px-1.5 py-1.5 rounded-lg transition-colors animate-pulse ${
                          isNearLimit
                            ? 'bg-danger/30 text-danger hover:bg-danger/40 ring-1 ring-danger/60'
                            : 'bg-danger/20 text-danger hover:bg-danger/30'
                        }`}
                        title={`Nagrywanie ${recordingTime}s / ${VOICE_RECORDING_LIMIT_SECONDS}s — kliknij by zatrzymać`}
                      >
                        <MicOff className="w-4 h-4" />
                        <span className="text-[10px] font-semibold tabular-nums">
                          {recordingTime}s / {VOICE_RECORDING_LIMIT_SECONDS}s
                        </span>
                      </button>
                    );
                  })()
                ) : (
                  <button
                    onClick={startRecording}
                    disabled={suggestionLoading}
                    className="p-1.5 rounded-lg text-text-muted hover:text-info hover:bg-info-bg transition-colors"
                    title="Nagraj głos"
                  >
                    <Mic className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {isRecording && (
              <div className="flex items-center gap-2 mt-2">
                <span className="w-2 h-2 rounded-full bg-danger animate-pulse" />
                <span className={`text-xs tabular-nums ${
                  VOICE_RECORDING_LIMIT_SECONDS - recordingTime <= VOICE_RECORDING_WARN_SECONDS_LEFT
                    ? 'text-danger font-semibold'
                    : 'text-danger'
                }`}>
                  Nagrywanie {recordingTime}s / {VOICE_RECORDING_LIMIT_SECONDS}s
                </span>
              </div>
            )}
            {transcribing && (
              <p className="text-xs text-info mt-2 flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> Transkrybuję...
              </p>
            )}
            <div className="flex items-center justify-between mt-3">
              <span className="text-[10px] text-text-disabled">{suggestionText.length}/2000</span>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSuggestionSubmit}
                  disabled={!suggestionText.trim() || suggestionLoading || isRecording || transcribing}
                  loading={suggestionLoading}
                  size="sm"
                  leftIcon={<Sparkles />}
                >
                  Generuj
                </Button>
                <Button
                  onClick={() => { if (!suggestionLoading) { setShowSuggestionPopup(false); setSuggestionContext(null); } }}
                  disabled={suggestionLoading}
                  variant="ghost"
                  size="sm"
                >
                  Anuluj
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          role="alert"
          className={`fixed top-4 right-4 z-[150] min-w-[280px] max-w-md rounded-xl border bg-surface-raised p-4 shadow-2xl transition-all flex items-start gap-3 ${
            toast.type === 'success'
              ? 'border-success-border'
              : toast.type === 'warning'
                ? 'border-warning-border'
                : 'border-danger-border'
          }`}
        >
          {toast.type === 'success' ? (
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
          ) : toast.type === 'warning' ? (
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          ) : (
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
          )}
          <span className="flex-1 text-sm leading-snug text-text-primary">{toast.message}</span>
          {toast.action && (
            <button
              onClick={() => {
                toast.action?.onClick();
                if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
                setToast(null);
              }}
              className="shrink-0 rounded-lg border border-border bg-surface-hover px-2.5 py-1 text-xs font-medium text-text-primary hover:border-border-strong"
            >
              {toast.action.label}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
              setToast(null);
            }}
            className="shrink-0 rounded p-0.5 text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
            aria-label="Zamknij komunikat"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Slash command dropdown — Portal w document.body z position:fixed.
          Pozycja liczona z Quill getBounds(cursorIndex) + container rect.
          Dzięki Portal omija wszystkie overflow:hidden / clip context na
          rodzicach editora (scroll wrapper, fullscreen wrapper itd.). */}
      {slashQuery !== null &&
        slashFilteredTemplates.length > 0 &&
        slashDropdownPos &&
        createPortal(
          <div
            ref={slashDropdownRef}
            className="fixed bg-surface-raised border border-border-strong rounded-lg shadow-2xl max-h-60 overflow-y-auto"
            style={{
              top: slashDropdownPos.top,
              left: slashDropdownPos.left,
              width: slashDropdownPos.width,
              zIndex: 9999,
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {slashTemplateGroups.map(group => (
              <div key={group.key} className="border-b border-border-subtle last:border-b-0">
                <div className="sticky top-0 z-[1] bg-surface-raised/95 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-muted backdrop-blur-sm">
                  {group.name}
                </div>
                {group.templates.map(({ template, flatIndex }) => (
                  <button
                    key={template.id}
                    // preventDefault na MOUSE DOWN konkretnego buttona — bez tego
                    // klik zabiera focus z editora, applySlashTemplate dostaje
                    // editor.getSelection() === null i nie wie skąd usuwać "/sza".
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applySlashTemplate(template)}
                    onMouseEnter={() => setSlashIndex(flatIndex)}
                    className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                      flatIndex === slashIndex
                        ? 'bg-accent-primary/15 text-text-primary'
                        : 'text-text-secondary hover:bg-surface-hover'
                    }`}
                  >
                    <span className="block truncate text-xs text-text-primary">
                      {template.name.trim() || `/${template.shortcut || 'szablon'}`}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
